import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { consumePendingRecord } from '../../layouts/navigateTo';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { FormTitle } from '../../components/FormTitle';
import {
  ColFilterButton,
  passesColumnFilter,
  type ColumnFilterValue,
} from '../../components/ColumnFilterDropdown';
import { GridFilterBar } from '../../components/GridFilterBar';
import { withCanReport } from './canReport';
import { DEFAULT_DATE_RANGE } from '../../components/listDefaults';
import { useSort, SortableHeader } from '../../components/SortableHeader';
// Global grid feature — the shared header right-click menu (Sort · …) + the
// column model (Hide · Show All · Column Chooser · drag-reorder · resize ·
// persistence), same as AppDataGrid (client 2026-08-03: "all the grids, list AND
// detail view"; 2026-08-05: extend to the Custom Reporting multi-job grid).
import { useHeaderMenu, useColumnManager } from '../../components/AppDataGrid';
import { useNotify } from '../../components/notifications';
import { statusVariant } from '../../components/statusPill';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, ToolbarDivider, type ExportData } from '../../components/AppToolbar';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { GridSearch } from '../../components/GridSearch';
import { useSaveFlow, useConfirm, showDialog } from '../../components/confirm';
import { StepHint, type StepHintTone } from '../../components/StepHint/StepHint';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { ReportProgressOverlay } from './ReportProgressOverlay';
import { SignReportDialog } from './SignReportDialog';
import EddReportQueryModal from './EddReportQueryModal';
import { AllParamsPopup } from './AllParamsPopup';
import { useEddResultStore, type EddResult } from './eddResultStore';
import { useNavigate } from 'react-router-dom';
import { runLoading, BusyIcon, LoaderSymbol } from '../../components/loading';
import { fmtLocal, fmtLocalDate } from '../../utils/datetime';
import { PREVIEW_AS_TOKEN, openReportPreview } from '../../utils/reportPreview';
import { useAuthStore } from '../../auth/store';
import { useNavActions } from '../../hooks/useNavActions';
import { ComboSelect } from '../../components/ComboSelect';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import Tooltip from '@mui/material/Tooltip';

// ─────────────────────────────────────────────────────────────────────────────
// Shared list component for the 8 BTLIMS Reporting-module screens.
// Three row shapes:
//   - 'reporting'        → ReportingListRow  (6 variants: custom / tracking /
//                          validation / approval / delivery / mor-approval /
//                          mor-delivery)
//   - 'edd'              → EddRow
//   - 'msr'              → MsrRow
// ─────────────────────────────────────────────────────────────────────────────

export type ReportingKind =
  | 'custom' | 'validation' | 'approval' | 'delivery' | 'tracking'
  | 'mor-list' | 'mor-approval' | 'mor-delivery' | 'edd' | 'msr';

export interface MorReportRow {
  id:                 string;
  reportId:           string | null;
  reportName:         string | null;
  templateName:       string | null;
  category:           string | null;
  dateCollectedFrom:  string | null;
  dateCollectedTo:    string | null;
  dateCreated:        string | null;
  createdBy:          string | null;   // email
  dateApproved:       string | null;
  approvedBy:         string | null;   // email
  dateDelivered:      string | null;
  deliveredBy:        string | null;
  statusCode:         number;
  statusText:         string;
  emailTo:            string | null;
  lastUpdatedDate:    string | null;
}

interface ReportingListRow {
  id:                  string;
  reportId:            string | null;
  reportName:          string | null;
  reportedDate:        string | null;
  validatedDate:       string | null;
  approvedDate:        string | null;
  deliveredDate:       string | null;
  deliveryMethodCode:  number | null;
  deliveryMethod:      string | null;
  deliveryAddress:     string | null;
  email:               string | null;     // multi-line recipients (To)
  emailAddress:        string | null;
  emailCC:             string | null;     // CC to DISPLAY (Project.ReportingMailCCTo → Contact.EmailCC)
  // NO LONGER CONSUMED by the Deliver payload as of 2026-08-18 (the displayed emailCC is
  // saved+sent instead — see deliverSelected). Kept on the row because the SP still
  // returns it and it is the record of the pre-change rule; remove both together if the
  // override-suppressed CC is confirmed dead.
  emailCCSend:         string | null;     // CC actually saved+sent on Deliver — blank under a
                                          // global DefaultMailRecipient override (unchanged behaviour)
  deliveryCopies:      number | null;
  printCopies:         number | null;
  tracking:            string | null;
  mail:                boolean | null;
  doNotDeliver:        boolean | null;
  jobId:               string | null;
  jobOid:              string | null;
  jobDueDate:          string | null;
  jobReceivedDate:     string | null;
  client:              string | null;
  projectId:           string | null;
  projectName:         string | null;
  lastUpdatedDate:     string | null;
  stageText:           string;
  statusId:            string | null;
  statusCode:          number | null;
  reportedByCode:      string | null;
  reportValidatedByCode: string | null;
  reportApprovedByCode:  string | null;
  deliveredByCode:     string | null;
  amended:             boolean | null;
  amendedComment:      string | null;
  eddId:               string | null;   // ALPACA Reporting.EDDID (newest EDD for the job)
  eddOid:              string | null;   // its EDDReportGenerator.Oid — for the download link
}

// (EddRow / MsrRow per-view row interfaces removed — the shared <AppDataGrid>
//  renders every reporting view from a generic Record<string, unknown> row set.)

// ── Delivery view — Email checked-combo + Mail/DoNotDeliver checkboxes ────────
interface DeliveryContact { email: string; contactName: string | null; }
// ccPicked = the user actually chose the CC from the combo (as opposed to the CC merely
// being auto-shown). Only a picked CC is sent; an auto-shown one is display-only, so the
// widened EmailCC column can never change who is CC'd on the real email (2026-08-05).
interface DeliveryEdit    { email: string | null; emailCC: string | null; mail: boolean; doNotDeliver: boolean; ccPicked: boolean; }

const MONTH_NAMES = ['', 'Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function monthLabel(n: number | null): string {
  if (n == null) return '—';
  return MONTH_NAMES[n] ?? String(n);
}

interface ReportingListPageProps {
  kind:     ReportingKind;
  title:    string;
  subtitle: string;
  /** When provided AND kind is 'custom', clicking a JobID drills into the
   *  per-job detail view instead of opening a PDF.  AppShell wires this. */
  onCustomJobDrill?: (jobOid: string, jobId: string) => void;
}

const ENDPOINT: Record<ReportingKind, string> = {
  'custom':       '/api/v1/reporting/custom',
  'validation':   '/api/v1/reporting/validation',
  'approval':     '/api/v1/reporting/approval',
  'delivery':     '/api/v1/reporting/delivery',
  'tracking':     '/api/v1/reporting/tracking',
  'mor-list':     '/api/v1/reporting/mor',
  'mor-approval': '/api/v1/reporting/mor-approval',
  'mor-delivery': '/api/v1/reporting/mor-delivery',
  'edd':          '/api/v1/reporting/edd',
  'msr':          '/api/v1/reporting/msr',
};

// PAGE_SIZES + pageSizeLabel sourced from shared listDefaults.ts.
type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | 'All';

// Render via the shared §14b.6 helper (local tz, MM/DD/YYYY); keep the '—'
// placeholder for empty/unparseable values used in grid column formatters.
function fmtDate(v?: string | null): string {
  return v ? (fmtLocalDate(v) || '—') : '—';
}

function sinceFromKey(k: DateRangeKey): string | undefined {
  if (k === 'All') return undefined;
  const now = new Date();
  const map: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()),
    '3M': () => new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    '6M': () => new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
    '1Y': () => new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
  };
  const d = map[k as Exclude<DateRangeKey, 'All'>]();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Reporting uses the shared statusVariant() helper from components/statusPill.ts
// — every list page maps the same status word to the same pill colour.
const stagePillVariant = statusVariant;

// ── Per-kind column definitions ─────────────────────────────────────────────
// Column keys + labels match GoLive ALPACA LIMS exactly (audited 2026-05-24).
// Loose row the column formatters read.  The grid feeds generic snapshot rows
// (Record<string, unknown>); the formatters touch only known, string-ish fields,
// so a permissive cell type keeps them string-returning without an explicit `any`.
type ReportingRow = Record<string, string | null | undefined>;
interface ColDef {
  key:       string;
  label:     string;
  formatter: (r: ReportingRow) => string;
  width:     number;
}

// Custom Reporting (Samplecheckin job-level) — GoLive header row:
//   JobID · ProjectID · ProjectName · Client · DueDate · ReceivedDate · Status,
//   plus StatusID (owner 2026-10-28) — both from the same StatusDefinition row.
const CUSTOM_COLS: ColDef[] = [
  { key: 'JobID',        label: 'JobID',        width: 110, formatter: r => r.jobId ?? '' },
  { key: 'ProjectID',    label: 'ProjectID',    width: 130, formatter: r => r.projectId ?? '' },
  { key: 'ProjectName',  label: 'ProjectName',  width: 200, formatter: r => r.projectName ?? '' },
  { key: 'Client',       label: 'Client',       width: 180, formatter: r => r.client ?? '' },
  { key: 'DueDate',      label: 'DueDate',      width: 110, formatter: r => fmtDate(r.jobDueDate) },
  { key: 'ReceivedDate', label: 'ReceivedDate', width: 120, formatter: r => fmtDate(r.jobReceivedDate) },
  { key: 'Status',       label: 'Status',       width: 140, formatter: r => r.stageText ?? '' },
  { key: 'StatusID',     label: 'StatusID',     width:  90, formatter: r => r.statusId ?? '' },
];

// Report Validation — this list only ever shows reports still awaiting level-2
// validation (ReportValidatedDate IS NULL), so ReportValidatedDate /
// ReportValidatedBy are always empty and Email is not relevant to this step —
// all three columns are intentionally omitted (2026-07-11 user request).
// Columns shown:
//   ReportID · EDDID · Report · JobID · Client · ProjectID · ProjectName
//   · ReportedDate · ReportedBy · Amended
const VALIDATION_COLS: ColDef[] = [
  { key: 'ReportID',            label: 'ReportID',            width: 130, formatter: r => r.reportId ?? '' },
  { key: 'EDDID',               label: 'EDDID',               width: 110, formatter: r => r.eddId ?? '' },
  { key: 'Report',              label: 'Report',              width: 220, formatter: r => r.reportName ?? '' },
  { key: 'JobID',               label: 'JobID',               width: 110, formatter: r => r.jobId ?? '' },
  { key: 'Client',              label: 'Client',              width: 180, formatter: r => r.client ?? '' },
  { key: 'ProjectID',           label: 'ProjectID',           width: 130, formatter: r => r.projectId ?? '' },
  { key: 'ProjectName',         label: 'ProjectName',         width: 200, formatter: r => r.projectName ?? '' },
  { key: 'ReportedDate',        label: 'ReportedDate',        width: 130, formatter: r => fmtDate(r.reportedDate) },
  { key: 'ReportedBy',          label: 'ReportedBy',          width: 130, formatter: r => r.reportedByCode ?? '' },
  { key: 'Amended',             label: 'Amended',             width:  90, formatter: r => r.amended ? '✓' : '' },
];

// Report Delivery — GoLive header row:
//   ReportID · JobID · Client · ProjectID · ProjectName · EmailTo · EmailCC · Mail · DoNotDeliver
//   · DateDelivered · DeliveredBy · Status · StatusID
const DELIVERY_COLS: ColDef[] = [
  { key: 'ReportID',      label: 'ReportID',      width: 130, formatter: r => r.reportId ?? '' },
  { key: 'JobID',         label: 'JobID',         width: 110, formatter: r => r.jobId ?? '' },
  { key: 'Client',        label: 'Client',        width: 180, formatter: r => r.client ?? '' },
  { key: 'ProjectID',     label: 'ProjectID',     width: 130, formatter: r => r.projectId ?? '' },
  { key: 'ProjectName',   label: 'ProjectName',   width: 200, formatter: r => r.projectName ?? '' },
  // 'Email' key kept (drives the editable To checked-combo, see kind==='delivery' below); caption is EmailTo.
  { key: 'Email',         label: 'EmailTo',       width: 220, formatter: r => r.email ?? '' },
  // EmailCC — read-only display; source computed by AI_Reporting_List_SP:
  //   Project.ReportingMailCCTo contacts; else Contact.EmailCC (2026-08-05).
  { key: 'EmailCC',       label: 'EmailCC',       width: 220, formatter: r => r.emailCC ?? '' },
  { key: 'Mail',          label: 'Mail',          width:  60, formatter: r => r.mail ? '✓' : '' },
  { key: 'DoNotDeliver',  label: 'DoNotDeliver',  width:  90, formatter: r => r.doNotDeliver ? '✓' : '' },
  { key: 'DateDelivered', label: 'DateDelivered', width: 130, formatter: r => fmtDate(r.deliveredDate) },
  { key: 'DeliveredBy',   label: 'DeliveredBy',   width: 130, formatter: r => r.deliveredByCode ?? '' },
  { key: 'Status',        label: 'Status',        width: 110, formatter: r => r.stageText ?? '' },
  { key: 'StatusID',      label: 'StatusID',      width:  90, formatter: r => r.statusId ?? '' },
];

// Report Tracking — GoLive header row:
//   ReportID · Report · JobID · Client · ReportedDate · Status · StatusID
const TRACKING_COLS: ColDef[] = [
  { key: 'ReportID',     label: 'ReportID',     width: 130, formatter: r => r.reportId ?? '' },
  { key: 'Report',       label: 'Report',       width: 220, formatter: r => r.reportName ?? '' },
  { key: 'JobID',        label: 'JobID',        width: 110, formatter: r => r.jobId ?? '' },
  { key: 'Amended',      label: 'Amended',      width:  90, formatter: r => r.amended ? '✓' : '' },
  { key: 'Client',       label: 'Client',       width: 180, formatter: r => r.client ?? '' },
  { key: 'ReportedDate', label: 'ReportedDate', width: 130, formatter: r => fmtDate(r.reportedDate) },
  { key: 'ReportedBy',   label: 'ReportedBy',   width: 130, formatter: r => r.reportedByCode ?? '' },
  { key: 'Status',       label: 'Status',       width: 140, formatter: r => r.stageText ?? '' },
  { key: 'StatusID',     label: 'StatusID',     width:  90, formatter: r => r.statusId ?? '' },
];

// Report ESign (was 'approval' — no non-MOR Pending Approval in GoLive; we
// re-aim this kind at the closest GoLive form which is "Report ESign", a step
// between Validation and Delivery in the report workflow).
const ESIGN_COLS: ColDef[] = [
  { key: 'ReportID',     label: 'ReportID',     width: 130, formatter: r => r.reportId ?? '' },
  { key: 'Report',       label: 'Report',       width: 220, formatter: r => r.reportName ?? '' },
  { key: 'JobID',        label: 'JobID',        width: 110, formatter: r => r.jobId ?? '' },
  { key: 'Client',       label: 'Client',       width: 180, formatter: r => r.client ?? '' },
  { key: 'ReportedDate', label: 'ReportedDate', width: 130, formatter: r => fmtDate(r.reportedDate) },
  { key: 'Status',       label: 'Status',       width: 140, formatter: r => r.stageText ?? '' },
];

// MOR Reporting (all DWQR rows) — GoLive: ReportID · Template · Report
//   · Category · DateCollectedFrom · DateCollectedTo · DateCreated · CreatedBy · Status
const MOR_LIST_COLS: ColDef[] = [
  { key: 'ReportID',          label: 'ReportID',          width: 130, formatter: r => r.reportId ?? '' },
  { key: 'Template',          label: 'Template',          width: 200, formatter: r => r.templateName ?? '' },
  { key: 'Report',            label: 'Report',            width: 200, formatter: r => r.reportName ?? '' },
  { key: 'Category',          label: 'Category',          width: 140, formatter: r => r.category ?? '' },
  { key: 'DateCollectedFrom', label: 'DateCollectedFrom', width: 130, formatter: r => fmtDate(r.dateCollectedFrom) },
  { key: 'DateCollectedTo',   label: 'DateCollectedTo',   width: 130, formatter: r => fmtDate(r.dateCollectedTo) },
  { key: 'DateCreated',       label: 'DateCreated',       width: 130, formatter: r => fmtDate(r.dateCreated) },
  { key: 'CreatedBy',         label: 'CreatedBy',         width: 220, formatter: r => r.createdBy ?? '' },
  { key: 'Status',            label: 'Status',            width: 140, formatter: r => r.statusText ?? '' },
];

// MOR Pending Approval — adds DateApproved + ApprovedBy. GoLive uses
//   TemplateName / ReportName labels (not Template / Report).
const MOR_APPROVAL_COLS: ColDef[] = [
  { key: 'ReportID',          label: 'ReportID',          width: 130, formatter: r => r.reportId ?? '' },
  { key: 'TemplateName',      label: 'TemplateName',      width: 200, formatter: r => r.templateName ?? '' },
  { key: 'ReportName',        label: 'ReportName',        width: 200, formatter: r => r.reportName ?? '' },
  { key: 'Category',          label: 'Category',          width: 140, formatter: r => r.category ?? '' },
  { key: 'DateCollectedFrom', label: 'DateCollectedFrom', width: 130, formatter: r => fmtDate(r.dateCollectedFrom) },
  { key: 'DateCollectedTo',   label: 'DateCollectedTo',   width: 130, formatter: r => fmtDate(r.dateCollectedTo) },
  { key: 'DateCreated',       label: 'DateCreated',       width: 130, formatter: r => fmtDate(r.dateCreated) },
  { key: 'CreatedBy',         label: 'CreatedBy',         width: 220, formatter: r => r.createdBy ?? '' },
  { key: 'DateApproved',      label: 'DateApproved',      width: 130, formatter: r => fmtDate(r.dateApproved) },
  { key: 'ApprovedBy',        label: 'ApprovedBy',        width: 220, formatter: r => r.approvedBy ?? '' },
  { key: 'Status',            label: 'Status',            width: 140, formatter: r => r.statusText ?? '' },
];

// MOR Pending Delivery — GoLive shows same columns as MOR Pending Approval.
const MOR_DELIVERY_COLS: ColDef[] = [
  { key: 'ReportID',          label: 'ReportID',          width: 130, formatter: r => r.reportId ?? '' },
  { key: 'TemplateName',      label: 'TemplateName',      width: 200, formatter: r => r.templateName ?? '' },
  { key: 'ReportName',        label: 'ReportName',        width: 200, formatter: r => r.reportName ?? '' },
  { key: 'Category',          label: 'Category',          width: 140, formatter: r => r.category ?? '' },
  { key: 'DateCollectedFrom', label: 'DateCollectedFrom', width: 130, formatter: r => fmtDate(r.dateCollectedFrom) },
  { key: 'DateCollectedTo',   label: 'DateCollectedTo',   width: 130, formatter: r => fmtDate(r.dateCollectedTo) },
  { key: 'DateCreated',       label: 'DateCreated',       width: 130, formatter: r => fmtDate(r.dateCreated) },
  { key: 'CreatedBy',         label: 'CreatedBy',         width: 220, formatter: r => r.createdBy ?? '' },
  { key: 'DateApproved',      label: 'DateApproved',      width: 130, formatter: r => fmtDate(r.dateApproved) },
  { key: 'ApprovedBy',        label: 'ApprovedBy',        width: 220, formatter: r => r.approvedBy ?? '' },
  { key: 'Status',            label: 'Status',            width: 140, formatter: r => r.statusText ?? '' },
];

/** The forms a link can open on one job: list kind → nav id. */
const JOB_LINK_PAGE: Partial<Record<ReportingKind, string>> = {
  custom: 'custreport', validation: 'repval', approval: 'repappr', delivery: 'repdel',
};

function reportingCols(kind: ReportingKind): ColDef[] {
  if (kind === 'custom')        return CUSTOM_COLS;
  if (kind === 'validation')    return VALIDATION_COLS;
  if (kind === 'approval')      return ESIGN_COLS;
  if (kind === 'delivery')      return DELIVERY_COLS;
  if (kind === 'tracking')      return TRACKING_COLS;
  if (kind === 'mor-list')      return MOR_LIST_COLS;
  if (kind === 'mor-approval')  return MOR_APPROVAL_COLS;
  if (kind === 'mor-delivery')  return MOR_DELIVERY_COLS;
  // edd / msr handled separately below
  return TRACKING_COLS;
}

/** E-Signature status badges for the Validation/Approval grids (ADR-0009). */
function EsignBadge({ stage }: { stage: 'validation' | 'approval' }) {
  const base = { display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700 } as const;
  const req = (m: string) => <span style={{ ...base, background: 'var(--ai-pill-warn-bg)', color: 'var(--ai-pill-warn-fg)', border: '1px solid var(--ai-line)' }}>✒ Sign required → {m}</span>;
  const done = (m: string) => <span style={{ ...base, background: 'var(--ai-pill-ok-bg)', color: 'var(--ai-pill-ok-fg)', border: '1px solid var(--ai-line)' }}>✔ {m}</span>;
  return stage === 'validation'
    ? req('Validated')
    : <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>{done('Validated')}{req('Approved')}</span>;
}

// EDD Report Generator — GoLive header row:
//   EDDReportID · EDDTemplate · Client · ProjectID · DateCreated · CreatedBy
//   · IsDateCollected · IsDateReceived
const EDD_COLS: ColDef[] = [
  { key: 'EDDReportID',    label: 'EDDReportID',    width: 150, formatter: r => r.reportId ?? '' },
  { key: 'EDDTemplate',    label: 'EDDTemplate',    width: 200, formatter: r => r.templateName ?? r.template ?? '' },
  { key: 'Client',         label: 'Client',         width: 200, formatter: r => r.client ?? '' },
  { key: 'ProjectID',      label: 'ProjectID',      width: 200, formatter: r => r.projectId ?? '' },
  { key: 'DateCreated',    label: 'DateCreated',    width: 130, formatter: r => fmtDate(r.createdDate) },
  { key: 'CreatedBy',      label: 'CreatedBy',      width: 120, formatter: r => r.createdBy ?? '' },
  { key: 'IsDateCollected',label: 'IsDateCollected',width: 110, formatter: r => r.isDateCollected ? '✓' : '' },
  { key: 'IsDateReceived', label: 'IsDateReceived', width: 110, formatter: r => r.isDateReceived  ? '✓' : '' },
  // Download column REMOVED 2026-07-15 (user request) — a saved EDD is opened with
  // the top-right Load button, and downloaded from the Spreadsheet View's Export.
];

// Monthly Summary Repository — GoLive header row:
//   MSRID · Category · Month · Year · Status · DateCreated · CreatedBy · LastUpdatedDate
const MSR_COLS: ColDef[] = [
  { key: 'MSRID',           label: 'MSRID',           width: 130, formatter: r => r.msrId ?? '' },
  { key: 'Category',        label: 'Category',        width: 220, formatter: r => r.category ?? '' },
  { key: 'Month',           label: 'Month',           width:  80, formatter: r => monthLabel(r.month != null ? Number(r.month) : null) },
  { key: 'Year',            label: 'Year',            width:  70, formatter: r => r.year ?? '' },
  { key: 'Status',          label: 'Status',          width: 100, formatter: r => r.statusText ?? '' },
  { key: 'DateCreated',     label: 'DateCreated',     width: 130, formatter: r => fmtDate(r.createdDate) },
  { key: 'CreatedBy',       label: 'CreatedBy',       width: 130, formatter: r => r.createdBy ?? '' },
  { key: 'LastUpdatedDate', label: 'LastUpdatedDate', width: 130, formatter: r => fmtDate(r.lastUpdatedDate) },
];

// kind → the form's nav id, so the shared <FormTitle> sources the icon/colour
// from the SAME navData entry as the sidebar (CLAUDE.md §18.6, task 3).
const KIND_NAV_ID: Record<ReportingKind, string> = {
  'custom':       'custreport',
  'validation':   'repval',
  'approval':     'morpapp',
  'delivery':     'repdel',
  'tracking':     'reptrack',
  'mor-list':     'morlist',
  'mor-approval': 'morpapp',
  'mor-delivery': 'morpdel',
  'edd':          'eddgen',
  'msr':          'msr',
};

// kind → the REGISTERED audit FormName (CLAUDE.md §14b.2 registry) — drives the
// shared, config-gated <AuditTrailButton variant="chip"> on the title row via
// <FormTitle auditFormName>.  Kinds without a registered FormName render no chip.
const KIND_AUDIT_FORM: Partial<Record<ReportingKind, string>> = {
  'custom':     'Custom Reporting',
  'validation': 'Report Validation',
  'approval':   'Report Approval',
  'delivery':   'Report Delivery',
  'tracking':   'Report Tracking',
  'edd':        'EDD Report Generator',
};

export default function ReportingListPage({ kind, title, subtitle, onCustomJobDrill }: ReportingListPageProps) {
  const notify = useNotify();
  // Effective MAC action permissions for THIS form (New=create, Edit=write,
  // Delete=delete). Hides the buttons the user's Module Access Control level
  // doesn't grant (e.g. Delete for a R/W/C-only Reporting level). Falls open for
  // admins / when no MAC level applies, so it only ever tightens access.
  const perm = useNavActions(KIND_NAV_ID[kind]);
  const { confirm: confirmDlg } = useConfirm();
  const navigate = useNavigate();
  const setEddResult = useEddResultStore((s) => s.setResult);
  const [rows, setRows]       = useState<unknown[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [error, setError]     = useState<string | null>(null);
  // Whether the signed-in account is linked to an Employee record (employee_oid
  // claim), sourced from the list-response meta.  When false the user can still
  // SEE the reports (read-only) — only the Validate/Approve/Deliver actions are
  // unavailable.  The real gate is server-side on each action endpoint; this
  // only drives the UI affordances so a permission restriction never blanks the
  // grid.  Defaults true (non-actionable views don't report it).
  const [hasEmployeeLink, setHasEmployeeLink] = useState(true);
  // Report Signing (ADR-0009) — surfaced on the validation/approval lists.
  const [signCfg, setSignCfg] = useState<{ enabled: boolean; approvalRequired: boolean; secondFactor: string }>(
    { enabled: false, approvalRequired: false, secondFactor: 'Mfa' });
  const [signTarget, setSignTarget] = useState<{ stage: 'validation' | 'approval'; oid: string; reportId: string } | null>(null);
  // Tooltip shown on the disabled action buttons when there's no Employee link.
  const noEmpLinkTip =
    "Your account isn't linked to an Employee record — this action is unavailable until it's linked.";
  const [dateRange, setDateRange] = useState<DateRangeKey>(DEFAULT_DATE_RANGE.ReportingWorkflow as DateRangeKey);
  const [search,   setSearch]   = useState('');
  // EDD Report Generator — "New" → EDD Report Query popup open state. (Merged 2026-07-07.)
  const [eddQueryOpen, setEddQueryOpen] = useState(false);
  const [statusChip, setStatusChip] = useState<string>('all');
  // Custom Reporting GoLive "JobID Filter" toolbar dropdown (All / Pending).
  const [jobIdFilter, setJobIdFilter] = useState<'All' | 'Pending'>('Pending');

  // Deep-link from the Reporting backlog (owner review 2026-10-06): double-clicking a
  // Job ID there calls navigateToPage('custreport', jobId), parking the Job ID as a
  // one-shot handoff. Consume it on mount and pre-fill the search + show ALL statuses so
  // that Job surfaces immediately, ready to Retrieve. consumePendingRecord clears it, so
  // a later plain visit from the sidebar still opens the full list (standing rule 10).
  // My Jobs ▸ Resume opens the same way on the later stages; the job may be older than the default window.
  useEffect(() => {
    const page = JOB_LINK_PAGE[kind];
    const jobId = page ? consumePendingRecord(page) : null;
    if (!jobId) return;
    setSearch(jobId);
    setDateRange('All');
    if (kind === 'custom') setJobIdFilter('All');
  }, [kind]);
  // Multi-job selection — Custom Reporting only.  User ticks N jobs in the
  // list, clicks "Retrieve Selected" → a popup fetches every job's detail
  // rows in parallel and shows the combined grid (one row per sample
  // parameter across all picked jobs).  Set of row.id (= JobOid).
  const [selectedJobOids, setSelectedJobOids] = useState<Set<string>>(new Set());
  const [multiPopupOpen,  setMultiPopupOpen]  = useState(false);
  // Presentational-only "reviewed" tracker (Reporting-Module-Prototype.html):
  // a report the user has opened (clicked its ReportID) is marked reviewed.
  // Drives ONLY the Delivery stepper's Review step + the ✓-reviewed row marker —
  // it changes no API call, gate, or saved data.
  const [reviewedOids, setReviewedOids] = useState<Set<string>>(new Set());

  // Report Validation — the "Validate" action stamps ReportValidatedDate /
  // ReportValidatedBy on each ticked row, then the row falls off the list
  // (it no longer has ReportValidatedBy IS NULL).  Distinct from
  // selectedJobOids so the two kinds never cross-contaminate state.
  const [validating, setValidating]   = useState(false);
  // (The old inline validate/approve/deliver success pill was replaced by the
  //  shared full-width notify banner — BO UI Standard §13, 2026-07-06.)
  // Approval ("Approve" action) + Delivery ("Deliver" action) share the same
  // ticked-row set (selectedJobOids) and the same success toast as Validate.
  const [approving, setApproving]     = useState(false);
  const [delivering, setDelivering]   = useState(false);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);   // Report Delivery — Preview (PDF)
  const [deleting,  setDeleting]      = useState(false);   // Delete action (validation/delivery/tracking)
  const [editOpen, setEditOpen]       = useState(false);   // Edit Report (revise) dialog — Tracking
  const [viewOid, setViewOid]         = useState<string | null>(null);  // read-only samples detail (Validation row click)
  const [editBusy, setEditBusy]       = useState(false);
  // Project Notes popup at LIST level (BO UI Standard §8 — mandatory on every
  // screen; prototype list toolbars 2026-07-06).  Anchored to exactly ONE
  // ticked report's Job (prototype jobIdForNotes: "Tick exactly one report").
  const [listNotesJob, setListNotesJob] = useState<{ jobOid: string; jobId: string } | null>(null);
  // Custom Reporting multi-select rule (2026-06-13): a report may only span
  // JobIDs of ONE client.  When the user ticks a JobID from a different client
  // than the current selection we block it and surface this message.
  // (Same-client selection guard messages now go through the shared useNotify
  //  notifier — §18.6 — instead of a per-form inline banner.)

  // Delivery view — Email checked-combo + Mail/DoNotDeliver checkboxes.
  // Cleared on every row reload so stale edits don't linger after a Deliver.
  const [deliveryEdits,    setDeliveryEdits]   = useState<Map<string, DeliveryEdit>>(new Map());
  const [deliveryContacts, setDeliveryContacts] = useState<Map<string, DeliveryContact[]>>(new Map());
  // CC options per job — for the EmailCC checked-combo (parity with To). Priority:
  // Project.ReportingMailCCTo; else Contact.EmailCC (2026-08-05).
  const [deliveryCcContacts, setDeliveryCcContacts] = useState<Map<string, DeliveryContact[]>>(new Map());
  // Which row's dropdown is open (by row.id), and WHICH column's combo (To vs CC),
  // + where to position it.
  const [emailComboId,     setEmailComboId]     = useState<string | null>(null);
  const [emailComboCol,    setEmailComboCol]    = useState<'To' | 'CC'>('To');
  const [emailComboPos,    setEmailComboPos]    = useState<{ top: number; left: number; width: number } | null>(null);

  // Reporting kinds carry a `stageText` field; EDD and MSR do not.
  const hasStageChips = kind !== 'edd' && kind !== 'msr';

  // Kinds whose grid renders the multi-select checkbox column.  Approval +
  // Delivery join Validation as actionable list views (2026-06-14).
  const isSelectableKind =
    kind === 'custom' || kind === 'validation' || kind === 'approval'
    || kind === 'delivery' || kind === 'tracking' || kind === 'edd';

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    // silent = a background reconcile (e.g. after an optimistic delete): don't
    // flip the blocking grid-loading flag or drive the global top loader, so the
    // grid doesn't flash/block for the ~1.5s list SP.
    if (!opts?.silent) setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      const since  = sinceFromKey(dateRange);
      const params: Record<string, string> = since ? { sinceDate: since } : {};
      if (kind === 'custom') {
        params.jobIdFilter = jobIdFilter;
        // A work queue: never served from the API's 60-sec output cache (it varies by query).
        params._ts = String(Date.now());
      }
      const res = await api.get(ENDPOINT[kind],
        opts?.silent ? ({ params, silentLoading: true } as never) : { params });
      const body = res.data as { data: unknown[]; meta?: { hasEmployeeLink?: boolean; signEnabled?: boolean; approvalRequired?: boolean; secondFactor?: string } };
      setRows(body.data ?? []);
      // Only the actionable lists report this; default to true elsewhere so we
      // never gate a view that doesn't need it.
      setHasEmployeeLink(body.meta?.hasEmployeeLink !== false);
      // Report-signing config (ADR-0009) — on the validation/approval lists.
      setSignCfg({
        enabled: body.meta?.signEnabled === true,
        approvalRequired: body.meta?.approvalRequired === true,
        secondFactor: body.meta?.secondFactor ?? 'Mfa',
      });
    } catch (e) {
      console.error(e);
      setRows([]);
      setError('Failed to load — check the API logs.');
    } finally {
      setLoadMs(performance.now() - t0);
      if (!opts?.silent) setLoading(false);
    }
  }, [kind, dateRange, jobIdFilter]);

  useEffect(() => { void load(); }, [load]);

  // Fetch contact emails for every unique jobOid visible in the delivery list.
  // Runs after each row-load (rows changes on every load()).  Also resets
  // per-row edits so stale email/checkbox state doesn't survive a Deliver action.
  useEffect(() => {
    if (kind !== 'delivery') return;
    setDeliveryEdits(new Map());
    setEmailComboId(null);
    setEmailComboPos(null);
    // BATCHED (perf fix 2026-07-12): fetch EVERY visible job's delivery contacts in
    // ONE request instead of one-per-job.  The old loop fired N GETs (487 on the
    // GoLive Delivery list) — instant when the API is next to the DB, but ~16 s from
    // a remote dev box (487 × ~241 ms RTT).  One POST = one round-trip, fast anywhere.
    const jobOids = Array.from(new Set(
      (rows as ReportingListRow[]).map(r => r.jobOid).filter((j): j is string => !!j)));
    setDeliveryContacts(new Map());     // reset while the fetch is in flight
    setDeliveryCcContacts(new Map());
    if (jobOids.length === 0) return;
    void (async () => {
      try {
        // Fetch To options AND CC options in parallel (one round-trip each).
        const [toRes, ccRes] = await Promise.all([
          api.post<{ data: Record<string, DeliveryContact[]> }>(
            '/api/v1/reporting/delivery/contacts-batch', jobOids, { silentLoading: true } as never),
          api.post<{ data: Record<string, DeliveryContact[]> }>(
            '/api/v1/reporting/delivery/cc-batch', jobOids, { silentLoading: true } as never),
        ]);
        const m  = new Map<string, DeliveryContact[]>();
        for (const [job, contacts] of Object.entries(toRes.data.data ?? {})) m.set(job, contacts);
        const cm = new Map<string, DeliveryContact[]>();
        for (const [job, contacts] of Object.entries(ccRes.data.data ?? {})) cm.set(job, contacts);
        setDeliveryContacts(m);
        setDeliveryCcContacts(cm);

        // Auto-select every available To + CC contact for each deliverable row that
        // has no SAVED value yet (user request 2026-07-17, extended to CC 2026-07-29).
        // A saved Email / EmailCC is preserved; Mail / DoNotDeliver rows are skipped.
        setDeliveryEdits(prev => {
          const next = new Map(prev);
          for (const r of rows as ReportingListRow[]) {
            // Delivered (Sent) reports source Email + EmailCC STRICTLY from the saved
            // dbo.Reporting values (r.email / r.emailCC) — never re-auto-filled from
            // contacts (user request 2026-07-29: "for a saved delivered ReportID,
            // EmailCC comes from the Reporting table, like Email").
            if (r.doNotDeliver || r.mail || r.stageText === 'Sent') continue;
            const jobTo = r.jobOid ? (m.get(r.jobOid) ?? []) : [];
            // Auto-select all To recipients (user request 2026-07-17). CC is NOT
            // auto-selected — leave EmailCC empty unless a CC was already saved
            // (user request 2026-07-29: "no need to select CC; if there's no CC
            // leave it empty"). The CC combo stays available for manual picking.
            const email   = r.email   ?? (jobTo.length ? jobTo.map(c => c.email).join(';') : null);
            const emailCC = r.emailCC ?? null;
            if (email == null && emailCC == null) continue;
            next.set(r.id, { email, emailCC, mail: r.mail ?? false, doNotDeliver: r.doNotDeliver ?? false, ccPicked: false });
          }
          return next;
        });
      } catch { /* best-effort — delivery grid still works without the contact lists */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, rows]);

  // Close the email combo when the user clicks outside the dropdown or trigger.
  useEffect(() => {
    if (!emailComboId) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Element | null;
      if (t?.closest('[data-dlv-combo]') || t?.closest('[data-dlv-trigger]')) return;
      setEmailComboId(null);
      setEmailComboPos(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [emailComboId]);

  // ── Report Validation — POST the ticked Reporting Oids to the API. ─
  // The API stamps ReportValidatedDate + ReportValidatedBy on each row,
  // writes one AuditData row per validation, then we reload — validated
  // rows fall off the list because they no longer have
  // ReportValidatedBy IS NULL.
  const validateSelected = useCallback(async (oids: string[]) => {
    if (kind !== 'validation' || oids.length === 0) return;
    // Standing rule 7: every state-changing action is confirmed through the shared
    // centred dialog (PLM E2E 2026-10-16 found Validate ran on the first click).
    const okVal = await confirmDlg({
      title: 'Validate',
      message: `Validate ${oids.length} report${oids.length === 1 ? '' : 's'}?`,
      confirmLabel: 'Validate',
    });
    if (!okVal) return;
    setValidating(true);
    try {
      const res  = await runLoading(
        api.post('/api/v1/reporting/validation/validate', { oids }),
        { blocking: true, label: 'Validating…' });
      const data = (res.data as { data?: { validatedCount?: number; requestedCount?: number;
                                            alreadyValidated?: number } }).data;
      const n   = data?.validatedCount   ?? 0;
      const req = data?.requestedCount   ?? oids.length;
      const skipped = data?.alreadyValidated ?? 0;
      // Shared full-width banner (BO UI Standard §13) — never an inline pill.
      notify.success(
        skipped > 0
          ? `Validated ${n} of ${req} reports. ${skipped} already validated.`
          : `Validated ${n} report${n === 1 ? '' : 's'}.`);
      setSelectedJobOids(new Set());
      void load();
    } catch (e) {
      console.error(e);
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
                    .response?.data?.error?.message
                ?? (e as { message?: string }).message
                ?? 'Validate failed.';
      // Action failure — surface via the shared notifier (§18.6), NOT setError,
      // so a permission/validate error never replaces the grid with a red block.
      notify.error(msg);
    } finally {
      setValidating(false);
    }
  }, [kind, load, confirmDlg]);

  // ── Report Delete — soft-delete the ticked reports (Validation / Delivery /
  // Tracking).  The API reverts their samples to Pending Reporting and BLOCKS a
  // Sent/Delivered report (409).  One DELETE per row so a blocked row doesn't
  // abort the rest; the audit row is written server-side. ─────────────────
  const deleteSelected = useCallback(async (oids: string[]) => {
    if (oids.length === 0) return;
    if (!(kind === 'validation' || kind === 'approval' || kind === 'delivery' || kind === 'tracking')) return;
    // Shared centred danger confirm (standard dialog format, 2026-07-03 — was native confirm).
    const okDel = await confirmDlg({
      title: 'Delete', tone: 'danger', confirmLabel: 'Delete',
      message: `Delete ${oids.length} report${oids.length === 1 ? '' : 's'}? ` +
        `Their samples will go back to Pending Reporting.`,
    });
    if (!okDel) return;
    setDeleting(true);
    try {
      let ok = 0, blocked = 0, failed = 0;
      for (const oid of oids) {
        try {
          await runLoading(
            api.delete(`/api/v1/reporting/${kind}/report/${encodeURIComponent(oid)}`),
            { blocking: true, label: 'Deleting…' });
          ok++;
        } catch (e) {
          const status = (e as { response?: { status?: number } }).response?.status;
          if (status === 409) blocked++; else failed++;
        }
      }
      if (ok > 0)      notify.success(`Deleted ${ok} report${ok === 1 ? '' : 's'} — samples back to Pending Reporting.`);
      if (blocked > 0) notify.warning(`${blocked} report${blocked === 1 ? '' : 's'} already sent — cannot delete.`);
      if (failed > 0)  notify.error(`${failed} report${failed === 1 ? '' : 's'} failed to delete.`);
      // Optimistically drop the deleted rows so the grid updates INSTANTLY — the
      // list-reload SP is ~1.5s for a large list, so the old blocking `load()`
      // made Delete feel slow (user report 2026-07-12).  Reconcile silently in
      // the background (no grid flash / no top-loader).
      if (ok > 0)
        setRows(prev => (prev as Array<{ id?: unknown }>).filter(r => !oids.includes(String(r.id ?? ''))));
      setSelectedJobOids(new Set());
      void load({ silent: true });
    } finally {
      setDeleting(false);
    }
  }, [kind, load, notify, confirmDlg]);

  // ── EDD Report Generator — open one saved row (row double-click / Edit).
  // Re-runs the row's stored template + filters on the server and opens the
  // tabbed Spreadsheet View in a SAVED state (existing EddReportID shown; the
  // Save button is hidden because it's already saved). ─────────────────────
  const openEdd = useCallback(async (oid: string) => {
    if (!oid) return;
    try {
      const res = await runLoading(
        api.get(`/api/v1/edd/${encodeURIComponent(oid)}/open`),
        { blocking: true, label: 'Opening EDD…' });
      const d = res.data?.data as {
        eddReportId: string; eddReportOid: string; templateId: string;
        templateName: string; filter: unknown; sheets: EddResult['sheets'];
      };
      setEddResult({
        eddReportId:  d.eddReportId,
        eddReportOid: d.eddReportOid,
        templateName: d.templateName,
        sheets:       d.sheets ?? [],
        templateId:   d.templateId,
        filter:       d.filter,
        saved:        true,
      });
      navigate('/m/eddresult');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Failed to open the EDD report.';
      notify.error(msg);
    }
  }, [navigate, setEddResult, notify]);

  // ── EDD Report Generator — soft-delete the ticked rows (list Delete). ──────
  const deleteEddSelected = useCallback(async (oids: string[]) => {
    if (oids.length === 0) return;
    const okDel = await confirmDlg({
      title: 'Delete', tone: 'danger', confirmLabel: 'Delete',
      message: `Delete ${oids.length} EDD report${oids.length === 1 ? '' : 's'}?`,
    });
    if (!okDel) return;
    setDeleting(true);
    try {
      const res = await runLoading(
        api.post('/api/v1/edd/delete', { oids }),
        { blocking: true, label: 'Deleting…' });
      const n = (res.data?.data as { deleted?: number })?.deleted ?? 0;
      if (n > 0) notify.success(`Deleted ${n} EDD report${n === 1 ? '' : 's'}.`);
      else       notify.warning('Nothing was deleted.');
      setSelectedJobOids(new Set());
      void load();
    } catch {
      notify.error('Failed to delete the selected EDD report(s).');
    } finally {
      setDeleting(false);
    }
  }, [confirmDlg, load, notify]);

  // ── Edit Report (revise) — Report Tracking.  The single ticked report's Oid.
  // ALPACA's EditReport_Execute re-opens the report in Custom Reporting edit
  // mode; here a focused dialog (package + samples + mandatory revision reason)
  // posts to /report/{oid}/revise, which mints <base>R<n>, supersedes the old
  // report, and writes the "Report Tracking" ValueChanged audit. ─────────────
  const editOid = useMemo(
    () => (selectedJobOids.size === 1 ? Array.from(selectedJobOids)[0] : null),
    [selectedJobOids]);

  const submitRevise = useCallback(async (form: {
    revisionReason: string; packageName: string | null; keepParameterOids: string[];
  }) => {
    if (!editOid) return;
    setEditBusy(true);
    try {
      const res = await runLoading(
        api.post(`/api/v1/reporting/report/${encodeURIComponent(editOid)}/revise`, {
          revisionReason:    form.revisionReason,
          packageName:       form.packageName,
          keepParameterOids: form.keepParameterOids,
        }),
        { blocking: true, label: 'Saving revision…' });
      const d = (res.data as { data?: { revisionNo?: number } })?.data;
      // §18.6: revise is NOT one of the 3 ID-echoing flows — do NOT echo the new
      // ReportID in the toast; the new revision shows in the grid (Pending
      // Validation) after reload.
      notify.success(
        `Report revised${d?.revisionNo ? ` (revision ${d.revisionNo})` : ''}. `
        + `The new revision is now Pending Validation.`);
      setEditOpen(false);
      setSelectedJobOids(new Set());
      void load();
    } catch (e) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message ?? 'Failed to revise the report.');
    } finally {
      setEditBusy(false);
    }
  }, [editOid, load, notify]);

  // ── Report Approval — POST the ticked Reporting Oids to the API. ───
  // Stamps ReportApprovedDate + ReportApprovedBy (moving each row onto the
  // Delivery list), writes one "Report Approval" audit row each, then we
  // reload — approved rows fall off (ReportApprovedDate IS NOT NULL).
  const approveSelected = useCallback(async (oids: string[]) => {
    if (kind !== 'approval' || oids.length === 0) return;
    setApproving(true);
    try {
      const res  = await runLoading(
        api.post('/api/v1/reporting/approval/approve', { oids }),
        { blocking: true, label: 'Approving…' });
      const data = (res.data as { data?: { approvedCount?: number; requestedCount?: number;
                                            alreadyApproved?: number } }).data;
      const n   = data?.approvedCount   ?? 0;
      const req = data?.requestedCount  ?? oids.length;
      const skipped = data?.alreadyApproved ?? 0;
      // Shared full-width banner (BO UI Standard §13) — never an inline pill.
      notify.success(
        skipped > 0
          ? `Approved ${n} of ${req} reports. ${skipped} already approved.`
          : `Approved ${n} report${n === 1 ? '' : 's'}.`);
      setSelectedJobOids(new Set());
      void load();
    } catch (e) {
      console.error(e);
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
                    .response?.data?.error?.message
                ?? (e as { message?: string }).message
                ?? 'Approve failed.';
      notify.error(msg);
    } finally {
      setApproving(false);
    }
  }, [kind, load]);

  // ── Report Delivery — Preview: fetch the selected report(s)' stored PDF(s) and
  // open each in its own new browser tab.  Blob URLs are revoked after 10 s to
  // free memory.  (Combined from the 2026-07-04 MOR/Report-Delivery change set.)
  const previewSelected = useCallback(async () => {
    if (!(kind === 'delivery' || kind === 'validation' || kind === 'approval')
        || selectedJobOids.size === 0) return;

    const rowList = Array.from(selectedJobOids);
    const typedRows = rows as Array<{ id?: string; reportId?: string; reportName?: string }>;
    const getReportId = (rowId: string): string =>
      typedRows.find(r => r.id === rowId)?.reportId ?? rowId;
    // A saved "CCPP Report" or "LSI Report" is an Excel (opened via a token URL),
    // not a PDF.
    const isExcelReportRow = (rowId: string): boolean => {
      const n = (typedRows.find(r => r.id === rowId)?.reportName ?? '').trim().toLowerCase();
      return n === 'ccpp report' || n === 'lsi report';
    };

    setPdfPreviewLoading(true);
    let successCount = 0;
    const errors: string[] = [];
    // Show the shared blocking loader while the PDF(s) generate/fetch — otherwise
    // the page just sits and a blank tab appears before the report renders
    // (2026-07-11 user request: show loading, then the preview). Each tab is
    // opened only once its blob is ready, so it renders immediately on open.
    await runLoading(
      Promise.allSettled(rowList.map(async rowId => {
        const reportId = getReportId(rowId);
        try {
          // A CCPP (Water Stability) / LSI saved report is an EXCEL — open it via a
          // real preview URL (deliver=token) in a new tab, not a client blob +
          // <a download> (which pops a "Save As" dialog).  Single-click path (2026-10-13).
          if (isExcelReportRow(rowId)) {
            const tr = await api.get(
              `/api/v1/reports/${encodeURIComponent(reportId)}/pdf`, { params: { deliver: 'token' } });
            const url = (tr.data as { data?: { url?: string } })?.data?.url;
            if (url) { window.open(url, '_blank'); successCount++; }
            else errors.push(reportId);
            return;
          }
          const res = await api.get<Blob>(
            `/api/v1/reports/${encodeURIComponent(reportId)}/pdf`,
            { responseType: 'blob' });
          const url = URL.createObjectURL(res.data);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 10_000);
          successCount++;
        } catch {
          errors.push(reportId);
        }
      })),
      { blocking: true, label: 'Generating preview…' });
    if (errors.length > 0)
      notify.error(`No PDF available for: ${errors.join(', ')}`);
    if (successCount > 0)
      notify.success(`Opened ${successCount} PDF${successCount > 1 ? 's' : ''} in new tab${successCount > 1 ? 's' : ''}.`);
    setPdfPreviewLoading(false);
  }, [kind, selectedJobOids, rows, notify]);

  // ── Report Delivery — ALPACA's "Send" action (SimpleAction, ConfirmationMessage
  // = null): delivers the ticked reports DIRECTLY, no popup, no confirm, using each
  // report's OWN saved values — its saved recipient email + DoNotDeliver flag (Email
  // method).  Stamps DateDelivered/By + writes one "Report Delivery" audit row each;
  // server-side queues a delivery email for every delivered, non-DoNotDeliver report
  // (sent out-of-band by the background worker, never in this request — §9).
  const deliverSelected = useCallback(async () => {
    const oids = Array.from(selectedJobOids);
    if (kind !== 'delivery' || oids.length === 0) return;
    setDelivering(true);
    try {
      const all = rows as ReportingListRow[];
      const items = oids.map(oid => {
        const row  = all.find(r => r.id === oid);
        const edit = deliveryEdits.get(oid);
        // Use edited values when the user toggled them; fall back to the row's saved values.
        const mail        = edit !== undefined ? edit.mail        : (row?.mail        ?? false);
        const doNotDeliver = edit !== undefined ? edit.doNotDeliver : (row?.doNotDeliver ?? false);
        const email       = edit !== undefined ? edit.email       : (row?.email       ?? null);
        // CC combo value: the edited CC when the user explicitly picked one, else the
        // DISPLAYED EmailCC column value (row.emailCC).
        //
        // CHANGED 2026-08-18 (user: "i want save emailcc column values to table if i
        // click sent"). This previously sent row.emailCCSend — a SEPARATE column the SP
        // computes to keep the pre-2026-08-05 rule, which is NULL whenever a global
        // dbo.DefaultMailRecipient.To override exists. Because AI_Reporting_DeliverReports
        // persists with COALESCE(i.EmailCC, r.EmailCC), a NULL saved nothing, so the CC
        // column was never written for an untouched row — only 9 of 768 delivered reports
        // carry a saved EmailCC, and the live DB has 7 override rows all with To set.
        //
        // ⚠ THIS ALSO CHANGES WHO RECEIVES MAIL, because save and send are the same value:
        // ReportDeliveryNotifier reads dbo.Reporting.EmailCC as the Cc line
        // (ReportDeliveryNotifier.cs ~line 142). So the real Project/Client CC addresses
        // now go out as Cc even while a DefaultMailRecipient override is in force — the
        // override no longer suppresses the CC line. That is the explicit, informed
        // request; the 2026-08-05 display/send split it supersedes existed precisely to
        // prevent it. Do not "restore" emailCCSend here without a new instruction.
        const emailCC     = edit?.ccPicked ? edit.emailCC : (row?.emailCC ?? null);
        return {
          oid,
          deliveryMethodCode: mail ? 2 : 1,   // 2 = Mail (physical), 1 = Email (ALPACA default)
          deliveryAddress: null,
          deliveryCopies: null,
          email,
          emailCC,
          doNotDeliver,
        };
      });
      const res  = await runLoading(
        api.post('/api/v1/reporting/delivery/deliver', { items }),
        { blocking: true, label: 'Delivering…' });
      const data = (res.data as { data?: { deliveredCount?: number; requestedCount?: number;
                                            alreadyDelivered?: number; emailsQueued?: number;
                                            sendFailedCount?: number; oversizedCount?: number;
                                            warnings?: string[] } }).data;
      const n   = data?.deliveredCount  ?? 0;
      const req = data?.requestedCount  ?? oids.length;
      const skipped = data?.alreadyDelivered ?? 0;
      const queued  = data?.emailsQueued ?? 0;
      const failed  = data?.sendFailedCount ?? 0;
      const oversized = data?.oversizedCount ?? 0;
      const warnings  = data?.warnings ?? [];
      // Shared full-width banner (BO UI Standard §13) — never an inline pill.
      // Emails are QUEUED (sent out-of-band by ReportDeliveryEmailWorker), so the
      // request no longer waits on SMTP — say "queued", not "sent" (ADR 2026-09-29).
      notify.success(
        `Delivered ${n} of ${req} report${req === 1 ? '' : 's'}.`
        + (queued > 0 ? ` ${queued} email${queued === 1 ? '' : 's'} queued for sending.` : '')
        + (skipped > 0 ? ` ${skipped} already delivered.` : ''));
      // A TOO-LARGE report is a PERMANENT size rejection — surface it distinctly from a
      // transient SMTP failure (a retry can't help), showing the size + guidance the API built.
      if (oversized > 0)
        notify.warning(
          warnings.length
            ? warnings.join(' ')
            : `${oversized} report${oversized === 1 ? '' : 's'} too large to email — exceeds the mail server's message-size limit (marked Sending Failed, returned to Pending Delivery).`);
      // A true (transient) SMTP send failure flips the report to Sending Failed (RP-4-3) and
      // returns it to Pending Delivery — surface that distinctly from success.
      if (failed > 0)
        notify.warning(`${failed} report${failed === 1 ? '' : 's'} failed to send — marked Sending Failed (RP-4-3) and returned to Pending Delivery.`);
      setSelectedJobOids(new Set());
      void load();
    } catch (e) {
      console.error(e);
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
                    .response?.data?.error?.message
                ?? (e as { message?: string }).message
                ?? 'Deliver failed.';
      notify.error(msg);
    } finally {
      setDelivering(false);
    }
  }, [kind, rows, selectedJobOids, deliveryEdits, load]);

  // Toggle one JobID in the selection, enforcing the same-client rule for
  // Custom Reporting (a report may only span ONE client).  Shared by the
  // checkbox AND the JobID-cell click (the list-view drill-to-detail was
  // removed 2026-06-13 — the only path forward is select → Next).
  const toggleJobSelection = useCallback((id: string) => {
    const prev = selectedJobOids;
    if (prev.has(id)) { const n = new Set(prev); n.delete(id); setSelectedJobOids(n); return; }
    if (kind === 'custom' && prev.size > 0) {
      const all = rows as ReportingListRow[];
      const selClient = all.find(r => prev.has(r.id))?.client ?? null;
      const newClient = all.find(r => r.id === id)?.client ?? null;
      if ((selClient ?? '') !== (newClient ?? '')) {
        // Same-client rule violation — surface via the shared notifier (§18.6),
        // NOT an inline banner.  Called outside a state updater so it fires once.
        notify.warning(
          `Multiple JobIDs must belong to the same client. Your selection is "${selClient ?? '(none)'}" — "${newClient ?? '(none)'}" is a different client. Clear the selection to switch clients.`);
        return;
      }
    }
    const n = new Set(prev); n.add(id); setSelectedJobOids(n);
  }, [selectedJobOids, kind, rows, notify]);

  // Row-click handler — same signature for every Reporting kind so the
  // shared GenericReportingTable can call it as `onPreview(reportId, row)`.
  //
  // Behaviour depends on the row's source + the list `kind`:
  //
  //   • MOR Pending Approval list + adhoc row  → swap THIS PAGE'S body for
  //     MorAdhocReportingPage in read-only Approve mode.  Stays on the
  //     same `/m/morpapp` URL so the sidebar nav highlight does NOT move.
  //   • MOR Pending Delivery list + adhoc row  → same, but Deliver mode.
  //   • Anything else (legacy DWQR rows, other reporting kinds)        →
  //     open the rendered PDF in a new tab (existing behaviour).
  //
  // The "row.category === 'MOR Adhoc'" check is how the bridge SP marks
  // saved-report rows from AI_MorAdhoc_SavedReport — see
  // sql/AI_Reporting_MOR_SP.sql.
  const openReportPdf = useCallback(async (reportId: string, row?: unknown) => {
    if (!reportId) return;

    // Presentational: opening a workflow report marks it reviewed (drives the
    // Delivery stepper's Review step + the ✓ marker).  No side effect on data.
    if (kind === 'validation' || kind === 'approval' || kind === 'delivery' || kind === 'tracking') {
      const rid = (row as { id?: string })?.id;
      if (rid) setReviewedOids(prev => (prev.has(rid) ? prev : new Set(prev).add(rid)));
    }

    // Custom Reporting: the list-view drill-to-detail was REMOVED (2026-06-13).
    // Clicking a JobID no longer opens a per-job page — the only path forward
    // is select JobID(s) → Next → combined view (where Report/PDF/Save live).
    // A JobID click simply toggles that row's selection (same-client guarded),
    // as a convenience alongside the checkbox.
    if (kind === 'custom') {
      const r = row as { id?: string } | undefined;
      if (r?.id) toggleJobSelection(r.id);
      return;
    }

    // EDD Generator rows deliver a stored Excel (.xlsx) file — NOT a PDF.
    // It lives in dbo.FileData (FK EDDReportGenerator.ExcelFile) and is fetched
    // by the EDD row's Oid via the dedicated endpoint.  Save it as a file.
    if (kind === 'edd') {
      const eddOid = ((row as { id?: string })?.id ?? '').toString();
      if (!eddOid) {
        notify.warning('Cannot download this EDD — missing internal id.');
        return;
      }
      try {
        const res = await api.get(
          `/api/v1/reporting/edd/${encodeURIComponent(eddOid)}/download`,
          { responseType: 'blob' });
        const url = URL.createObjectURL(res.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        // Filename = "<TemplateName>_<yyyyMMddHHmmss>.xlsx" (same shape as the
        // Spreadsheet View's Export; name sanitized).
        const p2 = (n: number) => String(n).padStart(2, '0');
        const t = new Date();
        const ts = `${t.getFullYear()}${p2(t.getMonth() + 1)}${p2(t.getDate())}${p2(t.getHours())}${p2(t.getMinutes())}${p2(t.getSeconds())}`;
        const tplRow = row as { templateName?: string | null; template?: string | null };
        const tplName = tplRow.templateName ?? tplRow.template ?? reportId ?? 'EDD';
        const safeName = String(tplName).replace(/[\\/:*?"<>|]/g, '_');
        a.download = `${safeName}_${ts}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        // Milestone-success MODAL (BO UI Standard §13) via the shared global
        // showDialog — same centred acknowledgement as the EDD Save flow, not a
        // transient banner (user request 2026-07-08).
        await showDialog({
          type: 'success',
          title: 'EDD Downloaded',
          idLabel: 'EDD Report ID',
          idValue: reportId,
          rows: [
            { label: 'Template', value: String(tplName) },
            { label: 'File', value: `${safeName}_${ts}.xlsx` },
          ],
          note: 'The EDD Excel file has been downloaded.',
        });
      } catch {
        notify.error(`EDD "${reportId}" has no file available.`);
      }
      return;
    }

    // Monthly Summary Repository rows deliver a stored Excel import file
    // (dbo.FileData, FK MonthlySummaryRepository.ImportFile), fetched by the
    // row's Oid.  Only ~39/169 rows have a file; the rest 404 → message.
    if (kind === 'msr') {
      const msrOid = ((row as { id?: string })?.id ?? '').toString();
      if (!msrOid) {
        notify.warning('Cannot download this summary — missing internal id.');
        return;
      }
      try {
        const res = await api.get(
          `/api/v1/reporting/msr/${encodeURIComponent(msrOid)}/download`,
          { responseType: 'blob' });
        const url = URL.createObjectURL(res.data as Blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportId || 'MonthlySummary'}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        notify.success('Monthly Summary downloaded successfully.');
      } catch {
        notify.error(`Monthly Summary "${reportId}" has no import file available.`);
      }
      return;
    }

    const morRow = row as (MorReportRow & Record<string, unknown>) | undefined;

    // All three MOR list views (MOR Reporting, MOR Pending Approval, MOR
    // Pending Delivery) show legacy DWQR reports.  Their generated PDF lives
    // in DrinkingWaterQualityReports — stream it via the download endpoint.
    // The row's `id` is the legacy DWQR Oid (bridge SP projects it as Id).
    const isMorList = kind === 'mor-list' || kind === 'mor-approval' || kind === 'mor-delivery';
    if (isMorList && morRow) {
      const dwqrId = (morRow.id ?? (morRow.Id as string) ?? '').toString();
      if (!dwqrId) {
        console.error('MOR row missing id', morRow);
        notify.warning('Cannot download this report — missing internal id.');
        return;
      }
      try {
        const res = await runLoading(
          api.get(`/api/v1/mor-adhoc/${encodeURIComponent(dwqrId)}/download?format=pdf`, { responseType: 'blob' }),
          { blocking: true, label: 'Generating preview…' });
        const url = URL.createObjectURL(res.data as Blob);
        if (!window.open(url, '_blank'))
          notify.info('Preview ready — allow pop-ups for this site to open it.');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch {
        notify.error(`Report "${reportId}" has no generated PDF available.`);
      }
      return;
    }

    // A saved "CCPP Report" (Water Stability) or "LSI Report" is an EXCEL, not a PDF.
    // Open it via a REAL preview URL (deliver=token) in a new tab instead of a client
    // blob + <a download> — a real server URL avoids the browser "Save As" dialog; the
    // xlsx saves straight to Downloads (or opens if Excel is the default handler),
    // with a proper filename (2026-10-13; LSI added 2026-10-20).
    const rowReportName = ((row as { reportName?: string } | undefined)?.reportName ?? '').trim().toLowerCase();
    if (rowReportName === 'ccpp report' || rowReportName === 'lsi report') {
      try {
        const res = await runLoading(
          api.get(`/api/v1/reports/${encodeURIComponent(reportId)}/pdf`, { params: { deliver: 'token' } }),
          { blocking: true, label: 'Generating preview…' });
        const url = (res.data as { data?: { url?: string } })?.data?.url;
        if (!url) notify.error(`Report "${reportId}" could not be generated.`);
        else if (!window.open(url, '_blank'))
          notify.info('Report ready — allow pop-ups for this site to open it.');
      } catch (e: unknown) {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message ?? `Report "${reportId}" could not be loaded.`;
        notify.error(msg);
      }
      return;
    }

    // Default: open the rendered PDF in a new tab.  The server first tries the
    // stored PDF (legacy dbo.Report) and falls back to re-rendering from the
    // saved report (AI-created reports store no PDF) — so this works for both.
    // Show the shared loader on the CURRENT page while the PDF generates/downloads,
    // THEN open the ready blob in a new tab — so the user never stares at a blank
    // tab (2026-07-11 user request). The blob is fully in-memory before the tab
    // opens, so the new tab renders immediately.
    try {
      const res = await runLoading(
        api.get(`/api/v1/reports/${encodeURIComponent(reportId)}/pdf`, { responseType: 'blob' }),
        { blocking: true, label: 'Generating preview…' });
      const url = URL.createObjectURL(res.data as Blob);
      if (!window.open(url, '_blank'))
        notify.info('Preview ready — allow pop-ups for this site to open it.');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: unknown) {
      // The PDF error body is a Blob (responseType: 'blob') — parse it for the
      // server's message, else show a generic failure.  Shared notifier (§18.6).
      const errBlob = (e as { response?: { data?: Blob } }).response?.data;
      let msg = `Report "${reportId}" could not be loaded.`;
      if (errBlob && typeof errBlob.text === 'function') {
        try { msg = JSON.parse(await errBlob.text())?.error?.message ?? msg; }
        catch { /* not JSON — keep the generic message */ }
      }
      notify.error(msg);
    }
  }, [kind, toggleJobSelection, notify]);

  useEffect(() => { setStatusChip('all'); }, [kind]);  // reset stage chip on kind change

  // Active view's column set (GoLive header set per kind).  Drives both the
  // shared <AppDataGrid> (via gridCols below) and the CSV export.
  const cols = useMemo<ColDef[]>(() => {
    if (kind === 'edd') return EDD_COLS;
    if (kind === 'msr') return MSR_COLS;
    return reportingCols(kind);
  }, [kind]);

  // ALPACA parity: Delete is disabled when any selected report is Sent (RP-4-2).
  // The server already blocks it (409), but the button must be disabled upfront
  // so the UI matches ALPACA — no need to click and get a warning.
  const anySelectedSent = useMemo(() =>
    Array.from(selectedJobOids).some(id => {
      const row = (rows as ReportingListRow[]).find(r => r.id === id);
      return row?.statusId === 'RP-4-2';
    }),
    [selectedJobOids, rows]);

  const searchFiltered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r =>
      Object.values(r as Record<string, unknown>).some(v =>
        typeof v === 'string' && v.toLowerCase().includes(term),
      ));
  }, [rows, search]);

  // Stage / Status chip filter — Reporting rows use stageText; MOR rows use statusText.
  const statusField = (r: unknown): string | undefined => {
    const x = r as { stageText?: string; statusText?: string };
    return x.stageText ?? x.statusText;
  };
  // Rows handed to <AppDataGrid> — the page-level search box + stage-chip tab
  // filter.  Per-column filtering, sorting and pagination are owned by the
  // shared grid, so they're no longer computed here.
  const gridRows = useMemo(() => {
    if (!hasStageChips || statusChip === 'all') return searchFiltered;
    return searchFiltered.filter(r => statusField(r) === statusChip);
  }, [searchFiltered, statusChip, hasStageChips]);

  // Auto-derived stage chips with row counts.
  const stageChips = useMemo(() => {
    if (!hasStageChips) return [] as [string, number][];
    const m = new Map<string, number>();
    for (const r of searchFiltered) {
      const s = statusField(r);
      if (!s) continue;
      m.set(s, (m.get(s) ?? 0) + 1);
    }
    // Hide the Stage toggle entirely when the list has only a single stage
    // (e.g. Report Validation only ever shows "Pending Validation") — the
    // All / one-stage tabs would be redundant (2026-07-11 user request).
    const entries = Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
    return entries.length <= 1 ? [] : entries;
  }, [searchFiltered, hasStageChips]);

  // Whether the active view shows a ReportID / EDDReportID link column — when
  // not, the JobID cell becomes the report-open link instead (GoLive parity).
  const hasReportIdCol = useMemo(
    () => cols.some(c => c.key === 'ReportID' || c.key === 'EDDReportID'),
    [cols]);

  // EDDID cell (Report Validation) → download the job's EDD .xlsx by its Oid.
  // Reuses GET /reporting/edd/{oid}/download — the SAME stored file the EDD
  // Generator list serves (dbo.FileData via EDDReportGenerator.ExcelFile).
  const downloadEdd = useCallback(async (eddOid: string, eddId: string) => {
    if (!eddOid) return;
    try {
      const res = await runLoading(
        api.get(`/api/v1/reporting/edd/${encodeURIComponent(eddOid)}/download`, { responseType: 'blob' }),
        { blocking: true, label: 'Downloading EDD…' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(eddId || 'EDD').replace(/[\\/:*?"<>|]/g, '_')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      notify.success(`Downloaded EDD ${eddId}.`);
    } catch {
      notify.error('Could not download the EDD file.');
    }
  }, [notify]);

  // <AppDataGrid> columns for the active view — derived from the GoLive ColDef
  // set.  Only Status (pill), the report-open link cells (ReportID /
  // EDDReportID / JobID / MSRID) and the empty-cell dash need a renderer; the
  // rest are plain black text via the shared grid's default.  `text` always
  // carries the raw formatted string so the grid filters / sorts on what the
  // cell actually shows.
  const gridCols = useMemo<AppColumn<Record<string, unknown>>[]>(() => {
    const built = cols.map<AppColumn<Record<string, unknown>>>(c => {
      const cellText = (row: Record<string, unknown>) => c.formatter(row as ReportingRow);

      // (The EDD per-row Download column was removed 2026-07-15 — see EDD_COLS.)

      // Status → shared status pill (same statusVariant mapping as before).
      if (c.key === 'Status') {
        return { field: c.key, header: c.label, width: c.width, type: 'status', text: cellText };
      }

      // EDDID (Report Validation) → a clickable link that downloads the job's EDD
      // .xlsx (GET /reporting/edd/{eddOid}/download).  Shows plain text when the id
      // is known but the row carries no downloadable EDD Oid (shouldn't normally
      // happen), and nothing when the job has no EDD.
      if (c.key === 'EDDID') {
        return {
          field: c.key, header: c.label, width: c.width, text: cellText,
          render: (row) => {
            const rr = row as { eddId?: string | null; eddOid?: string | null };
            const id = rr.eddId ?? '';
            if (!id) return '';
            if (!rr.eddOid) return id;
            return (
              <a href="#" title={`Download ${id}`}
                 onClick={(e) => { e.preventDefault(); void downloadEdd(String(rr.eddOid), String(id)); }}
                 style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>
                {id}
              </a>
            );
          },
        };
      }

      // Report-open link cells — clicking opens the PDF / file (openReportPdf
      // routes per kind).  Keep the link, expose the id string as `text`.
      const isReportLink = c.key === 'ReportID' || c.key === 'EDDReportID';
      const isMsrLink    = c.key === 'MSRID';
      const isJobLink    = c.key === 'JobID' && !hasReportIdCol;
      if (isReportLink || isMsrLink || isJobLink) {
        return {
          field: c.key, header: c.label, width: c.width, text: cellText,
          render: (row) => {
            const v = c.formatter(row as ReportingRow);
            if (!v) return isJobLink ? '' : (v || '—');
            // MSRID is only a link when the row actually has a stored file.
            if (isMsrLink && !(row as { importFile?: string | null }).importFile) {
              return v;
            }
            // JobID link passes any non-empty identifier (reportId ?? id) so
            // openReportPdf can route to the custom-drill / saved-PDF path.
            const clickId = isJobLink
              ? String((row as { reportId?: string | null }).reportId
                  ?? (row as { id?: string | null }).id ?? '')
              : v;
            if (isJobLink && !clickId) return v;
            // ✓-reviewed marker (Reporting-Module-Prototype.html) — a green tick
            // once the user has opened this workflow report's ReportID.
            const reviewed = isReportLink
              && reviewedOids.has(String((row as { id?: string }).id ?? ''));
            return (
              <>
                {reviewed && <span style={{ color: 'var(--ai-pill-ok-fg)' }} title="Reviewed">✓ </span>}
                <a href="#"
                   onClick={(e) => { e.preventDefault(); e.stopPropagation(); openReportPdf(clickId, row); }}
                   title={isReportLink ? undefined : `Open ${v}`}
                   /* JobID is not underlined (user request); ReportID / MSRID links keep it. */
                   style={{ color: 'inherit', textDecoration: isJobLink ? 'none' : 'underline', cursor: 'pointer' }}>
                  {v}
                </a>
              </>
            );
          },
        };
      }

      // ── Delivery view: EmailTo + EmailCC → checked-combo; Mail/DoNotDeliver → checkbox ──
      // Both 'Email' (To) and 'EmailCC' (CC) are editable checked-combos, column-aware.
      // They differ ONLY in the EMPTY state: To shows "Select recipients…"; CC shows
      // BLANK — no "Select CC…" prompt and no dash — so the CC value auto-shows when it
      // exists (resolved for a pending report, saved for a delivered one), else empty
      // (user request 2026-07-29). CC options come from /delivery/cc-batch.
      if (kind === 'delivery' && (c.key === 'Email' || c.key === 'EmailCC')) {
        const col: 'To' | 'CC' = c.key === 'EmailCC' ? 'CC' : 'To';
        const valueOf = (e: DeliveryEdit | undefined, r: ReportingListRow): string | null =>
          col === 'CC'
            ? (e !== undefined ? e.emailCC : r.emailCC)
            : (e !== undefined ? e.email   : r.email);
        return {
          field: c.key, header: c.label, width: c.width,
          text: (row: Record<string, unknown>) => {
            const r = row as unknown as ReportingListRow;
            return valueOf(deliveryEdits.get(r.id), r) ?? '';
          },
          render: (row: Record<string, unknown>): ReactNode => {
            const r         = row as unknown as ReportingListRow;
            const edit      = deliveryEdits.get(r.id);
            const effective = valueOf(edit, r);
            const dnd       = edit !== undefined ? edit.doNotDeliver : (r.doNotDeliver ?? false);
            const contacts  = r.jobOid ? ((col === 'CC' ? deliveryCcContacts : deliveryContacts).get(r.jobOid) ?? []) : [];
            const isOpen    = emailComboId === r.id && emailComboCol === col;
            // Show each selected recipient's NAME (matched against the option list)
            // instead of the raw email; falls back to the email itself when unmatched.
            const displayText = effective
              ? effective.split(/[;\n,]+/).map(e => e.trim()).filter(Boolean)
                  .map(email => contacts.find(cc => cc.email.toLowerCase() === email.toLowerCase())?.contactName || email)
                  .join(', ')
              : null;

            // DoNotDeliver no longer suppresses the combo (user request 2026-08-14):
            // the picker renders for EVERY row so the recipient list is always visible
            // and editable in one consistent control. This SUPERSEDES the 2026-08-03
            // rule, which rendered DoNotDeliver rows as muted read-only text. Safe
            // because DoNotDeliver is enforced on the SEND path, not in this cell —
            // deliverSelected still skips those rows, so editing a recipient here can
            // never cause mail to go out. The flag is surfaced in the button tooltip.
            //
            // Nothing to show AND nothing to pick → To dash / CC blank. Otherwise the
            // combo button renders below; its dropdown lists the job's contacts PLUS any
            // saved recipient, so a delivered report's saved EmailTo/EmailCC also appears
            // (checked) in the dropdown even when the client has no matching contact
            // (user request 2026-07-30).
            if (contacts.length === 0 && !displayText) {
              return col === 'CC'
                ? <span style={{ padding: '0 4px' }} />
                : <span style={{ color: 'var(--ai-muted)', padding: '0 4px', userSelect: 'none' }}>—</span>;
            }

            // Active → interactive dropdown
            return (
              <button
                data-dlv-trigger={r.id}
                onClick={(ev) => {
                  ev.stopPropagation();   // don't open the row-click detail
                  if (isOpen) { setEmailComboId(null); setEmailComboPos(null); return; }
                  const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                  setEmailComboPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 300) });
                  setEmailComboCol(col);
                  setEmailComboId(r.id);
                }}
                title={[
                  effective ?? (col === 'CC' ? 'Click to select CC recipients' : 'Click to select recipients'),
                  dnd ? 'Flagged Do Not Deliver — recipient saved, not sent.' : null,
                ].filter(Boolean).join('\n')}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 4,
                  background: isOpen ? 'var(--ai-orange-tint)' : 'var(--ai-header-bg)',
                  cursor: 'pointer',
                  border: `1px solid ${isOpen ? 'var(--brand-accent, var(--ai-orange))' : 'var(--ai-line)'}`,
                  borderRadius: 3,
                  padding: '1px 4px 1px 6px',
                  font: 'inherit', boxSizing: 'border-box',
                }}
              >
                <span style={{
                  flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap', textAlign: 'left',
                  color: displayText ? 'inherit' : 'var(--ai-muted)',
                }}>
                  {displayText ?? (col === 'CC' ? '' : 'Select recipients…')}
                </span>
                <span style={{ flexShrink: 0, fontSize: 11, color: 'var(--ai-muted)', lineHeight: 1 }}>▾</span>
              </button>
            );
          },
        };
      }

      if (kind === 'delivery' && (c.key === 'Mail' || c.key === 'DoNotDeliver')) {
        const fk = c.key === 'Mail' ? 'mail' as const : 'doNotDeliver' as const;
        return {
          field: c.key, header: c.label, width: c.width, align: 'center' as const,
          text: cellText,
          render: (row: Record<string, unknown>): ReactNode => {
            const r    = row as unknown as ReportingListRow;
            const edit = deliveryEdits.get(r.id);
            const val  = edit !== undefined ? edit[fk] : (r[fk] ?? false);
            return (
              <input
                type="checkbox"
                checked={!!val}
                onClick={(e) => e.stopPropagation()}   // don't open the row-click detail
                onChange={() => {
                  const newVal = !val;
                  setDeliveryEdits(prev => {
                    const m  = new Map(prev);
                    const ex = m.get(r.id) ?? { email: r.email ?? null, emailCC: r.emailCC ?? null, mail: r.mail ?? false, doNotDeliver: r.doNotDeliver ?? false, ccPicked: false };
                    m.set(r.id, { ...ex, [fk]: newVal });
                    return m;
                  });
                  // Ticking DoNotDeliver no longer force-closes an open recipient
                  // combo — the picker stays available on those rows (2026-08-14).
                }}
                style={{ cursor: 'pointer', accentColor: 'var(--brand-accent, var(--ai-orange))', width: 15, height: 15 }}
              />
            );
          },
        };
      }
      // ── end delivery overrides ────────────────────────────────────────────────

      // Centered boolean columns — shared read-only `.cellcheck` glyph (BO UI
      // Standard); `text` keeps the ✓/blank string so filter/sort are unchanged.
      const center = c.key === 'Mail' || c.key === 'DoNotDeliver'
        || c.key === 'IsDateCollected' || c.key === 'IsDateReceived'
        || c.key === 'Amended';
      if (center) {
        return {
          field: c.key, header: c.label, width: c.width, align: 'center', text: cellText,
          render: (row): ReactNode => {
            const on = !!cellText(row);
            // Amended checkbox: on hover show the amendment note as a tooltip
            // (GoLive parity — ReportingViewController line 1270:
            //  e.Cell.ToolTip = Reporting.AmendedComment, the amended Note text).
            const tip = (c.key === 'Amended' && on)
              ? ((row as { amendedComment?: string | null }).amendedComment ?? undefined)
              : undefined;
            // MUI Tooltip gives instant appearance (like DevExpress XtraGrid) vs
            // the native title attribute which delays 500 ms+ (2026-07-13).
            const span = <span className={`cellcheck${on ? ' on' : ''}`} />;
            return tip
              ? <Tooltip title={tip} enterDelay={150} placement="top" arrow>{span}</Tooltip>
              : span;
          },
        };
      }

      // Plain text cell — keep the em-dash placeholder for empty values while
      // filtering/sorting on the real formatted text via `text`.
      return {
        field: c.key, header: c.label, width: c.width, text: cellText,
        render: (row): ReactNode => { const v = c.formatter(row as ReportingRow); return v || '—'; },
      };
    });

    // E-Signature column (ADR-0009) — only on the signable lists when enabled.
    if (signCfg.enabled && (kind === 'validation' || kind === 'approval')) {
      built.push({
        field: '__esign', header: 'E-Signature', width: 210,
        text: () => (kind === 'validation' ? 'Sign required: Validated' : 'Validated; sign required: Approved'),
        render: () => <EsignBadge stage={kind as 'validation' | 'approval'} />,
      });
    }
    return built;
  }, [cols, hasReportIdCol, openReportPdf, downloadEdd, signCfg.enabled, kind,
      deliveryEdits, deliveryContacts, deliveryCcContacts, emailComboId, emailComboCol, reviewedOids]);

  // Export matrix — built ONCE per data/column change, NOT on every render.  It
  // walks EVERY row × column (incl. date formatting), so recomputing it inline on
  // each render (hover, selection, stage-chip, etc.) was a big cost on large lists
  // like Report Tracking (~514 rows) — a major contributor to the slow load the
  // user reported (2026-07-12).  Memoised so it only recomputes when the rows or
  // columns actually change.
  const exportMatrix = useMemo(
    () => gridExportMatrix(gridRows as Record<string, unknown>[], gridCols),
    [gridRows, gridCols]);

  // Selection bridge for <AppDataGrid> (actionable kinds).  The grid hands us
  // the FULL next selection set; we diff against the current set, apply the
  // removals, then add each new id under the Custom-Reporting same-client rule
  // (a report may only span JobIDs of ONE client — 2026-06-13).  This keeps the
  // exact guard the old checkbox/toggle-all wiring enforced.
  const onGridSelectionChange = useCallback((next: Set<string | number>) => {
    const prev = selectedJobOids;
    const nextStr = new Set<string>();
    next.forEach(id => nextStr.add(String(id)));
    const removed = [...prev].filter(id => !nextStr.has(id));
    const added   = [...nextStr].filter(id => !prev.has(id));

    const working = new Set(prev);
    removed.forEach(id => working.delete(id));

    if (added.length === 0) { setSelectedJobOids(working); return; }

    if (kind === 'custom') {
      const all = rows as ReportingListRow[];
      // Anchor on the client already in the (post-removal) selection, else on
      // the first id being added.
      const anchorClient =
        (working.size > 0 ? all.find(r => working.has(r.id))?.client : undefined)
        ?? all.find(r => r.id === added[0])?.client ?? null;
      let skipped = 0;
      added.forEach(id => {
        const c = all.find(r => r.id === id)?.client ?? null;
        if ((c ?? '') === (anchorClient ?? '')) working.add(id); else skipped++;
      });
      // Same-client rule — surface skipped rows via the shared notifier (§18.6).
      if (skipped > 0) {
        notify.warning(
          `Multiple JobIDs must belong to the same client. ${skipped} JobID(s) of other clients were skipped — a report spans one client only. Clear the selection to switch clients.`);
      }
      setSelectedJobOids(working);
      return;
    }

    added.forEach(id => working.add(id));
    setSelectedJobOids(working);
  }, [selectedJobOids, kind, rows, notify]);

  // Open the attestation popup for the single selected report (ADR-0009).
  const openSign = (stage: 'validation' | 'approval') => {
    const oid = Array.from(selectedJobOids)[0];
    if (!oid) return;
    const row = (gridRows as Array<Record<string, unknown>>).find(r => (r as { id?: string }).id === oid);
    const reportId = String((row?.['ReportID'] ?? row?.['reportId'] ?? oid));
    setSignTarget({ stage, oid, reportId });
  };

  // ── Phase 2.3 · Module action keys for the Reporting workflow (CLAUDE.md
  //    standing rule 14, §18.6, ADR-0013).  Each key MIRRORS the title-row
  //    workflow button exactly — same handler, same disabled/visibility gate,
  //    grouped under the form's title.  Only the ACTIVE kind's action registers,
  //    so Alt+V / Alt+A / Alt+S never collide across the Validation / Approval /
  //    Delivery screens.  Registered unconditionally (empty on non-workflow kinds).
  //    MUST be above the `multiPopupOpen` early return below (rules of hooks).
  const wfKeys: HotkeyDef[] = [];
  if (kind === 'validation' && perm.write) {
    wfKeys.push(signCfg.enabled
      ? { combo: 'alt+v', label: 'Validate & Sign', group: title, order: 1, run: () => openSign('validation'),
          disabled: !hasEmployeeLink || selectedJobOids.size !== 1 }
      : { combo: 'alt+v', label: 'Validate', group: title, order: 1, run: () => { void validateSelected(Array.from(selectedJobOids)); },
          disabled: !hasEmployeeLink || selectedJobOids.size === 0 || validating });
  }
  if (kind === 'approval' && perm.write) {
    wfKeys.push(signCfg.enabled
      ? { combo: 'alt+a', label: 'Approve & Sign', group: title, order: 1, run: () => openSign('approval'),
          disabled: !hasEmployeeLink || selectedJobOids.size !== 1 }
      : { combo: 'alt+a', label: 'Approve', group: title, order: 1, run: () => { void approveSelected(Array.from(selectedJobOids)); },
          disabled: !hasEmployeeLink || selectedJobOids.size === 0 || approving });
  }
  if (kind === 'delivery' && perm.write) {
    // Deliver / Send — Alt+Y is the documented workflow key (Keyboard Shortcuts
    // Manual v3 §4: "deliverY"); mirrors the Send button.
    wfKeys.push({ combo: 'alt+y', label: 'Deliver / Send', group: title, order: 1, run: () => { void deliverSelected(); },
      disabled: !hasEmployeeLink || selectedJobOids.size === 0 || delivering });
  }
  // Edit Report (Tracking) — Alt+E; mirrors the tracking-list Edit button.
  if (kind === 'tracking' && perm.write) {
    wfKeys.push({ combo: 'alt+e', label: 'Edit Report', group: title, order: 1, run: () => setEditOpen(true),
      disabled: selectedJobOids.size !== 1 || editBusy });
  }
  // Preview (Validation / Approval / Delivery) — Alt+P; opens the ticked reports'
  // stored PDF(s). These custom left-slot buttons aren't AppToolbar onPreview, so
  // they need explicit registration.
  if (kind === 'validation' || kind === 'approval' || kind === 'delivery') {
    wfKeys.push({ combo: 'alt+p', label: 'Preview', group: title, order: 2, run: () => { void previewSelected(); },
      disabled: selectedJobOids.size === 0 || pdfPreviewLoading });
  }
  // Delete (Validation / Approval / Delivery / Tracking) — Alt+D; delete-gated,
  // blocked while a Sent report is selected (matches the button).
  if ((kind === 'validation' || kind === 'approval' || kind === 'delivery' || kind === 'tracking') && perm.delete) {
    wfKeys.push({ combo: 'alt+d', label: 'Delete', group: title, order: 3, run: () => { void deleteSelected(Array.from(selectedJobOids)); },
      disabled: selectedJobOids.size === 0 || deleting || anySelectedSent });
  }
  // Load (Custom Reporting / EDD) — Alt+L; mirrors the title-row Load button.
  if (kind === 'custom') {
    wfKeys.push({ combo: 'alt+l', label: 'Load', group: title, order: 1, run: () => setMultiPopupOpen(true),
      disabled: selectedJobOids.size === 0 });
  }
  if (kind === 'edd') {
    wfKeys.push({ combo: 'alt+l', label: 'Load', group: title, order: 1,
      run: () => { const o = Array.from(selectedJobOids)[0]; if (o) void openEdd(o); },
      disabled: selectedJobOids.size !== 1 });
  }
  useHotkeys(wfKeys);

  // ── Multi-job retrieve "view mode" ── (below useHotkeys so ALL hooks run
  // before this early return — rules of hooks).
  // When the user clicks Retrieve in Custom Reporting with N JobIDs ticked,
  // we swap the entire page body for a full-page combined-rows view
  // (NOT a popup — per 2026-06-07 feedback the popup was too constrained).
  // The view mirrors the per-job Detail page layout: breadcrumb + toolbar +
  // grid with JobID/SampleID/Matrix/Test/Method/Parameter/Client/Status.
  // Clicking Back returns to the list with the same selection intact.
  if (multiPopupOpen) {
    // IMPORTANT: build a stable list of {jobOid, jobId}.  An inline
    // .filter().map() recreates the array each render and would re-
    // trigger the child's fetch useEffect on every parent state change.
    // Sorting by jobOid gives a stable key for the child's useEffect
    // dep so the fetch only runs once per unique selection.
    const selectedJobs = (rows as ReportingListRow[])
      .filter(r => selectedJobOids.has(r.id))
      .map(r => ({ jobOid: r.id, jobId: r.jobId ?? '(?)' }))
      .sort((a, b) => a.jobOid.localeCompare(b.jobOid));
    return (
      <MultiJobRetrieveView
        jobs={selectedJobs}
        jobIdFilter={jobIdFilter}
        onBack={() => setMultiPopupOpen(false)}
        // A saved report moves its tests past Pending Reporting, so the list must be
        // re-read on return — otherwise it keeps showing the job and its old status.
        onSaved={() => { setMultiPopupOpen(false); setSelectedJobOids(new Set()); void load(); }}
        onCustomJobDrill={onCustomJobDrill}
      />
    );
  }

  // ── Guidance hint strip (shared <StepHint>, BO UI Standard §5) — the
  // contextual "what to do next" for each workflow screen.  Presentational only.
  const hint: { tone: StepHintTone; node: ReactNode } | null = (() => {
    switch (kind) {
      case 'custom':
        return { tone: 'guidance', node: <><b>Step 1 · Select Job(s).</b> Tick one or more Job IDs — same client only — then Load to retrieve their sample/test rows.</> };
      case 'validation':
        return { tone: 'guidance', node: <><b>Step 1 · Review, then select.</b> Click a <b>ReportID</b> to open and review it, tick the reviewed rows, then click <b>Validate</b> to move them to Report Approval.</> };
      case 'approval':
        return { tone: 'guidance', node: <><b>Step 1 · Review, then select.</b> Click a <b>ReportID</b> to open and review it, tick the reviewed rows, then click <b>Approve</b> to move them to Report Delivery. <i>(Optional stage — some tenants configure validated reports to skip straight to Delivery.)</i></> };
      case 'delivery':
        return { tone: 'guidance', node: <><b>Step 1 · Review, then select.</b> Click a <b>ReportID</b> to open and review it — and confirm each has a valid email on file — tick the reviewed rows, then click <b>Send</b>.</> };
      case 'tracking':
        return { tone: 'guidance', node: <><b>Tracking.</b> Every report follows Pending Validation → Pending Approval → Pending Delivery → Sent. Tick a row and click <b>Edit Report</b> to open it.</> };
      case 'edd':
        return { tone: 'guidance', node: <><b>Step 1 · Set your query.</b> Click <b>New</b> — <b>EDD Template</b> is required — narrow further by <b>Job ID · Client · Project · Category · Test · Method</b> or a date range, then <b>Load</b>.</> };
      default:
        return null;   // mor / msr — not part of the prototype's workflow
    }
  })();

  // ── Per-form workflow stepper (shared <StepIsland>) — each form computes its
  // OWN step states from LIVE selection/data, exactly like the prototype's
  // renderStepper(). `null` = "no single current step to show" → hide the island.
  const stepperSteps: IslandStep[] | null = (() => {
    const n = selectedJobOids.size;
    const selDone = (): IslandStep['state'] => (n > 0 ? 'done' : 'active');
    const actAfter = (): IslandStep['state'] => (n > 0 ? 'active' : 'todo');
    switch (kind) {
      case 'custom':
        // List-only screen: step 1 is the live one; the rest await the detail view.
        return [
          { id: 'select', label: 'Select Job',                   state: selDone() },
          { id: 'mark',   label: 'Mark Samples & Choose Report', state: actAfter() },
          { id: 'preview', label: 'Preview',                      state: 'todo' },
          { id: 'save',    label: 'Save',                         state: 'todo' },
        ];
      case 'validation':
        return [{ id: 'select', label: 'Select Reports', state: selDone() }, { id: 'validate', label: 'Validate', state: actAfter() }];
      case 'approval':
        return [{ id: 'select', label: 'Select Reports', state: selDone() }, { id: 'approve', label: 'Approve', state: actAfter() }];
      case 'delivery': {
        const selIds = Array.from(selectedJobOids);
        const anySel = selIds.length > 0;
        const allReviewed = anySel && selIds.every(id => reviewedOids.has(id));
        return [
          { id: 'select', label: 'Select Reports', state: selDone() },
          { id: 'review', label: 'Review',         state: !anySel ? 'todo' : (allReviewed ? 'done' : 'active') },
          { id: 'send',   label: 'Send',           state: !anySel ? 'todo' : (allReviewed ? 'active' : 'todo') },
        ];
      }
      case 'tracking': {
        // Pipeline mode — the ONE ticked report's own progress. Hidden otherwise.
        if (n !== 1) return null;
        const id = Array.from(selectedJobOids)[0];
        const row = (rows as ReportingListRow[]).find(r => r.id === id);
        if (!row) return null;
        const validated = !!row.validatedDate;
        const approved  = !!row.approvedDate;
        const delivered = row.stageText === 'Sent' || !!row.deliveredDate;
        return [
          { id: 'reported',  label: 'Reported',  state: 'done' },
          { id: 'validated', label: 'Validated', state: validated ? 'done' : 'active' },
          { id: 'approved',  label: 'Approved',  state: !validated ? 'todo' : (approved ? 'done' : 'active') },
          { id: 'delivered', label: 'Delivered', state: !approved ? 'todo' : (delivered ? 'done' : 'active') },
        ];
      }
      case 'edd':
        // List view = step 1 (Query) is live; Load → Select Format → Save happen
        // on the Spreadsheet View after clicking New.
        return [
          { id: 'query',  label: 'Query',         state: 'active' },
          { id: 'load',   label: 'Load',          state: 'todo' },
          { id: 'format', label: 'Select Format', state: 'todo' },
          { id: 'save',   label: 'Save',          state: 'todo' },
        ];
      default:
        return null;
    }
  })();

  // Edit Report — full-page replacement (ALPACA's Frame.SetView pattern).
  // When one report is ticked and "Edit Report" is clicked, the entire list
  // view is replaced by the edit form (same logic, proper page layout).
  if (editOpen && editOid && kind === 'tracking') {
    return (
      <EditReportPage
        reportingOid={editOid}
        busy={editBusy}
        onCancel={() => setEditOpen(false)}
        onSubmit={submitRevise}
        onUpdated={() => { setEditOpen(false); void load(); }}
      />
    );
  }

  // Read-only samples detail — Report Validation / Report Delivery row click
  // (user request 2026-07-25): show the report name + the job's samples (list
  // view), no save / preview.  Full-page replacement, same pattern as Edit Report.
  if (viewOid && (kind === 'validation' || kind === 'delivery')) {
    return (
      <ReportSamplesViewPage
        reportingOid={viewOid}
        backLabel={KIND_AUDIT_FORM[kind] ?? 'Reporting'}
        onClose={() => setViewOid(null)}
      />
    );
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Reporting</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{title}</span>
          </div>
          {/* Shared FormTitle — the ONE page heading (§18.6) ON the grey page.
              Custom Reporting's Load action sits at the top-right of this row
              (prototype parity) — advances the ticked job(s) to the combined
              detail view; enabled once ≥1 JobID is ticked. */}
          <div className="dp-title-row">
            <FormTitle pageId={KIND_NAV_ID[kind]} title={title} loadMs={loadMs} loading={loading}
                       auditFormName={KIND_AUDIT_FORM[kind]} />
            {/* Primary workflow action at the TOP-RIGHT (prototype parity, user
                request 2026-07-04): Load (Custom) · Validate (Validation) ·
                Approve (Approval).  No Rollback. */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {kind === 'custom' && (
                <button
                  className="ca-btn ca-btn-solid"
                  onClick={() => setMultiPopupOpen(true)}
                  disabled={selectedJobOids.size === 0}
                  title={(selectedJobOids.size === 0
                    ? 'Tick at least one JobID to enable Load.'
                    : `Load ${selectedJobOids.size} selected JobID(s).`) + ` (${formatCombo('alt+l')})`}>
                  <span aria-hidden="true">⬇️</span>
                  &nbsp;Load{selectedJobOids.size > 0 ? ` (${selectedJobOids.size})` : ''}
                </button>
              )}
              {/* EDD Report Generator — Load at the TOP-RIGHT, same placement and
                  style as Custom Reporting's (user request 2026-07-15).  This is
                  now the ONLY way to open a saved EDD: the toolbar Edit button and
                  the row double-click were removed, so a saved EDD's sheets load
                  ONLY on an explicit Load. */}
              {kind === 'edd' && (
                <button
                  className="ca-btn ca-btn-solid"
                  onClick={() => { const o = Array.from(selectedJobOids)[0]; if (o) void openEdd(o); }}
                  disabled={selectedJobOids.size !== 1}
                  title={selectedJobOids.size !== 1
                    ? 'Tick exactly one EDD report to enable Load.'
                    : 'Load the ticked EDD report’s saved sheets.'}>
                  <span aria-hidden="true">⬇️</span>
                  &nbsp;Load
                </button>
              )}
              {kind === 'validation' && signCfg.enabled && perm.write && (
                <button className="ca-btn ca-btn-solid"
                        onClick={() => openSign('validation')}
                        disabled={!hasEmployeeLink || selectedJobOids.size !== 1}
                        title={(!hasEmployeeLink ? noEmpLinkTip
                          : selectedJobOids.size !== 1 ? 'Tick exactly one report to sign.'
                          : 'Validate & electronically sign this report.') + ` (${formatCombo('alt+v')})`}>
                  <span aria-hidden="true">✓</span>&nbsp;Validate &amp; Sign
                </button>
              )}
              {kind === 'validation' && !signCfg.enabled && perm.write && (
                <button className="ca-btn ca-btn-solid"
                        onClick={() => validateSelected(Array.from(selectedJobOids))}
                        disabled={!hasEmployeeLink || selectedJobOids.size === 0 || validating}
                        title={(!hasEmployeeLink ? noEmpLinkTip
                          : selectedJobOids.size === 0 ? 'Tick at least one report to enable Validate.'
                          : `Validate ${selectedJobOids.size} selected report(s).`) + ` (${formatCombo('alt+v')})`}>
                  {validating ? <BusyIcon busy icon="ti-circle-check" /> : <span aria-hidden="true">✓</span>}
                  &nbsp;{validating ? 'Validating…' : `Validate${selectedJobOids.size > 0 ? ` (${selectedJobOids.size})` : ''}`}
                </button>
              )}
              {kind === 'approval' && signCfg.enabled && perm.write && (
                <button className="ca-btn ca-btn-solid"
                        onClick={() => openSign('approval')}
                        disabled={!hasEmployeeLink || selectedJobOids.size !== 1}
                        title={(!hasEmployeeLink ? noEmpLinkTip
                          : selectedJobOids.size !== 1 ? 'Tick exactly one report to sign.'
                          : 'Approve & electronically sign this report.') + ` (${formatCombo('alt+a')})`}>
                  <span aria-hidden="true">✓</span>&nbsp;Approve &amp; Sign
                </button>
              )}
              {kind === 'approval' && !signCfg.enabled && perm.write && (
                <button className="ca-btn ca-btn-solid"
                        onClick={() => approveSelected(Array.from(selectedJobOids))}
                        disabled={!hasEmployeeLink || selectedJobOids.size === 0 || approving}
                        title={(!hasEmployeeLink ? noEmpLinkTip
                          : selectedJobOids.size === 0 ? 'Tick at least one report to enable Approve.'
                          : `Approve ${selectedJobOids.size} selected report(s).`) + ` (${formatCombo('alt+a')})`}>
                  {approving ? <BusyIcon busy icon="ti-stamp" /> : <span aria-hidden="true">✓</span>}
                  &nbsp;{approving ? 'Approving…' : `Approve${selectedJobOids.size > 0 ? ` (${selectedJobOids.size})` : ''}`}
                </button>
              )}
              {/* Report Delivery — "Send" (ALPACA's Send action) at the TOP-RIGHT
                  with the 📤 emoji (user request 2026-07-04).  Write-gated. */}
              {kind === 'delivery' && perm.write && (
                <button className="ca-btn ca-btn-solid"
                        onClick={() => deliverSelected()}
                        disabled={!hasEmployeeLink || selectedJobOids.size === 0 || delivering}
                        title={(!hasEmployeeLink ? noEmpLinkTip
                          : selectedJobOids.size === 0 ? 'Tick at least one report to enable Send.'
                          : `Send ${selectedJobOids.size} selected report(s).`) + ` (${formatCombo('alt+s')})`}>
                  {delivering ? <BusyIcon busy icon="ti-send" /> : <span aria-hidden="true">📤</span>}
                  &nbsp;{delivering ? 'Sending…' : `Send${selectedJobOids.size > 0 ? ` (${selectedJobOids.size})` : ''}`}
                </button>
              )}
              {/* Report Tracking "Edit Report" moved into the toolbar (before
                  Delete) 2026-07-09 (user request) — see the toolbar block below. */}
            </div>
          </div>
          <div className="dp-subtitle mlv-subcap">
            Read-only · {loading ? '…' : rows.length} items{subtitle ? ' · ' : ''}{subtitle}
          </div>
          {/* Per-form workflow stepper — docked in the top bar (BO UI Standard
              §5) via <TopbarStepper>. Computed live from the current selection;
              hides itself when there's no step to show. */}
          <TopbarStepper steps={stepperSteps} />
        </div>
      </div>

      {/* Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
          only the grid card below is white. */}
      <div className="bl-grid-area">
          {/* Contextual guidance strip — what to do next on this screen. */}
          {hint && <StepHint tone={hint.tone}>{hint.node}</StepHint>}
          {/* Toolbar on its OWN full-width row (BO UI Standard §6). */}
          <div style={{ margin: '0 0 8px' }}>
            {/* Shared toolbar — the per-view filters (JobID Filter / Date
                Range), the dead-coded Retrieve, the per-kind action buttons
                (Validate · Approve / Approve All · Deliver) and
                the Export dropdown all live in `left` in their existing order;
                the reload is routed to the standard AppToolbar Refresh button.
                The Export menu keeps its own dropdown (Export As / CSV (N rows))
                so that behaviour is preserved unchanged. */}
            <AppToolbar
              onRefresh={load}
              refreshing={loading}
              left={
                <>
                  {/* Date Range filter FIRST (user request 2026-07-08) — the
                      form-specific filter leads the toolbar, before New/Edit/Delete. */}
                  {/* §17 Tracking-form date filter — shared <DateRangeSelect> chip
                      row (1M · 3M · 6M · 1Y · All, 3M default, active = orange). */}
                  <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                    value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
                    onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
                  {/* EDD Report Generator — "New" opens the EDD Report Query popup
                      (ALPACA parity); OK generates + opens the Spreadsheet View.
                      (Merged 2026-07-07.) */}
                  {kind === 'edd' && (
                    <>
                      {perm.create && (
                        <button className="ca-btn" onClick={() => setEddQueryOpen(true)}>
                          <span className="tb-i tb-i-green">＋</span> New
                        </button>
                      )}
                      {/* Edit REMOVED 2026-07-15 (user request) — opening a saved
                          EDD is now the top-right Load button only. */}
                      {perm.delete && (
                        <button className="ca-btn"
                                onClick={() => deleteEddSelected(Array.from(selectedJobOids))}
                                disabled={selectedJobOids.size === 0 || deleting}
                                title={selectedJobOids.size === 0
                                  ? 'Tick at least one report to delete.'
                                  : `Delete ${selectedJobOids.size} selected report(s).`}>
                          <BusyIcon busy={deleting} icon="ti-trash" />
                          &nbsp;{deleting ? 'Deleting…' : `Delete${selectedJobOids.size > 0 ? ` (${selectedJobOids.size})` : ''}`}
                        </button>
                      )}
                    </>
                  )}
                  {/* Custom Reporting only: GoLive JobID Filter dropdown */}
                  {kind === 'custom' && (
                    <div className="sa-date-wrap" style={{ marginRight: 4 }}>
                      <span className="sa-date-prefix" aria-hidden="true">🔎</span>
                      <span className="sa-date-label">JobID Filter</span>
                      <select className="sa-date-sel sa-date-sel-plain" value={jobIdFilter}
                        onChange={e => setJobIdFilter(e.target.value as 'All' | 'Pending')}>
                        <option value="All">All JobID</option>
                        <option value="Pending">Pending JobID</option>
                      </select>
                    </div>
                  )}
                  {/* Custom Reporting only — Load Selected (open the ticked jobs).
                      Visible when one or more jobs are ticked.  Opens a popup that
                      fetches every selected job's detail rows in parallel and shows
                      the combined grid (multi-job load flow).  Label is "Load"
                      (open-selected), never "Retrieve" (§18.6). */}
                  {/* Load moved to the title row (top-right, prototype parity,
                      2026-07-04) — the toolbar Load was removed to avoid a
                      duplicate; the advance action lives ONLY on the title row. */}
                  {/* Validate (Validation) moved to the TOP-RIGHT title row
                      2026-07-04 (user request) — no longer in the toolbar. */}
                  {/* Edit Report — Report Tracking (ALPACA's EditReport_Execute).
                      Enabled when exactly ONE report is ticked.  Opens the Edit
                      Report dialog → revise: mints <base>R<n>, supersedes the old
                      report, audits "Report Tracking" ValueChanged.  Allowed at any
                      status (a delivered report can be re-issued). */}
                  {/* Edit Report — Report Tracking (ALPACA's EditReport_Execute).
                      In the toolbar RIGHT BEFORE Delete (user request 2026-07-09).
                      Enabled when exactly ONE report is ticked; opens the Edit
                      Report dialog → revise (mints <base>R<n>, supersedes the old
                      report, audits "Report Tracking" ValueChanged). */}
                  {kind === 'tracking' && perm.write && (
                    <button className="ca-btn"
                            onClick={() => setEditOpen(true)}
                            disabled={selectedJobOids.size !== 1 || editBusy}
                            title={(selectedJobOids.size !== 1
                              ? 'Tick exactly one report to edit (revise) it.'
                              : 'Edit (revise) the selected report — creates a new revision.') + ` (${formatCombo('alt+e')})`}>
                      {editBusy ? <BusyIcon busy icon="ti-edit" />
                                : <span className="tb-i tb-i-blue" aria-hidden="true">✎</span>}
                      &nbsp;Edit Report
                    </button>
                  )}
                  {/* Delete — Validation / Delivery / Tracking.  Soft-deletes the
                      ticked report(s) + reverts their samples to Pending Reporting
                      (ALPACA ReportDelete_Execute); a Sent/Delivered report is blocked
                      server-side (409) and surfaced via the notifier. */}
                  {(kind === 'validation' || kind === 'approval' || kind === 'delivery' || kind === 'tracking') && perm.delete && (
                    <button className="ca-btn"
                            onClick={() => deleteSelected(Array.from(selectedJobOids))}
                            disabled={selectedJobOids.size === 0 || deleting || anySelectedSent}
                            title={(anySelectedSent
                              ? 'Cannot delete a Sent report — rollback delivery first.'
                              : selectedJobOids.size === 0
                                ? 'Tick at least one report to delete.'
                                : `Delete ${selectedJobOids.size} selected report(s).`) + ` (${formatCombo('alt+d')})`}>
                      <BusyIcon busy={deleting} icon="ti-trash" />
                      &nbsp;{deleting ? 'Deleting…' : `Delete${selectedJobOids.size > 0 ? ` (${selectedJobOids.size})` : ''}`}
                    </button>
                  )}
                  {/* Approve (Approval) moved to the TOP-RIGHT title row
                      2026-07-04 (user request) — no longer in the toolbar.
                      "Approve All" removed earlier the same day. */}
                  {/* Preview — Validation / Approval / Delivery.  Neutral .ca-btn
                      with a 📄 emoji icon (user request 2026-07-04 — like the
                      History/Refresh/Export toolbar buttons).  Fetches the ticked
                      report(s)' stored PDF and opens each in a new tab. */}
                  {(kind === 'delivery' || kind === 'validation' || kind === 'approval') && (
                    <button className="ca-btn"
                            onClick={() => { void previewSelected(); }}
                            disabled={selectedJobOids.size === 0 || pdfPreviewLoading}
                            title={(selectedJobOids.size === 0
                              ? 'Tick one or more reports to preview.'
                              : selectedJobOids.size === 1
                              ? `Preview ${Array.from(selectedJobOids)[0]} — opens in a new tab.`
                              : `Preview ${selectedJobOids.size} selected reports — each opens in a new tab.`) + ` (${formatCombo('alt+p')})`}>
                      {pdfPreviewLoading
                        ? <BusyIcon busy icon="ti-eye" />
                        : <span aria-hidden="true">📄</span>}
                      &nbsp;{pdfPreviewLoading ? 'Loading…' : 'Preview'}
                    </button>
                  )}
                  {/* Project Notes — §8 component on the LIST screens (prototype
                      list toolbars, added 2026-07-06).  Opens the ticked report's
                      Job notes; exactly ONE row must be ticked (prototype
                      jobIdForNotes).
                      EDD REMOVED 2026-07-15 (owner request): the EDD Report
                      Generator toolbar no longer shows Project Notes.  NOTE this
                      is a deliberate DEVIATION from BO UI Standard §8, which names
                      EDD Report Generator explicitly — kept here for Report
                      Approval + Report Tracking, which are unchanged. */}
                  {(kind === 'approval' || kind === 'tracking') && (
                    <button className="ca-btn"
                            onClick={() => {
                              if (selectedJobOids.size !== 1) {
                                notify.warning('Tick exactly one report to open its Project Notes.');
                                return;
                              }
                              const oid = Array.from(selectedJobOids)[0];
                              const row = (rows as ReportingListRow[]).find(r => r.id === oid);
                              if (!row?.jobOid) {
                                notify.warning('This report has no linked Job — no Project Notes to show.');
                                return;
                              }
                              setListNotesJob({ jobOid: row.jobOid, jobId: row.jobId ?? row.reportId ?? '(?)' });
                            }}
                            title={selectedJobOids.size === 1
                              ? 'Open Project Notes for the ticked report’s Job.'
                              : 'Tick exactly one report to open its Project Notes.'}>
                      <span className="tb-i" style={{ color: 'var(--ai-icon-notes, #c8841a)' }} aria-hidden="true">📝</span>
                      &nbsp;Project Notes
                    </button>
                  )}
                  {/* Report Delivery "Send" moved to the TOP-RIGHT title row
                      2026-07-04 (user request) — no longer in the toolbar. */}
                </>
              }
              exportData={{ filename: kind, ...exportMatrix }}
            />
          </div>

          {/* Employee-link restriction — a calm, INFORMATIONAL notice (never a
              red data-load error).  The reports stay VISIBLE and read-only; only
              the Validate/Approve/Deliver action is gated (server-enforced).
              Shown when the signed-in account isn't linked to an Employee record. */}
          {!hasEmployeeLink && (kind === 'validation' || kind === 'approval' || kind === 'delivery') && (
            <div style={{
              background: 'var(--ai-pill-info-bg)', border: '1px solid var(--ai-line)',
              color: 'var(--ai-pill-info-fg)', padding: '8px 14px', borderRadius: 4,
              fontSize: 12, margin: '0 14px 10px 14px',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <i className="ti ti-info-circle" style={{ fontSize: 15 }} />
              <span style={{ flex: 1 }}>
                {`Your account isn't linked to an Employee record, so ${
                  kind === 'validation' ? 'validation' : kind === 'approval' ? 'approval' : 'delivery'
                } is unavailable. The reports below are view-only.`}
              </span>
            </div>
          )}

          {/* (Report Delivery has no dialog — ALPACA's "Send" delivers directly.)
              Validate/Approve/Deliver successes surface via the shared
              full-width notify banner (BO UI Standard §13) — the old inline
              green pill here was removed 2026-07-06. */}

          {/* Project Notes — list-level popup (BO UI Standard §8; prototype
              list toolbars 2026-07-06).  Same shared grid popup the Custom
              Reporting detail uses, anchored to the ONE ticked report's Job. */}
          {listNotesJob && (
            <MultiJobActionPopup
              title={`Project Notes — JobID ${listNotesJob.jobId}`}
              jobs={[listNotesJob]}
              onClose={() => setListNotesJob(null)}
              endpointTemplate="/api/v1/reporting/custom/{jobOid}/project-notes"
              editRowEndpointBase="/api/v1/reporting/custom/notes"
              editFormName="Project Notes"
              canAdd
              addEndpointTemplate="/api/v1/reporting/custom/{jobOid}/project-notes"
              columns={[
                { key: 'title',       label: 'Title',          width: 160 },
                { key: 'nameSource',  label: 'BusinessObject', width: 130 },
                { key: 'sourceId',    label: 'SourceID',       width: 110 },
                { key: 'plainText',   label: 'Note',           width: 380,
                  fmt: (_, r) => String((r as Record<string, unknown>).plainText ?? (r as Record<string, unknown>).text ?? '') },
                { key: 'createdBy',   label: 'Author',         width:  90 },
                { key: 'createdDate', label: 'Date',           width: 140,
                  fmt: v => (v ? (fmtLocal(String(v)) || String(v)) : '') },
              ]}
            />
          )}

          {/* Edit Report — handled by early-return full-page mode above. */}

          {/* Electronic signature (ADR-0009) — Validate & Sign / Approve & Sign. */}
          {signTarget && (
            <SignReportDialog
              stage={signTarget.stage}
              reportingOid={signTarget.oid}
              reportId={signTarget.reportId}
              secondFactor={signCfg.secondFactor}
              onClose={() => setSignTarget(null)}
              onSigned={(meaning) => {
                setSignTarget(null);
                notify.success(`Report ${signTarget.reportId} signed (${meaning}).`);
                setSelectedJobOids(new Set());
                void load();
              }}
            />
          )}

          {/* Same-client selection guard now surfaces via the shared notifier
              (useNotify, §18.6) — the old inline banner was removed 2026-07-04. */}

          {/* The shared <GridSearch> component (components/GridSearch.tsx) — same
              search bar as Version Log and every other list form. The Custom
              Reporting advance action ("Load") now lives at the top-right of the
              title row (prototype parity) — the old search-row "Next" button was
              removed 2026-07-04 per user request. */}
          <GridSearch
            value={search}
            onChange={setSearch}
            right={gridRows.length !== rows.length ? (
              <span className="search-match-count">
                {gridRows.length} match{gridRows.length !== 1 ? 'es' : ''}
              </span>
            ) : undefined}
          />

          {/* ONE white grid card — filter-chip strip + grid + its single footer. */}
          <div className="bl-card bl-card-flex">

          {/* Stage filter chips — auto-derived from the data (Pending Validation, Pending Approval, …) */}
          {stageChips.length > 0 && (
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Stage:</span>
              <button
                className={`sa-filter-btn${statusChip === 'all' ? ' active' : ''}`}
                onClick={() => setStatusChip('all')}
              >All ({searchFiltered.length})</button>
              {stageChips.map(([label, n]) => (
                <button key={label}
                  className={`sa-filter-btn${statusChip === label ? ' active' : ''}`}
                  onClick={() => setStatusChip(label)}
                >{label} ({n})</button>
              ))}
            </div>
          )}

          {error && !loading ? (
            <div className="bl-empty" style={{ color: 'var(--ai-red)', flexDirection: 'column', gap: 6 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: .35 }} />
              <div>{error}</div>
            </div>
          ) : (
            /* Shared data grid — styling, per-column filters, click-to-sort,
               footer / pagination, load-time and (for actionable kinds) the
               selection checkboxes are all standardised in <AppDataGrid>. */
            <AppDataGrid<Record<string, unknown>>
              rows={gridRows as Record<string, unknown>[]}
              columns={gridCols}
              getRowId={(r) => (r as { id: string }).id}
              loading={loading}
              loadMs={loadMs}
              emptyText="No rows match the current filters."
              selectable={isSelectableKind}
              selectedIds={selectedJobOids as Set<string | number>}
              onSelectionChange={onGridSelectionChange}
              /* Row click (Report Validation / Report Delivery) opens a READ-ONLY
                 samples detail — the report name + the job's samples (SampleID/
                 Matrix/Test/Parameter/Method), reusing the Edit Report /edit-context
                 feed. No save / preview (user request 2026-07-25). The checkbox
                 column, the ReportID/EDDID link (opens the PDF), and Delivery's
                 editable cells (Email combo, Mail/DoNotDeliver) all stopPropagation,
                 so only a plain-cell click opens the detail. */
              onRowClick={(kind === 'validation' || kind === 'delivery')
                ? (r) => { const id = (r as { id?: string }).id; if (id) setViewOid(id); }
                : undefined}
              /* Row double-click NO LONGER opens a saved EDD (user request
                 2026-07-15) — the sheets load ONLY via the top-right Load button,
                 so an accidental double-click can't kick off a heavy load. */
            />
          )}

          {/* Step 0 (Select jobs) has NO bottom stepper — the "Next" action
              lives only at the TOP (next to the search row) now; the old bottom
              Back/Next bar was removed (user request 2026-07-03: "remove next in
              bottom"). Back was disabled here anyway (first step). Step 1's
              footer keeps its active Back (below). */}
        </div>
      </div>

      {/* Multi-job Retrieve view renders as a full-page replacement at
          the top of this component's return (see early-return above). */}

      {/* ── Delivery Email checked-combo dropdown ────────────────────────────────
          Rendered with position:fixed so it escapes the grid's overflow.
          Opens when the user clicks an Email cell in the Pending Delivery list. */}
      {kind === 'delivery' && emailComboId && emailComboPos && (() => {
        const activeRow  = (rows as ReportingListRow[]).find(r => r.id === emailComboId);
        const isCc       = emailComboCol === 'CC';
        const isSent     = activeRow?.stageText === 'Sent';
        const edit       = deliveryEdits.get(emailComboId);
        const effective  = isCc
          ? (edit !== undefined ? edit.emailCC : (activeRow?.emailCC ?? null))
          : (edit !== undefined ? edit.email   : (activeRow?.email   ?? null));
        const selected   = new Set(
          (effective ?? '').split(/[;\n,]+/).map(e => e.trim()).filter(Boolean)
        );
        // Option list = the job's priority contacts PLUS any saved/selected recipient
        // not already among them, so the saved EmailTo/EmailCC always appears (checked)
        // in the dropdown — for EVERY report, incl. a delivered one whose client has no
        // matching contact (user request 2026-07-30).
        const baseContacts = activeRow?.jobOid
          ? ((isCc ? deliveryCcContacts : deliveryContacts).get(activeRow.jobOid) ?? [])
          : [];
        const contacts   = [
          ...baseContacts,
          ...Array.from(selected)
            .filter(e => !baseContacts.some(c => c.email.toLowerCase() === e.toLowerCase()))
            .map(e => ({ email: e, contactName: null as string | null })),
        ];
        if (contacts.length === 0) return null;
        const toggleEmail = (email: string) => {
          if (isSent) return;
          const rid = emailComboId;
          setDeliveryEdits(prev => {
            const m  = new Map(prev);
            const ex = m.get(rid) ?? { email: activeRow?.email ?? null, emailCC: activeRow?.emailCC ?? null, mail: activeRow?.mail ?? false, doNotDeliver: activeRow?.doNotDeliver ?? false, ccPicked: false };
            const next = new Set(selected);
            if (next.has(email)) next.delete(email); else next.add(email);
            const joined = Array.from(next).join(';') || null;
            // Picking in the CC combo marks the CC as user-chosen, which is what makes it
            // eligible to actually be sent (see deliverSelected).
            m.set(rid, isCc ? { ...ex, emailCC: joined, ccPicked: true } : { ...ex, email: joined });
            return m;
          });
        };
        return (
          <div
            data-dlv-combo
            style={{
              position: 'fixed', top: emailComboPos.top, left: emailComboPos.left,
              minWidth: emailComboPos.width, maxWidth: 420, maxHeight: 280,
              background: '#fff', border: '1px solid var(--ai-muted)', borderRadius: 4,
              boxShadow: '0 4px 14px rgba(0,0,0,.18)', zIndex: 9999,
              overflowY: 'auto', padding: '4px 0',
            }}
          >
            {isSent && (
              <div style={{
                padding: '4px 12px 6px', fontSize: 11, color: 'var(--ai-muted)',
                borderBottom: '1px solid var(--ai-line-soft)', fontStyle: 'italic',
              }}>
                Read-only — report already sent
              </div>
            )}
            {contacts.map(c => (
              <label
                key={c.email}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '6px 12px',
                  cursor: isSent ? 'default' : 'pointer',
                  userSelect: 'none',
                  background: selected.has(c.email) ? 'var(--ai-orange-tint)' : 'transparent',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.email)}
                  disabled={isSent}
                  onChange={() => toggleEmail(c.email)}
                  style={{
                    marginTop: 2, accentColor: 'var(--brand-accent, var(--ai-orange))', flexShrink: 0,
                    opacity: isSent ? 0.5 : 1,
                    cursor: isSent ? 'default' : 'pointer',
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  {c.contactName && c.contactName !== c.email && (
                    <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.contactName}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--ai-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.email}
                  </div>
                </div>
              </label>
            ))}
          </div>
        );
      })()}

      {/* EDD Report Generator — "New" → EDD Report Query popup; OK generates the
          sheets and navigates to the Spreadsheet View. (Merged 2026-07-07.) */}
      {kind === 'edd' && (
        <EddReportQueryModal open={eddQueryOpen} onClose={() => setEddQueryOpen(false)} />
      )}
    </div>
  );
}

// ── Custom Reporting flow footer ────────────────────────────────────────────
// Two-bar footer matching the Claude design (see screenshot 2026-06-13):
//   • a grey COUNT bar  — "{n} jobs · {m} selected"  +  page nav / page size
//   • a white NAV bar   — "‹ Back" on the left, "→ Next" (orange) on the right
// Shared by BOTH steps so the Back button is identical and always works:
//   Step 0 (Select-jobs list)  → Back disabled, Next enabled when ≥1 ticked.
//   Step 1 (combined Retrieve) → Back ACTIVE (returns to the list), no Next.
function CustomReportFlowFooter({
  slim,
  countLabel,
  pageSize, pageSizes, pageSizeFmt, onPageSize,
  safePage, totalPages, onPrevPage, onNextPage,
  backDisabled, onBack,
  showNext, nextDisabled, onNext, nextTitle,
}: {
  /** When true, the grey count/pagination bar is omitted — the page already has
   *  the standard <AppDataGrid> footer, so only the Back/Next stepper renders
   *  (no duplicate footer, CLAUDE.md §18.6 task 2). */
  slim?: boolean;
  countLabel: string;
  pageSize?: number;
  pageSizes?: readonly number[];
  pageSizeFmt?: (n: number) => string;
  onPageSize?: (n: number) => void;
  safePage?: number;
  totalPages?: number;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  backDisabled: boolean;
  onBack?: () => void;
  showNext: boolean;
  nextDisabled?: boolean;
  onNext?: () => void;
  nextTitle?: string;
}) {
  const page    = safePage ?? 0;
  const pages   = totalPages ?? 1;
  const navPage = typeof totalPages === 'number' && pages > 1;
  const navSize = typeof pageSize === 'number' && !!pageSizes && !!onPageSize;
  const chip = (disabled: boolean): CSSProperties => ({
    background: '#fff', border: '1px solid var(--ai-line)', borderRadius: 4,
    padding: '4px 8px', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? .4 : 1, fontSize: 12, lineHeight: 1,
  });
  return (
    <div style={{ flex: 'none' }}>
      {/* Bar 1 — grey count bar (omitted in `slim` mode — the page already has
          the standard <AppDataGrid> footer: count · pagination · page-size). */}
      {!slim && (
      <div style={{
        padding: '8px 16px', background: 'var(--ai-bg)', borderTop: '1px solid var(--ai-line)',
        display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--ai-ink)',
      }}>
        <span>{countLabel}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {navPage && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={onPrevPage} disabled={page === 0} title="Previous page" style={chip(page === 0)}>
                <i className="ti ti-chevron-left" />
              </button>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{page + 1} / {pages}</span>
              <button onClick={onNextPage} disabled={page >= pages - 1} title="Next page" style={chip(page >= pages - 1)}>
                <i className="ti ti-chevron-right" />
              </button>
            </span>
          )}
          {navSize && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              Page size:
              <select value={pageSize} onChange={e => onPageSize!(Number(e.target.value))}
                style={{ padding: '3px 7px', fontSize: 12, border: '1px solid var(--ai-line)', borderRadius: 4 }}>
                {pageSizes!.map(n => <option key={n} value={n}>{pageSizeFmt ? pageSizeFmt(n) : n}</option>)}
              </select>
            </span>
          )}
        </span>
      </div>
      )}

      {/* Bar 2 — white nav bar (Back left · Next right).  Shared `.ca-btn`
          styling; Next is the workflow's solid primary. */}
      <div style={{
        padding: '12px 16px', background: '#fff', borderTop: '1px solid var(--ai-line)',
        display: 'flex', alignItems: 'center',
      }}>
        <button className="ca-btn" onClick={onBack} disabled={backDisabled}>
          <i className="ti ti-chevron-left" /> Back
        </button>
        {showNext && (
          <button className="ca-btn ca-btn-solid" onClick={onNext} disabled={nextDisabled} title={nextTitle}
            style={{ marginLeft: 'auto' }}>
            <i className="ti ti-arrow-right" /> Next
          </button>
        )}
      </div>
    </div>
  );
}

// (GenericReportingTable removed — the list views now render through the shared
//  <AppDataGrid> via gridCols.  The Multi-job Retrieve popup below keeps its own
//  table because it is a transient selection dialog, not a list view.)

// ─────────────────────────────────────────────────────────────────────
// Multi-job Retrieve popup — opens from the Custom Reporting list
// toolbar's "Retrieve" button.  Takes the user's ticked JobIDs, fetches
// every job's detail rows in parallel via the existing per-job endpoint,
// and shows the combined sample-parameter grid in one view.  Export
// CSV button included for "save what I retrieved" workflows.
// ─────────────────────────────────────────────────────────────────────
interface DetailRow {
  parameterOid: string; sampleId?: string | null; jobId?: string | null;
  /** SampleLogIn.Oid for this row's sample — returned by /parameters; used to
   *  scope the CCPP (Water Stability) preview to the ticked samples. */
  sampleOid?: string | null;
  jobOid?: string | null; notReport?: boolean;
  matrix?: string | null; test?: string | null; method?: string | null;
  parameter?: string | null; client?: string | null;
  status?: string | null; statusId?: string | null;
  statusValue?: number;
  /** TRUE when this row is already linked to a report (ALPACA's
   *  `sp.Reportings.Count() > 0`). Drives the revision-vs-normal save. */
  alreadyReported?: boolean;
  /** TRUE when the test is at Pending Reporting or a later reporting stage
   *  (SampleStatusNew) and not on hold — only such a row can be ticked, previewed
   *  and saved (owner 2026-10-29). Earlier stages are listed for context. */
  canReport?: boolean;
}

/** Sentinel Oid for the synthetic "CCPP Report" entry in the Report combo — it is
 *  NOT a dbo.ReportPackage row.  Selecting it and clicking Preview downloads the
 *  CCPP (Water Stability) Excel for the ticked samples, reusing the Ad Hoc engine
 *  server-side scoped by the loaded Job IDs (owner request 2026-10-10). */
const CCPP_PACKAGE_OID = 'ccc00000-0000-0000-0000-00000000ccc0';
const isCcppPackageOid = (oid: string | undefined) =>
  (oid ?? '').toLowerCase() === CCPP_PACKAGE_OID;

/** Sentinel Oid for the synthetic "LSI Report" entry in the Report combo — the
 *  SAME mechanism as the CCPP sentinel above, for the EWPP LSI report.  Selecting
 *  it and clicking Preview downloads the LSI Excel for the ticked samples, reusing
 *  the Ad Hoc engine server-side scoped by the loaded Job IDs (owner request
 *  2026-10-20: offer the LSI report in Custom Reporting, same as CCPP). */
const LSI_PACKAGE_OID = '15100000-0000-0000-0000-000000015100';
const isLsiPackageOid = (oid: string | undefined) =>
  (oid ?? '').toLowerCase() === LSI_PACKAGE_OID;
/** TRUE for either synthetic Excel-report sentinel (CCPP or LSI) — both Preview
 *  to a downloaded Excel instead of the sub-report PDF popup. */
const isExcelReportOid = (oid: string | undefined) =>
  isCcppPackageOid(oid) || isLsiPackageOid(oid);

/** Report Package dialog values that G5 stamps on the saved report. */
interface PackageReportInfo {
  reportedDate?: string;    // yyyy-MM-dd
  approvedByOid?: string;
}

function MultiJobRetrieveView({ jobs, jobIdFilter = 'Pending', onBack, onSaved, onCustomJobDrill }: {
  jobs: Array<{ jobOid: string; jobId: string }>;
  /** The list's "Pending JobID" / "All JobID" filter — G5 rgFilterByJobID decides which
   *  tests the drill-in shows and the save links (owner 2026-10-17, G5 parity). */
  jobIdFilter?: 'All' | 'Pending';
  onBack: () => void;
  /** Called after a report is saved: return to the list AND reload it. Falls back to onBack. */
  onSaved?: () => void;
  /** AppShell callback — opens the per-job CustomReportingDetailPage
   *  (full report-generation functionality).  When the user clicks a
   *  JobID cell in the multi-job grid OR the toolbar PDF/Save button,
   *  we route them into the detail page for THAT job so every toolbar
   *  action works the way it does from the list-page drill-down. */
  onCustomJobDrill?: (jobOid: string, jobLabel: string) => void;
}) {
  // Multi-job Custom Reporting view — Save Report mints a ReportID → Create-gated.
  const perm = useNavActions('custreport');
  const [rows,        setRows]        = useState<DetailRow[]>([]);
  // Snapshot of the rows as loaded — drives NotReport dirty-tracking so the
  // "Save changes" / "Cancel" toolbar buttons enable only on real edits.
  const [originalRows, setOriginalRows] = useState<DetailRow[]>([]);
  const [savingChanges, setSavingChanges] = useState(false);
  const [loading,     setLoading]     = useState(true);
  const [loadMs,      setLoadMs]      = useState<number | null>(null);
  const [err,         setErr]         = useState<string | null>(null);
  const [search,      setSearch]      = useState('');
  // Pre-tick every retrieved row by default — same UX as the per-job
  // Custom Reporting detail screen (user feedback 2026-06-08).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Per-column Excel-style filters + click-to-sort header — shared
  // components used by every other list page in the app.
  const [colF, setColF] = useState<Record<string, ColumnFilterValue>>({});
  // The filter bar's tick box (XAF filter panel): off = the column filters are kept
  // but not applied. Editing a filter turns it back on.
  const [filterOn, setFilterOn] = useState(true);
  const sortApi = useSort('jobId');
  // Column model (Hide · Show All · Column Chooser · drag-reorder · resize ·
  // persistence) — same as every other list grid. The leading select-all checkbox
  // stays frozen (not in the model); NotReport is a non-sortable action column.
  const MJ_COLS = useMemo<Array<{ key: string; label: string; sortable: boolean; fmt?: (r: DetailRow) => string }>>(() => [
    { key: 'jobId',     label: 'JobID',     sortable: true, fmt: r => String(r.jobId ?? '') },
    { key: 'sampleId',  label: 'SampleID',  sortable: true, fmt: r => String(r.sampleId ?? '') },
    { key: 'matrix',    label: 'Matrix',    sortable: true, fmt: r => String(r.matrix ?? '') },
    { key: 'test',      label: 'Test',      sortable: true, fmt: r => String(r.test ?? '') },
    { key: 'method',    label: 'Method',    sortable: true, fmt: r => String(r.method ?? '') },
    { key: 'parameter', label: 'Parameter', sortable: true, fmt: r => String(r.parameter ?? '') },
    { key: 'client',    label: 'Client',    sortable: true, fmt: r => String(r.client ?? '') },
    { key: 'notReport', label: 'NotReport', sortable: false },
    { key: 'status',    label: 'Status',    sortable: true, fmt: r => String(r.status ?? '') },
    { key: 'statusId',  label: 'StatusID',  sortable: true, fmt: r => String(r.statusId ?? '') },
  ], []);
  const cm = useColumnManager(MJ_COLS, c => c.key, c => c.label, 'reporting-multijob');
  // Right-click header menu (Sort + column management); `headerMenu` + `chooser`
  // render once and portal to body.
  const { openHeaderMenu, headerMenu } = useHeaderMenu(sortApi, cm.columnMenu);

  // ── Report combo (real list from /report-packages — user 2026-06-08) ──
  // ALPACA's "1) Level 1, RL, Sample Per Page", "1A) Level II, RL, ...", etc.
  // Same endpoint used by the per-job detail page.  Used for PDF generation
  // — clicking PDF with a package picked drills into the first JobID's
  // detail screen with that package pre-selected (multi-job package render
  // would need server changes; tracked for later).
  const [packages, setPackages] = useState<Array<{
    oid: string; packageName: string | null; reportName: string | null; sort: number;
  }>>([]);
  const [selectedPackage, setSelectedPackage] = useState<string>('');

  // ── 3 multi-job action popups (Case Narrative / Notes / All Params) ──
  // Each fans out to all selected JobIDs and combines the rows so the
  // user sees one merged grid — same chrome as the per-job ActionPopup
  // (theme-tinted title bar, search box, sortable headers).
  const [actionOpen, setActionOpen] = useState<null | 'case' | 'notes' | 'allparams'>(null);

  // ── Multi-job Report Package popup ────────────────────────────────
  // Clicking the PDF icon on the toolbar now opens the SAME popup the
  // per-job detail page shows: Reported Date / Approved By / Accreditation
  // toggle, plus a sub-report grid (CoverPage, COC, SCC, …).  The popup's
  // own Preview button does the fan-out — one /render-devx per selected
  // JobID, opening each PDF in a new tab.  Matches the single-job flow.
  const [pkgPopupOpen, setPkgPopupOpen] = useState(false);
  // (Render errors surface via the shared notifier popup — useNotify §18.6 —
  //  so no per-form pdfErr banner state is needed.)
  // ── Save gate (multi-job, ALPACA flow) ───────────────────────────
  // Save commits ONE ReportID covering every selected sample across the
  // (same-client) jobs.  DISABLED until the report PDF has been generated,
  // then ENABLED — identical gating to the per-job detail page.
  const [reportGenerated, setReportGenerated] = useState(false);
  // CCPP (owner request 2026-10-13, superseding the 2026-10-10 download-only rule):
  // Preview downloads the Water Stability Excel AND then enables Save Report — a CCPP
  // save mints a Report ID + moves the samples to Pending Validation, exactly like a
  // normal package.  The synthetic combo Oid is not a real dbo.ReportPackage, so the
  // save resolves the real "CCPP Report" package (by name) for the SP.  The CCPP
  // download sets the shared reportGenerated flag below (→ Save Report on).
  const [savingReport, setSavingReport]       = useState(false);
  // Amended flag — set inside MultiJobPackagePopup, consumed by onSaveReport.
  const [pkgAmended,     setPkgAmended]       = useState(false);
  const [pkgAmendedNote, setPkgAmendedNote]   = useState('');
  // Report Package dialog "Reported Date" / "Approved By" — G5 stamps them as the
  // saved report's ReportedDate / ReportedBy (owner 2026-10-17, G5 parity).  Only
  // values the user actually set are sent; otherwise the server uses now / the user.
  const [pkgReportInfo,  setPkgReportInfo]    = useState<PackageReportInfo>({});
  const notify = useNotify();
  const runSave = useSaveFlow();
  // Shared confirm + text-prompt dialogs — used by the ALPACA amended-save flow
  // ("Do you want to generate a new report ID?" → Revision Reason popup).
  const { confirm: confirmDlg, prompt: promptDlg } = useConfirm();

  // Stable cache key for the effect dep — comma-joined sorted jobOids.
  // Reference-equality on `jobs` would re-fetch on every parent render
  // because the array is rebuilt inline in the parent's JSX.
  const jobsKey = jobs.map(j => j.jobOid).join(',');

  // ITEM 2 (2026-06-17): total project-notes count across the selected jobs,
  // shown on the "Notes" toolbar button like ALPACA's "Project Notes (n)".
  // Fans out the existing per-job project-notes endpoint (silentLoading so the
  // global loader doesn't flash); refreshed whenever the Notes popup closes.
  const [projectNotesCount, setProjectNotesCount] = useState<number | null>(null);
  const loadProjectNotesCount = useCallback(async () => {
    if (jobs.length === 0) { setProjectNotesCount(null); return; }
    try {
      const results = await Promise.allSettled(jobs.map(j =>
        api.get(`/api/v1/reporting/custom/${encodeURIComponent(j.jobOid)}/project-notes`,
          { silentLoading: true } as never)));
      let total = 0;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          total += ((r.value.data as { data?: unknown[] }).data ?? []).length;
        }
      }
      setProjectNotesCount(total);
    } catch { setProjectNotesCount(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsKey]);
  useEffect(() => { void loadProjectNotesCount(); }, [loadProjectNotesCount]);

  // Load the Report combo's package list — same /report-packages endpoint
  // used by the per-job detail page (matches ALPACA's 8-item list).
  useEffect(() => {
    api.get('/api/v1/reporting/custom/report-packages')
      .then(res => {
        const data = (res.data as { data?: typeof packages }).data ?? [];
        setPackages(data);
      })
      .catch(() => setPackages([]));
  }, []);  // mount-only: static endpoint, setPackages is a stable setter

  // Pre-select the Report combo to the template chosen at Sample Registration
  // (Samplecheckin.ReportTemplate → ReportPackage), matching ALPACA so the
  // operator lands on the registration template with no extra click.  Uses the
  // first selected job's template (the multi-job report inherits one template);
  // only applies while the combo is still empty AND the resolved package is a
  // real option in the loaded list, so it never overrides a manual pick.
  useEffect(() => {
    if (selectedPackage) return;
    if (packages.length === 0) return;
    const firstJob = jobs[0]?.jobOid;
    if (!firstJob) return;
    let cancelled = false;
    api.get(`/api/v1/reporting/custom/${encodeURIComponent(firstJob)}/default-package`)
      .then(res => {
        if (cancelled) return;
        const oid = (res.data as { data?: { packageOid?: string | null } }).data?.packageOid;
        if (oid && packages.some(p => p.oid === oid)) setSelectedPackage(oid);
      })
      .catch(() => { /* leave the combo empty on error */ });
    return () => { cancelled = true; };
    // jobsKey is the stable proxy for `jobs` (see note above); deliberately
    // excluded `jobs` to avoid re-fetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsKey, packages, selectedPackage]);

  // Load (and reload) the combined parameter rows for the selected jobs.
  // Exposed as a callback so the toolbar Refresh button re-runs it.
  // Fan out: one GET per JobID, in parallel.  Errors are captured per job
  // (settled, not all/none) so one bad JobID doesn't blank the whole grid.
  // The per-job detail rows live at /custom/{jobOid}/parameters.
  const loadRows = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const t0 = performance.now();
    if (jobs.length === 0) {
      setRows([]); setOriginalRows([]); setSelectedIds(new Set());
      setLoadMs(performance.now() - t0);
      setLoading(false);
      return;
    }
    try {
      const results = await Promise.allSettled(jobs.map(j =>
        api.get('/api/v1/reporting/custom/' + encodeURIComponent(j.jobOid) + '/parameters',
          { params: { filter: jobIdFilter } })));
      const allRows: DetailRow[] = [];
      const failures: string[] = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          const data = (r.value.data as { data?: DetailRow[] }).data ?? [];
          // Tag each row with its owning JobOid so a NotReport edit can be
          // saved back to the correct per-job /notreport endpoint.
          const jobOid = jobs[i]?.jobOid;
          allRows.push(...data.map(d => withCanReport({ ...d, jobOid: d.jobOid ?? jobOid })));
        } else {
          const msg = (r.reason as { response?: { data?: { error?: { message?: string } } }; message?: string })
            ?.response?.data?.error?.message
            ?? (r.reason as { message?: string })?.message
            ?? 'request failed';
          failures.push((jobs[i]?.jobId ?? '?') + ': ' + msg);
          // eslint-disable-next-line no-console
          console.error('[MultiJobRetrieve] ' + (jobs[i]?.jobId ?? '?') + ' FAILED:', r.reason);
        }
      });
      setRows(allRows);
      setOriginalRows(allRows.map(r => ({ ...r })));
      // Rows at Pending Reporting or later are tickable (owner 2026-10-29), but only
      // the ones still AT Pending Reporting are pre-ticked: an already-reported test
      // is re-reported (a revision) only when the user ticks it.
      setSelectedIds(new Set(allRows
        .filter(r => r.canReport && r.status === 'Pending Reporting' && r.statusValue !== 10)
        .map(r => r.parameterOid)));
      setErr(failures.length > 0 ? 'Some jobs failed to load: ' + failures.join(' | ') : null);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsKey, jobIdFilter]);

  useEffect(() => { void loadRows(); }, [loadRows]);

  const toggleRow = (id: string) => {
    // A row before Pending Reporting is never tickable (owner 2026-10-29).
    if (!rows.find(r => r.parameterOid === id)?.canReport) return;
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  // Select-all acts on the reportable rows the grid is showing (search + filters).
  const toggleAllRows = () => {
    const visible = filtered.filter(r => r.canReport).map(r => r.parameterOid);
    setSelectedIds(prev => {
      const n = new Set(prev);
      const all = visible.length > 0 && visible.every(id => n.has(id));
      visible.forEach(id => (all ? n.delete(id) : n.add(id)));
      return n;
    });
  };

  // ── CCPP (Water Stability) PREVIEW download ────────────────────────
  // When the synthetic "CCPP Report" option is selected, Preview generates the
  // Water Stability Excel for the ticked samples across the loaded job(s) and
  // triggers the browser save — the same on-preview behaviour as Ad Hoc / the
  // EDD, scoped by Job ID server-side.  204 = no CCPP data for the selection
  // (e.g. results not yet approved / the snapshot hasn't refreshed).
  const downloadCcppExcel = useCallback(async () => {
    const sampleOids = Array.from(new Set(
      rows.filter(r => selectedIds.has(r.parameterOid))
          .map(r => r.sampleOid).filter((x): x is string => !!x)));
    const jobOids = jobs.map(j => j.jobOid);
    if (jobOids.length === 0) return;
    if (sampleOids.length === 0) {
      notify.warning('Tick at least one sample to preview the CCPP report.');
      return;
    }
    try {
      await runLoading((async () => {
        const res = await api.get('/api/v1/reporting/custom/ccpp-excel', {
          params: { jobOids: jobOids.join(','), sampleOids: sampleOids.join(',') },
          responseType: 'blob', validateStatus: s => s === 200 || s === 204,
        });
        const blob = res.data as Blob | undefined;
        if (res.status === 204 || !blob || blob.size === 0) {
          notify.warning('No CCPP (Water Stability) data found for the selected samples — the results may not be approved yet.');
          return;
        }
        const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
        const m = /filename\*?=(?:UTF-8'')?"?([^\";]+)"?/i.exec(cd);
        const fileName = m && m[1] ? decodeURIComponent(m[1]) : 'CCPP_Report.xlsx';
        const url = URL.createObjectURL(blob);
        try {
          const a = document.createElement('a');
          a.href = url; a.download = fileName;
          document.body.appendChild(a); a.click(); a.remove();
          setReportGenerated(true);     // owner request 2026-10-13: enable Save Report after the CCPP download
        } finally {
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
      })(), { blocking: true, label: 'Generating CCPP report…' });
    } catch {
      notify.error('CCPP report failed to generate.');
    }
  }, [rows, selectedIds, jobs, notify]);

  // When the synthetic "LSI Report" option is selected, Preview generates the EWPP
  // LSI Excel for the ticked samples across the loaded job(s) and triggers the
  // browser save — the SAME on-preview behaviour as CCPP, scoped by Job ID
  // server-side.  204 = no LSI data for the selection (results not yet approved).
  const downloadLsiExcel = useCallback(async () => {
    const sampleOids = Array.from(new Set(
      rows.filter(r => selectedIds.has(r.parameterOid))
          .map(r => r.sampleOid).filter((x): x is string => !!x)));
    const jobOids = jobs.map(j => j.jobOid);
    if (jobOids.length === 0) return;
    if (sampleOids.length === 0) {
      notify.warning('Tick at least one sample to preview the LSI report.');
      return;
    }
    try {
      await runLoading((async () => {
        const res = await api.get('/api/v1/reporting/custom/lsi-excel', {
          params: { jobOids: jobOids.join(','), sampleOids: sampleOids.join(',') },
          responseType: 'blob', validateStatus: s => s === 200 || s === 204,
        });
        const blob = res.data as Blob | undefined;
        if (res.status === 204 || !blob || blob.size === 0) {
          notify.warning('No LSI data found for the selected samples — the results may not be approved yet.');
          return;
        }
        const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
        const m = /filename\*?=(?:UTF-8'')?"?([^\";]+)"?/i.exec(cd);
        const fileName = m && m[1] ? decodeURIComponent(m[1]) : 'LSI_Report.xlsx';
        const url = URL.createObjectURL(blob);
        try {
          const a = document.createElement('a');
          a.href = url; a.download = fileName;
          document.body.appendChild(a); a.click(); a.remove();
          setReportGenerated(true);     // enable Save Report after the LSI download (CCPP parity)
        } finally {
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
      })(), { blocking: true, label: 'Generating LSI report…' });
    } catch {
      notify.error('LSI report failed to generate.');
    }
  }, [rows, selectedIds, jobs, notify]);

  // Preview trigger — the synthetic "CCPP Report" / "LSI Report" options download
  // their Excel; every real package opens the sub-report popup (PDF) as before.
  const onPreviewClick = useCallback(() => {
    if (isCcppPackageOid(selectedPackage)) { void downloadCcppExcel(); return; }
    if (isLsiPackageOid(selectedPackage))  { void downloadLsiExcel();  return; }
    setPkgPopupOpen(true);
  }, [selectedPackage, downloadCcppExcel, downloadLsiExcel]);

  // Pipeline: free-text search → per-column Excel filters → click sort.
  // Identical chain used by GenericReportingTable above (which the list
  // pages use), so behaviour matches what the user sees everywhere else.
  const filtered = useMemo(() => {
    let v: DetailRow[] = rows;
    const t = search.trim().toLowerCase();
    if (t) {
      v = v.filter(r =>
        [r.jobId, r.sampleId, r.matrix, r.test, r.method, r.parameter, r.status, r.statusId, r.client]
          .some(x => String(x ?? '').toLowerCase().includes(t)));
    }
    // Apply each per-column filter via the shared passesColumnFilter()
    // helper.  Key by the cell-key strings used in the header below.
    const cols: Array<[string, (r: DetailRow) => string]> = [
      ['jobId',     r => String(r.jobId ?? '')],
      ['sampleId',  r => String(r.sampleId ?? '')],
      ['matrix',    r => String(r.matrix ?? '')],
      ['test',      r => String(r.test ?? '')],
      ['method',    r => String(r.method ?? '')],
      ['parameter', r => String(r.parameter ?? '')],
      ['client',    r => String(r.client ?? '')],
      ['status',    r => String(r.status ?? '')],
      ['statusId',  r => String(r.statusId ?? '')],
    ];
    for (const [key, fmt] of cols) {
      const f = filterOn ? colF[key] : null;   // filter bar tick box off → not applied
      if (f) v = v.filter(r => passesColumnFilter(f, fmt(r)));
    }
    // Sort by the active header column.
    const sk = sortApi.sort?.key;
    if (sk) {
      const col = cols.find(([k]) => k === sk);
      if (col) {
        const [, fmt] = col;
        const dir = sortApi.sort?.dir === 'desc' ? -1 : 1;
        v = [...v].sort((a, b) => fmt(a).localeCompare(fmt(b), undefined, { numeric: true }) * dir);
      }
    }
    return v;
  }, [rows, search, colF, filterOn, sortApi.sort]);

  // Full-format export (all 10 ALPACA formats + every filtered row) — the combined
  // multi-job grid is a hand-rolled <table>, so the export matrix is built inline
  // from the same `filtered` rows + column set the grid renders.
  const exportData: ExportData = {
    filename: 'multi-job-retrieve-' + jobs.length + 'jobs',
    headers: ['JobID', 'SampleID', 'Matrix', 'Test', 'Method', 'Parameter', 'Client', 'Status'],
    rows: filtered.map(r => [
      r.jobId ?? '', r.sampleId ?? '', r.matrix ?? '', r.test ?? '',
      r.method ?? '', r.parameter ?? '', r.client ?? '', r.status ?? '',
    ].map(v => String(v))),
  };

  // Pre-built — mirrors the per-job Custom Reporting detail screen
  // exactly (user requested 2026-06-08).  Same toolbar buttons (Save,
  // Report combo, PDF, Refresh, Case Narrative, Notes, All Params,
  // Export, Audit), same checkbox grid, same status pills, same
  // pagination footer.  Multi-job-only buttons that can't reasonably
  // operate across jobs are tooltip-disabled with a hint to drill into
  // a single JobID.
  // Switching the report (or the job set) forces a fresh generate before Save.
  useEffect(() => { setReportGenerated(false); }, [selectedPackage, jobsKey]);

  // ── NotReport editing ──────────────────────────────────────────────
  // The NotReport flag marks a parameter to be EXCLUDED from the generated
  // report (ALPACA parity).  Editing it invalidates any already-generated
  // PDF, so we force a fresh generate before the report can be committed.
  const toggleNotReport = (oid: string) => {
    setRows(rs => rs.map(r =>
      r.parameterOid === oid ? { ...r, notReport: !r.notReport } : r));
    setReportGenerated(false);
  };
  const notReportDirty = useMemo(() => {
    const orig = new Map(originalRows.map(r => [r.parameterOid, !!r.notReport]));
    return rows.some(r => orig.get(r.parameterOid) !== !!r.notReport);
  }, [rows, originalRows]);

  // Persist NotReport edits — grouped by JobOid because the endpoint is
  // per-job (PUT /custom/{jobOid}/notreport).  One PUT per affected job.
  // Returns how many parameters changed (0 = nothing to flush).  Shared by the
  // explicit "Save changes" button and the report-commit Save (so a committed
  // report never ignores an unsaved NotReport edit).
  const flushNotReportEdits = useCallback(async (): Promise<number> => {
    const orig = new Map(originalRows.map(r => [r.parameterOid, !!r.notReport]));
    const changed = rows.filter(r => orig.get(r.parameterOid) !== !!r.notReport);
    if (changed.length === 0) return 0;
    const byJob = new Map<string, Array<{ parameterOid: string; notReport: boolean }>>();
    for (const r of changed) {
      const jo = r.jobOid ?? jobs.find(j => j.jobId === r.jobId)?.jobOid;
      if (!jo) continue;
      const arr = byJob.get(jo) ?? [];
      arr.push({ parameterOid: r.parameterOid, notReport: !!r.notReport });
      byJob.set(jo, arr);
    }
    await Promise.all(Array.from(byJob.entries()).map(([jo, arr]) =>
      api.put('/api/v1/reporting/custom/' + encodeURIComponent(jo) + '/notreport', arr)));
    setOriginalRows(rows.map(r => ({ ...r })));
    return changed.length;
  }, [rows, originalRows, jobs]);

  const onSaveNotReport = useCallback(async () => {
    if (!notReportDirty) { notify.info('No changes to save.'); return; }
    setSavingChanges(true);
    try {
      const n = await flushNotReportEdits();
      notify.success(`Saved ${n} NotReport change${n === 1 ? '' : 's'}.`);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Save failed.';
      notify.error(msg);
    } finally {
      setSavingChanges(false);
    }
  }, [notReportDirty, flushNotReportEdits, notify]);

  const onCancelNotReport = () => {
    setRows(originalRows.map(r => ({ ...r })));
    notify.info('Reverted.');
  };

  // ── Save the multi-job report (ALPACA SaveReport_Execute, multi-JobID) ──
  // ONE ReportID for every ticked sample across the selected same-client jobs;
  // flips them to Pending Validation.  Gated behind reportGenerated.
  const onSaveReport = useCallback(async () => {
    if (!selectedPackage) { notify.warning('Select a report from the toolbar first.'); return; }
    const oids = Array.from(selectedIds);
    if (oids.length === 0) { notify.warning('Tick at least one sample row to report.'); return; }

    // The combo's CCPP entry is a synthetic sentinel, NOT a real dbo.ReportPackage, so
    // the save resolves the real "CCPP Report" package (still in `packages`, just hidden
    // from the combo) to mint a Report ID (owner request 2026-10-13).  Every other report
    // saves under its own selected package.
    const savePackageOid = isExcelReportOid(selectedPackage)
      ? packages.find(p => ((p.packageName ?? p.reportName) ?? '').trim().toLowerCase()
          === (isCcppPackageOid(selectedPackage) ? 'ccpp report' : 'lsi report'))?.oid
      : selectedPackage;
    if (!savePackageOid) {
      const label = isLsiPackageOid(selectedPackage) ? 'LSI Report' : 'CCPP Report';
      notify.error(`The "${label}" package is not configured, so the report cannot be saved. Preview still downloads the Excel.`);
      return;
    }

    // The actual commit, shared by the normal and amended paths.  Flushes any
    // pending NotReport edits first, then commits the multi-job report (one
    // ReportID covering every ticked sample).  A non-null revisionReason makes
    // the server mint the REVISED <base>R<n> id.  Returns the success message
    // (echoing the minted id — §18.6 Custom-Reporting exception).
    const commitReport = async (revisionReason: string | null, isAmended: boolean): Promise<string> => {
      await flushNotReportEdits();
      const res = await api.post(
        '/api/v1/reporting/custom/save-report-multi',
        { selectedParameterOids: oids, isAmended,
          amendedNote: pkgAmendedNote || null, revisionReason,
          // G5 parity (owner 2026-10-17): the drill-in filter decides which tests of
          // each ticked row are linked; the dialog's date / approver become the
          // report's ReportedDate / ReportedBy.
          filterMode: jobIdFilter,
          reportedDate: pkgReportInfo.reportedDate ?? null,
          reportedByOid: pkgReportInfo.approvedByOid ?? null },
        // packageOid resolves the synthetic CCPP sentinel to the real "CCPP Report"
        // package (savePackageOid); for every other report it IS selectedPackage.
        { params: { packageOid: savePackageOid } });
      const id = (res.data as { data?: { reportId?: string } }).data?.reportId;
      return id ? `Report ${id} saved successfully.` : 'Report saved successfully.';
    };

    // ── ALPACA save-decision parity (ReportingViewController.SaveReport_Execute /
    //    _Executing, GoLive) ─────────────────────────────────────────────────
    //   1. Any selected sample NOT yet approved (below Pending Reporting AND not
    //      already reported — ALPACA L3338): block, show "Without approve…", the
    //      user can only Preview.
    //   2. Any selected sample ALREADY reported (ALPACA L3274
    //      `sp.Reportings.Count() > 0`): the save is a REVISION → confirm
    //      "generate a new report ID?" → reason (matches ALPACA even though the
    //      user did NOT pre-toggle "Amended").
    //   3. Otherwise: a normal fresh-ID save.
    const selRows = rows.filter(r => selectedIds.has(r.parameterOid));
    // G5 (RVC ~3350): any selected row at Status 0/1/2 blocks the save, reported or not.
    // Owner 2026-10-29: the stage comes from SampleStatusNew (canReport), not the int
    // Status — a Pending Delivery test is int 1 yet may be re-reported.
    const anyNotApproved   = selRows.some(r => !r.canReport);
    const anyAlreadyReport = selRows.some(r => r.alreadyReported);
    const doRevision = pkgAmended || anyAlreadyReport;

    setSavingReport(true);
    try {
      if (anyNotApproved) {
        // ALPACA L3341 "withoutapprove" — cannot save until approved; preview only.
        notify.warning('Only samples at Pending Reporting or a later reporting stage can be saved to a report.');
        return;
      }

      if (doRevision) {
        // ── AMENDED SAVE (ALPACA parity) — re-reporting already-reported samples
        //    is a REVISION: confirm "generate a new report ID?" → Revision Reason
        //    prompt → the server mints <base>R<n> + audits "Report Tracking". ───
        const genNew = await confirmDlg({
          title: 'Generate New Report ID',
          message: 'Do you want to generate a new report ID?',
        });
        if (!genNew) return;
        const reason = await promptDlg({
          title: 'Revision Reason',
          message: 'Changes made on the selected job(s), please provide the comment.',
          placeholder: 'Reason',
          required: true,
        });
        if (reason === null) return;   // user cancelled the Revision Reason prompt
        let msg = 'Report saved successfully.';
        await runLoading((async () => { msg = await commitReport(reason.trim(), true); })(),
          { blocking: true, label: 'Saving…' });
        notify.success(msg);
        setReportGenerated(false);
        (onSaved ?? onBack)();
        return;
      }

      // ── NORMAL SAVE — shared useSaveFlow ("Do you want to Save?" → save →
      //    "Report {id} saved successfully." → return to the list). ───────────
      let successMsg = 'Report saved successfully.';
      await runSave({
        save: async () => { successMsg = await commitReport(null, false); },
        get successMessage() { return successMsg; },
        successAs: 'toast',
        // ALPACA SaveReport → View.Close(): return to the Custom Reporting list.
        onSaved: () => { setReportGenerated(false); (onSaved ?? onBack)(); },
        onError: (e) => {
          const msg = (e as { response?: { data?: { error?: { message?: string } } } })
            .response?.data?.error?.message ?? 'Save failed.';
          notify.error(msg);
        },
      });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Save failed.';
      notify.error(msg);
    } finally {
      setSavingReport(false);
    }
  }, [selectedPackage, selectedIds, rows, onBack, onSaved, notify, runSave, confirmDlg, promptDlg,
      flushNotReportEdits, pkgAmended, pkgAmendedNote, packages, jobIdFilter, pkgReportInfo]);

  // The header tick box reflects the reportable rows only (earlier stages can't be ticked).
  const reportable = filtered.filter(r => r.canReport);
  const allSelected = reportable.length > 0
    && reportable.every(r => selectedIds.has(r.parameterOid));
  const someSelected = !allSelected && reportable.some(r => selectedIds.has(r.parameterOid));

  // Multi-job Custom Reporting keyboard shortcuts (CLAUDE.md standing rule 14,
  // §18.6, ADR-0013).  Bound INSIDE this full-page view so its own actions are
  // covered (the outer list's wfKeys belong to the list, not this view).  Each
  // entry mirrors its button's render/disabled condition.  mod+s → Save Report
  // matches the sibling CustomReportingDetailPage.
  useHotkeys([
    // Save Report — mint a ReportID (Create-gated); mirrors the top-right button.
    { combo: 'mod+s', label: 'Save Report', group: 'Custom Reporting', order: 1,
      disabled: !perm.create || !reportGenerated || savingReport,
      run: () => { void onSaveReport(); } },
    // Cancel — discard NotReport edits and return to the list.
    { combo: 'esc', label: 'Cancel', group: 'Custom Reporting', order: 2, allowInInput: false,
      run: () => { if (notReportDirty) onCancelNotReport(); onBack(); } },
    // Open Report Package — generate the PDF per JobID.
    { combo: 'alt+p', label: 'Preview report', group: 'Custom Reporting', order: 3,
      disabled: jobs.length === 0 || !selectedPackage,
      run: onPreviewClick },
    // Case Narrative across the selected JobIDs.
    { combo: 'alt+shift+c', label: 'Case Narrative', group: 'Custom Reporting', order: 4,
      disabled: jobs.length === 0,
      run: () => setActionOpen('case') },
    // Project Notes across the selected JobIDs.
    { combo: 'alt+o', label: 'Project Notes', group: 'Custom Reporting', order: 5,
      disabled: jobs.length === 0,
      run: () => setActionOpen('notes') },
    // All Params — needs at least one ticked row.
    { combo: 'alt+shift+a', label: 'All Params', group: 'Custom Reporting', order: 6,
      disabled: selectedIds.size === 0,
      run: () => setActionOpen('allparams') },
    // Save — persist the grid's NotReport edits (Create-gated).
    { combo: 'alt+shift+s', label: 'Save', group: 'Custom Reporting', order: 7,
      disabled: !perm.create || !notReportDirty || savingChanges,
      run: () => { void onSaveNotReport(); } },
  ]);

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span style={{ cursor: 'pointer', color: 'var(--ai-blue)' }} onClick={onBack}>Home</span>
            <i className="ti ti-chevron-right" />
            <span style={{ cursor: 'pointer', color: 'var(--ai-blue)' }} onClick={onBack}>Custom Reporting</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{jobs.map(j => j.jobId).join(', ')}</span>
          </div>
          {/* ONE shared <FormTitle> — nav icon/colour + TOP load-time chip +
              the config-gated Audit Trail chip (title row, BO UI Standard v2).
              Save Report is docked at the TOP-RIGHT (prototype parity, user
              request 2026-07-04). */}
          <div className="dp-title-row">
            <FormTitle pageId="custreport" loadMs={loadMs} loading={loading}
                       auditFormName="Custom Reporting" />
            {/* Form-level actions at the TOP-RIGHT (§6 altitudes): Save Report +
                the ONE Cancel.  Cancel discards any pending NotReport edits and
                returns to the Custom Reporting list — the single cancel for the
                whole screen (user request 2026-07-06; the toolbar Cancel below
                was removed). */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* Save Report mints a ReportID — Create-gated. */}
              {perm.create && (
              <button className="ca-btn ca-btn-solid"
                      onClick={onSaveReport}
                      disabled={!reportGenerated || savingReport}
                      title={(isExcelReportOid(selectedPackage)
                        ? (reportGenerated
                          ? `Save the ${isLsiPackageOid(selectedPackage) ? 'LSI' : 'CCPP'} report — assigns a Report ID and moves the ticked samples to Pending Validation`
                          : `Preview the ${isLsiPackageOid(selectedPackage) ? 'LSI' : 'CCPP'} report first (downloads the Excel), then Save Report`)
                        : reportGenerated
                        ? `Save ONE report covering all ${jobs.length} selected JobID(s) — assigns a Report ID and moves the samples to Pending Validation`
                        : 'Generate the report first (Report → PDF), then Save Report') + ` (${formatCombo('mod+s')})`}>
                {savingReport ? <BusyIcon busy icon="ti-device-floppy" /> : <span aria-hidden="true">💾</span>} {savingReport ? 'Saving…' : 'Save Report'}
              </button>
              )}
              <button className="ca-btn"
                      onClick={() => { if (notReportDirty) onCancelNotReport(); onBack(); }}
                      title={`Cancel — discard any changes and return to the Custom Reporting list (${formatCombo('esc')})`}>
                <span className="tb-i" aria-hidden="true">✕</span> Cancel
              </button>
            </div>
          </div>
          <div className="dp-subtitle">
            JobID <strong>{jobs.map(j => j.jobId).join(', ')}</strong>
            &nbsp;·&nbsp;{loading ? '…' : rows.length} parameters
          </div>
          {/* Per-form workflow stepper — docked in the top bar (BO UI Standard
              §5) via <TopbarStepper>. The combined detail's own 4 steps,
              computed live. Step 1 (Select Job) is complete on arrival here. */}
          <TopbarStepper
            steps={((): IslandStep[] => {
              const templateChosen = !!selectedPackage;
              // CCPP / LSI: Preview downloads the Excel, then Save Report mints a Report
              // ID — a full 4-step flow like a real package (CCPP 2026-10-13, LSI 2026-10-20).
              if (isExcelReportOid(selectedPackage)) {
                return [
                  { id: 'select',   label: 'Select Job',                   state: 'done' },
                  { id: 'mark',     label: 'Mark Samples & Choose Report', state: 'done' },
                  { id: 'download', label: 'Download',                     state: reportGenerated ? 'done' : 'active' },
                  { id: 'save',     label: 'Save',                         state: reportGenerated ? 'active' : 'todo' },
                ];
              }
              return [
                { id: 'select',  label: 'Select Job',                   state: 'done' },
                { id: 'mark',    label: 'Mark Samples & Choose Report', state: (templateChosen || reportGenerated) ? 'done' : 'active' },
                { id: 'preview', label: 'Preview',                      state: reportGenerated ? 'done' : (templateChosen ? 'active' : 'todo') },
                { id: 'save',    label: 'Save',                         state: reportGenerated ? 'active' : 'todo' },
              ];
            })()}
          />
        </div>
      </div>

      {/* Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
          only the grid card below is white. */}
      <div className="bl-grid-area">

          {/* Contextual guidance strip — what to do next on this screen. */}
          {isExcelReportOid(selectedPackage)
            ? (reportGenerated
                ? <StepHint tone="complete"><b>{isLsiPackageOid(selectedPackage) ? 'LSI' : 'CCPP'} downloaded.</b> The {isLsiPackageOid(selectedPackage) ? 'LSI' : 'Water Stability'} Excel downloaded for the ticked samples — now click <b>Save Report</b> to mint a Report ID and send it to Report Validation.</StepHint>
                : <StepHint><b>{isLsiPackageOid(selectedPackage) ? 'LSI report' : 'CCPP (Water Stability) report'}.</b> Tick the samples to include, then click <b>Preview</b> — the Excel downloads for the ticked samples. Then <b>Save Report</b> is enabled.</StepHint>)
            : reportGenerated
            ? <StepHint tone="complete"><b>Step 4 · Save.</b> The report generated successfully — click <b>Save Report</b> to mint a Report ID and send it to Report Validation.</StepHint>
            : <StepHint><b>Step 2 · Mark samples &amp; choose a report.</b> Untick any sample rows to exclude, select a <b>Report</b> template from the toolbar, then click the package icon to open the Report Package.</StepHint>}

          {/* Save result is shown via the shared notification service (useNotify);
              the new Report ID appears in the grid row, never in the toast. */}

          {/* ── Toolbar on its OWN full-width row — shared <AppToolbar>; every
              form-specific button (Save · Report combo + PDF · Case Narrative ·
              Notes · All Params · Export · Audit) stays in `left` in its
              existing order with its existing dividers, while the reload action
              is routed to the standard Refresh button via onRefresh. */}
          <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={loadRows}
            refreshing={loading}
            exportData={exportData}
            exportDisabled={loading || filtered.length === 0}
            left={
              <>
                {/* GROUP 1 · Report combo + PDF — FIRST item on the bar (before
                    the Save group).  White dropdown (sa-date-sel-plain, user
                    request 2026-07-04) — orange value text, no orange fill. */}
                <span className="app-toolbar__label">Report</span>
                {/* Custom orange dropdown (ComboSelect) — a native <select>'s open
                    list is browser-drawn (blue highlight, unstyleable); this uses
                    the app's orange-themed popup (2026-07-11 user request). */}
                <div style={{ width: 240 }} title="Pick the report package to generate">
                  <ComboSelect
                    value={selectedPackage}
                    onChange={setSelectedPackage}
                    placeholder="— Select —"
                    options={[
                      ...packages
                        // Custom Reporting always appends its OWN synthetic "CCPP Report"
                        // and "LSI Report" below (Preview → downloads an Excel).  A real
                        // dbo.ReportPackage header literally named "CCPP Report" / "LSI
                        // Report" would duplicate it in the combo with a broken
                        // Report-Package popup, so drop those labels.  (The Ad Hoc
                        // __WATER_STABILITY__ / __LSI_REPORT__ sentinels are dropped too, in
                        // case they ever feed this list.) (2026-10-13; LSI added 2026-10-20)
                        .filter(pkg => {
                          const label = (pkg.packageName ?? pkg.reportName ?? '').trim().toLowerCase();
                          if (label === 'ccpp report' || label === 'lsi report') return false;
                          if (pkg.reportName === '__WATER_STABILITY__' || pkg.reportName === '__LSI_REPORT__') return false;
                          if (pkg.packageName === '__WATER_STABILITY__' || pkg.packageName === '__LSI_REPORT__') return false;
                          return true;
                        })
                        .map(pkg => ({
                          value: pkg.oid,
                          label: pkg.packageName ?? pkg.reportName ?? '(unnamed)',
                        })),
                      // Synthetic "CCPP Report" — downloads the Water Stability Excel
                      // for the ticked samples on Preview (owner request 2026-10-10).
                      // Shown ONLY when the real dbo.ReportPackage "CCPP Report" header is
                      // ACTIVE: the backend list (CustomReportingPackagesAsync) returns only
                      // IsActive=1 rows, so setting that row IsActive=0 drops it from
                      // `packages` and hides this option (owner request 2026-10-15).
                      ...(packages.some(pkg => ((pkg.packageName ?? pkg.reportName ?? '').trim().toLowerCase()) === 'ccpp report')
                        ? [{ value: CCPP_PACKAGE_OID, label: 'CCPP Report' }]
                        : []),
                      // Synthetic "LSI Report" — same mechanism as CCPP; shown ONLY when the
                      // real dbo.ReportPackage "LSI Report" header is ACTIVE (owner request
                      // 2026-10-20).
                      ...(packages.some(pkg => ((pkg.packageName ?? pkg.reportName ?? '').trim().toLowerCase()) === 'lsi report')
                        ? [{ value: LSI_PACKAGE_OID, label: 'LSI Report' }]
                        : []),
                    ]}
                  />
                </div>
                <button className="ca-btn"
                        disabled={jobs.length === 0 || !selectedPackage}
                        onClick={onPreviewClick}
                        title={(!selectedPackage
                          ? 'Pick a report package first'
                          : isCcppPackageOid(selectedPackage)
                            ? 'Preview the CCPP (Water Stability) report — downloads Excel for the ticked samples'
                            : isLsiPackageOid(selectedPackage)
                            ? 'Preview the LSI report — downloads Excel for the ticked samples'
                            : 'Open Report Package — pick sub-reports + generate PDF per JobID') + ` (${formatCombo('alt+p')})`}>
                  {/* Icon-only PDF action — 📑 report emoji (user pick 2026-07-04). */}
                  <span style={{ fontSize: 15 }} aria-hidden="true">📑</span>
                </button>

                <ToolbarDivider />

                {/* GROUP 2 · Job actions — Case Narrative / Notes drill into
                    the per-job detail page (full functionality there).  The
                    reload action moved to the standard AppToolbar Refresh
                    button via onRefresh. */}
                <button className="ca-btn"
                        disabled={jobs.length === 0}
                        onClick={() => setActionOpen('case')}
                        title={`Case Narrative across ${jobs.length} JobID${jobs.length === 1 ? '' : 's'} (${formatCombo('alt+shift+c')})`}>
                  <span aria-hidden="true">📋</span> Case Narrative
                </button>
                <button className="ca-btn"
                        disabled={jobs.length === 0}
                        onClick={() => setActionOpen('notes')}
                        title={`Project Notes across ${jobs.length} JobID${jobs.length === 1 ? '' : 's'} (${formatCombo('alt+o')})`}>
                  <span aria-hidden="true">📝</span> Project Notes
                  {projectNotesCount != null && ` (${projectNotesCount})`}
                </button>
                <button className="ca-btn" disabled={selectedIds.size === 0}
                        onClick={() => setActionOpen('allparams')}
                        title={(selectedIds.size === 0
                          ? 'Tick at least one row to enable All Params'
                          : `Report Parameters — the parameters of the ${selectedIds.size} selected sample test(s)`) + ` (${formatCombo('alt+shift+a')})`}>
                  <span aria-hidden="true">📊</span> All Params
                </button>

                <ToolbarDivider />

                {/* (Audit Trail is VIEW-ONLY — it renders as the shared chip on
                    the title row via <FormTitle auditFormName>, never as a
                    toolbar action — BO UI Standard v2 ruling 2026-07-01.) */}

                {/* GROUP 3 · "Save Report" + Cancel moved to the TOP-RIGHT title
                    row (user request 2026-07-04 / 2026-07-06).  This toolbar Save
                    persists the grid's NotReport edits; discarding them is handled
                    by the ONE top-right Cancel (the toolbar Cancel was removed). */}
                {perm.create && (
                <button className="ca-btn" onClick={onSaveNotReport}
                        disabled={!notReportDirty || savingChanges}
                        title={(notReportDirty
                          ? 'Save the NotReport changes'
                          : 'No NotReport changes to save') + ` (${formatCombo('alt+shift+s')})`}>
                  {savingChanges ? <BusyIcon busy icon="ti-checkbox" /> : <span aria-hidden="true">☑️</span>} {savingChanges ? 'Saving…' : 'Save'}
                </button>
                )}
              </>
            }
          />
          </div>

          {/* Search row + "N selected" indicator — shared .list-search-row on
              the grey page, never inside the card. */}
          <div className="list-search-row" style={{ position: 'relative' }}>
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" value={search}
                     onChange={e => setSearch(e.target.value)}
                     placeholder="Search across all columns…" />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ai-ink)' }}>
              {selectedIds.size} Selected
            </span>
          </div>

          {/* ONE white grid card — grid + its single footer.  (Render errors now
              surface through the shared notifier popup — useNotify, §18.6 — the
              old inline pdfErr banner was removed 2026-07-04.) */}
          <div className="bl-card bl-card-flex">

          {/* Grid — same bl-tbl class + same column set as the per-job
              Detail screen.  Checkbox column on the left, all rows pre-
              ticked.  Status uses bl-pill variants. */}
          <div className="bl-tbl-wrap">
            {err && !loading && (
              <div className="bl-empty" style={{ color: 'var(--ai-red)', flexDirection: 'column', gap: 6 }}>
                <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: .35 }} />
                <div>{err}</div>
              </div>
            )}
            {loading && (
              <div className="bl-loading"><LoaderSymbol size={14} /> Loading from {jobs.length} job{jobs.length === 1 ? '' : 's'}…</div>
            )}
            {/* The table (and its header filters) stays up when nothing matches, so the
                user can always widen or clear a filter — only the data rows go
                (owner 2026-10-29). The "No rows" line is a body row. */}
            {!loading && !err && rows.length === 0 && (
              <div className="bl-empty" style={{ flexDirection: 'column', gap: 6 }}>
                <i className="ti ti-report-off" style={{ fontSize: 24, opacity: .35 }} />
                <div>No rows.</div>
              </div>
            )}
            {!loading && !err && rows.length > 0 && (
              <table className="bl-tbl">
                <thead>
                  <tr>
                    <th style={{ width: 36, textAlign: 'center' }}>
                      <input type="checkbox"
                             checked={allSelected}
                             disabled={reportable.length === 0}
                             title={reportable.length === 0
                               ? 'No sample is at Pending Reporting or later' : undefined}
                             ref={el => { if (el) el.indeterminate = someSelected; }}
                             onChange={toggleAllRows} />
                    </th>
                    {/* Array-driven from the column model → Hide / reorder keep the
                        header and body in lock-step. Sortable columns keep the
                        SortableHeader + Excel-style ColFilterButton; NotReport is a
                        plain action header. `.adg-resize` handle on every column. */}
                    {cm.visibleColumns.map(c => (
                      <th key={c.key}
                          className={cm.dragClass(c.key)}
                          style={{ cursor: 'pointer',
                                   ...(cm.colWidth(c.key) ? { width: cm.colWidth(c.key), minWidth: cm.colWidth(c.key) } : {}),
                                   ...(c.key === 'notReport' ? { textAlign: 'center' as const } : {}) }}
                          onClick={c.sortable ? () => sortApi.setSort(c.key) : undefined}
                          onContextMenu={(e) => openHeaderMenu(e, { field: c.key, label: c.label, sortable: c.sortable })}
                          {...cm.dragProps(c.key)}>
                        {c.sortable ? (
                          <SortableHeader label={c.label} sortKey={c.key} sort={sortApi.sort}>
                            <ColFilterButton variant="inline" col={c.key} label={c.label}
                              rows={rows as unknown[]}
                              formatter={(r: unknown) => c.fmt!(r as DetailRow)}
                              /* filter list follows the column sort; default DESC
                                 (this is a JobID-first grid) when nothing is click-sorted */
                              sortDir={sortApi.sort.key === c.key ? sortApi.sort.dir : 'desc'}
                              value={colF[c.key] ?? null}
                              onChange={v => { setColF(p => ({ ...p, [c.key]: v })); setFilterOn(true); }} />
                          </SortableHeader>
                        ) : c.label}
                        <span className="adg-resize" onMouseDown={(e) => cm.startResize(c.key, e)} onClick={(e) => e.stopPropagation()} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => {
                    // One cell per column key; the row renders the checkbox (frozen)
                    // then the VISIBLE columns in the user's order — same source as
                    // the header, so a reorder/hide keeps them aligned.
                    const cell: Record<string, ReactNode> = {
                      jobId: (
                        <td key="jobId" className="jobid">
                          {/* Click JobID → drill into that job's detail page (full
                              report-generation functionality). Look up the JobOid
                              from the jobs prop by matching the displayed JobId. */}
                          {onCustomJobDrill && r.jobId ? (
                            <a href="#"
                               onClick={e => {
                                 e.preventDefault();
                                 const job = jobs.find(j => j.jobId === r.jobId);
                                 if (job) onCustomJobDrill(job.jobOid, job.jobId);
                               }}
                               style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
                               title={'Open ' + r.jobId + ' detail page (Save / Report / PDF / Case Narrative / Notes all work there)'}>
                              {r.jobId}
                            </a>
                          ) : (r.jobId ?? '')}
                        </td>
                      ),
                      sampleId:  <td key="sampleId" className="jobid">{r.sampleId ?? ''}</td>,
                      matrix:    <td key="matrix">{r.matrix ?? ''}</td>,
                      test:      <td key="test">{r.test ?? ''}</td>,
                      method:    <td key="method">{r.method ?? ''}</td>,
                      parameter: <td key="parameter">{r.parameter ?? ''}</td>,
                      client:    <td key="client">{r.client ?? ''}</td>,
                      notReport: (
                        <td key="notReport" style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                          <input type="checkbox"
                                 checked={r.notReport ?? false}
                                 onChange={() => toggleNotReport(r.parameterOid)}
                                 title="Exclude this parameter from the report" />
                        </td>
                      ),
                      status:    <td key="status"><span className={'bl-pill ' + stagePillVariant(r.status ?? '')}>{r.status ?? ''}</span></td>,
                      statusId:  <td key="statusId" className="jobid">{r.statusId ?? ''}</td>,
                    };
                    return (
                      <tr key={r.parameterOid + i}
                          className={r.canReport ? undefined : 'bl-row-blocked'}
                          style={selectedIds.has(r.parameterOid)
                            ? { background: 'var(--ai-row-selected)' } : undefined}>
                        <td style={{ width: 36, textAlign: 'center' }}>
                          <input type="checkbox"
                                 checked={selectedIds.has(r.parameterOid)}
                                 disabled={!r.canReport}
                                 title={r.canReport ? undefined
                                   : 'Only samples at Pending Reporting or a later reporting stage can be reported'}
                                 onChange={() => toggleRow(r.parameterOid)} />
                        </td>
                        {cm.visibleColumns.map(c => cell[c.key])}
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={cm.visibleColumns.length + 1}
                            style={{ textAlign: 'center', color: 'var(--ai-muted)', padding: 20 }}>
                      No rows match the filter.
                    </td></tr>
                  )}
                </tbody>
              </table>
            )}
            {/* Header right-click menu (Sort + column management) + Column Chooser
                popup — both portal to body; rendered once. */}
            {headerMenu}
            {cm.chooser}
          </div>

          {/* Active-filter strip (XAF filter panel) — only while a column is filtered. */}
          <GridFilterBar
            filters={colF}
            labels={Object.fromEntries(cm.visibleColumns.map(c => [c.key, c.label]))}
            enabled={filterOn}
            onToggle={setFilterOn}
            onClear={() => { setColF({}); setFilterOn(true); }} />

          {/* Footer (step 1 · combined Retrieve) — SAME two-bar design as
              the Select-jobs list, but here BACK is active and returns to
              the list (selection intact).  This is the working Back the
              user asked for after clicking Next. Kept when a filter empties the
              grid, so Back is never lost. */}
          {!loading && rows.length > 0 && (
            <CustomReportFlowFooter
              countLabel={`${jobs.length.toLocaleString()} Job${jobs.length === 1 ? '' : 's'} · ${filtered.length.toLocaleString()} Row${filtered.length === 1 ? '' : 's'}`}
              backDisabled={false}
              onBack={onBack}
              showNext={false}
            />
          )}
        </div>
      </div>

      {/* ── Report Package popup — same chrome as per-job detail page;
          its Preview button fans out the /render-devx call to every
          selected JobID and opens each PDF in a new tab. */}
      {pkgPopupOpen && selectedPackage && (
        <MultiJobPackagePopup
          jobs={jobs}
          packageOid={selectedPackage}
          packageName={packages.find(pp => pp.oid === selectedPackage)?.packageName ?? 'Report'}
          selectedParameterOids={Array.from(selectedIds)}
          onClose={() => setPkgPopupOpen(false)}
          onError={msg => notify.error(msg)}
          onGenerated={() => setReportGenerated(true)}
          amended={pkgAmended}
          amendedNote={pkgAmendedNote}
          onAmendedChange={setPkgAmended}
          onAmendedNoteChange={setPkgAmendedNote}
          onReportInfoChange={setPkgReportInfo}
        />
      )}

      {/* ── Multi-job popups — fan out to all selected JobIDs ─────── */}
      {actionOpen === 'case' && (
        <MultiJobActionPopup
          title="Case Narrative"
          jobs={jobs}
          onClose={() => setActionOpen(null)}
          endpointTemplate="/api/v1/reporting/custom/{jobOid}/case-narrative"
          editRowEndpointBase="/api/v1/reporting/custom/notes"
          editFormName="Case Narrative"
          reviewTick
          columns={[
            { key: 'sourceId',    label: 'SourceID',    width: 110 },
            { key: 'nameSource',  label: 'NameSource',  width: 130 },
            { key: 'title',       label: 'Title',       width: 160 },
            { key: 'plainText',   label: 'Note',        width: 360,
              fmt: (_, r) => String((r as Record<string, unknown>).plainText ?? (r as Record<string, unknown>).text ?? '') },
            { key: 'sort',        label: 'Sort',        width:  60 },
            { key: 'projectName', label: 'ProjectName', width: 180 },
            // ALPACA Case Narrative also shows DateReviewed + ReviewedBy (stamped
            // when a note is ticked as Case Narrative) — from the existing
            // dbo.Notes.CaseNarrativeDateReviewed / CaseNarrativeReviewedBy.
            // DateReviewed renders date-only (MM/DD/YYYY) to match ALPACA.
            { key: 'caseNarrativeDateReviewed', label: 'DateReviewed', width: 120,
              fmt: v => (v ? (fmtLocalDate(String(v)) || '') : '') },
            { key: 'caseNarrativeReviewedBy',   label: 'ReviewedBy',   width: 110 },
          ]}
        />
      )}
      {actionOpen === 'notes' && (
        <MultiJobActionPopup
          title="Project Notes"
          jobs={jobs}
          onClose={() => { setActionOpen(null); void loadProjectNotesCount(); }}
          endpointTemplate="/api/v1/reporting/custom/{jobOid}/project-notes"
          editRowEndpointBase="/api/v1/reporting/custom/notes"
          editFormName="Project Notes"
          canAdd
          addEndpointTemplate="/api/v1/reporting/custom/{jobOid}/project-notes"
          columns={[
            { key: 'title',       label: 'Title',          width: 160 },
            { key: 'nameSource',  label: 'BusinessObject', width: 130 },
            { key: 'sourceId',    label: 'SourceID',       width: 110 },
            { key: 'plainText',   label: 'Note',           width: 380,
              fmt: (_, r) => String((r as Record<string, unknown>).plainText ?? (r as Record<string, unknown>).text ?? '') },
            { key: 'createdBy',   label: 'Author',         width:  90 },
            { key: 'createdDate', label: 'Date',           width: 140,
              // Shared helper (§14b.6): local tz, 24-hour, no AM/PM, force-UTC inside.
              fmt: v => (v ? (fmtLocal(String(v)) || String(v)) : '') },
          ]}
        />
      )}
      {actionOpen === 'allparams' && (
        // ALPACA parity: All Param Results is now an EDITABLE analyte picker
        // (check = reports, uncheck = excluded).  The query + save are keyed by
        // ParameterOid (cross-job in one call), so the first job's Oid is used
        // only for the audit path.  Unknown OIDs are simply skipped server-side.
        <AllParamsPopup
          title="Report Parameters"
          fetchEndpoint={`/api/v1/reporting/custom/${encodeURIComponent(jobs[0]?.jobOid ?? '')}/all-params`}
          fetchBody={Array.from(selectedIds)}
          saveEndpoint={`/api/v1/reporting/custom/${encodeURIComponent(jobs[0]?.jobOid ?? '')}/notparamreport`}
          onClose={() => setActionOpen(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MultiJobActionPopup — fans out the same endpoint to every selected
// JobID in parallel (Promise.allSettled), merges the rows, and renders
// them in a single themed popup.  Each merged row is decorated with
// `_jobId` (the JobID it came from) so users can see at a glance which
// rows belong to which job.  Failed jobs surface in the footer rather
// than blanking the whole grid.
// ─────────────────────────────────────────────────────────────────────
interface MultiJobColDef {
  key: string;
  label: string;
  width?: number;
  fmt?: (v: unknown, row: Record<string, unknown>) => string;
}

function MultiJobActionPopup(props: {
  title: string;
  jobs: Array<{ jobOid: string; jobId: string }>;
  onClose: () => void;
  /** "/api/v1/reporting/custom/{jobOid}/case-narrative" — {jobOid} is
   *  substituted per job. */
  endpointTemplate: string;
  columns: MultiJobColDef[];
  fetchMethod?: 'GET' | 'POST';
  fetchBody?: unknown;
  /** Click-to-edit (ITEM 3): when set, double-clicking a note row whose
   *  server CanEdit flag is true opens the inline note editor (Title / Sort /
   *  Note), which PUTs to `{editRowEndpointBase}/{noteOid}`.  The API is the
   *  authoritative author-only gate (403 for non-authors) — this just mirrors
   *  it in the UI, identical to the single-job ActionPopup. */
  editRowEndpointBase?: string;
  /** Audit form context for the edit — "Case Narrative" (open edit, anyone) or
   *  "Project Notes" (author-only).  Sent on the PUT + attachment upload. */
  editFormName?: string;
  /** Project Notes: show a "New" button + add form (ALPACA parity).  POSTs
   *  {title,text} to addEndpointTemplate with the chosen JobID substituted. */
  canAdd?: boolean;
  addEndpointTemplate?: string;   // "/api/v1/reporting/custom/{jobOid}/project-notes"
  /** Case Narrative: show a leading review checkbox + OK/Cancel footer.  Ticking
   *  a row auto-fills DateReviewed/ReviewedBy (cleared on un-tick); OK PUTs the
   *  ticked + cleared Oids per job to `endpointTemplate` (ALPACA parity). */
  reviewTick?: boolean;
}) {
  const { title, jobs, onClose, endpointTemplate, columns,
          fetchMethod = 'GET', fetchBody, editRowEndpointBase, editFormName,
          canAdd, addEndpointTemplate, reviewTick } = props;
  const notify = useNotify();
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [savingReview, setSavingReview] = useState(false);
  // Reviewer name for the transient auto-fill (the server stamps the Employee
  // on OK; the real code shows after re-open).  Non-reactive read is fine here.
  const reviewerName = useAuthStore.getState().user?.name ?? '(you)';

  // Theme — chrome (title bar, accent) follows the active Login theme.
  // Reuses the same hook as the per-job popup so multi-job and single-job
  // popups look identical apart from the JobID column.
  type AppTheme = { accent: string; featureBg: string };
  const [theme, setTheme] = useState<AppTheme>(() => {
    const root = document.documentElement;
    const s = getComputedStyle(root);
    return {
      accent:    s.getPropertyValue('--brand-accent').trim()     || '#F97316', /* token-exempt: JS fallback of --brand-accent */
      featureBg: s.getPropertyValue('--brand-feature-bg').trim() || '#FFF7ED', /* token-exempt: JS fallback of --brand-feature-bg */
    };
  });
  useEffect(() => {
    const obs = new MutationObserver(() => {
      const s = getComputedStyle(document.documentElement);
      setTheme({
        accent:    s.getPropertyValue('--brand-accent').trim()     || '#F97316',
        featureBg: s.getPropertyValue('--brand-feature-bg').trim() || '#FFF7ED',
      });
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    return () => obs.disconnect();
  }, []);

  const [rows,    setRows]    = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [errors,  setErrors]  = useState<string[]>([]);

  // Stable key for fetch dep — comma-joined sorted jobOids.
  const jobsKey = jobs.map(j => j.jobOid).join(',');

  // Fan-out fetch, extracted to a callback so the inline note editor (ITEM 3)
  // can refresh the merged grid after a successful PUT.
  const reload = useCallback(async () => {
    setLoading(true);
    setErrors([]);
    if (jobs.length === 0) { setRows([]); setLoading(false); return; }
    const results = await Promise.allSettled(jobs.map(j => {
      const url = endpointTemplate.replace('{jobOid}', encodeURIComponent(j.jobOid));
      return fetchMethod === 'POST'
        ? api.post(url, fetchBody ?? [])
        : api.get(url);
    }));
    const all: Record<string, unknown>[] = [];
    const failures: string[] = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        // Decorate every row with the JobID it came from so the user can tell
        // which rows belong to which job.  If the row already carries `jobId`
        // (All Params does), keep that.  Always also stash `_fromJobId` for sort.
        const data = (r.value.data as { data?: Record<string, unknown>[] }).data ?? [];
        for (const row of data) all.push({ ...row, _fromJobId: jobs[i]?.jobId ?? '', _fromJobOid: jobs[i]?.jobOid ?? '' });
      } else {
        const msg = (r.reason as { response?: { data?: { error?: { message?: string } } };
                                   message?: string })
          ?.response?.data?.error?.message
          ?? (r.reason as { message?: string })?.message
          ?? 'request failed';
        failures.push((jobs[i]?.jobId ?? '?') + ': ' + msg);
      }
    });
    setRows(all);
    setErrors(failures);
    // Case Narrative: seed the ticked set from the notes already marked reviewed.
    if (reviewTick) {
      const t = new Set<string>();
      for (const row of all) {
        const v = row.isCaseNarrative;
        if (v === true || v === 1 || v === '1') t.add(String(row.oid));
      }
      setTicked(t);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsKey, endpointTemplate, fetchMethod, JSON.stringify(fetchBody)]);
  useEffect(() => { void reload(); }, [reload]);

  // Case Narrative OK — reconcile the review state per job: ticked Oids get
  // stamped (IsCaseNarrative + reviewer + DateReviewed), un-ticked get cleared.
  // ALSO the single commit point for any staged Sort edits (dirtySortOids) —
  // ALPACA parity: nothing is written to the DB until OK (see commitSort above).
  const onOkReview = async () => {
    setSavingReview(true);
    try {
      const byJob = new Map<string, { ticked: string[]; cleared: string[] }>();
      for (const r of rows) {
        const jobOid = String(r._fromJobOid ?? '');
        const oid    = String(r.oid ?? '');
        if (!jobOid || !oid) continue;
        const g = byJob.get(jobOid) ?? { ticked: [], cleared: [] };
        (ticked.has(oid) ? g.ticked : g.cleared).push(oid);
        byJob.set(jobOid, g);
      }
      const reviewPuts = Array.from(byJob.entries()).map(([jobOid, g]) =>
        api.put(endpointTemplate.replace('{jobOid}', encodeURIComponent(jobOid)),
                { ticked: g.ticked, cleared: g.cleared }));
      const sortPuts = editRowEndpointBase
        ? Array.from(dirtySortOids)
            .map(oid => rows.find(r => String(r.oid) === oid))
            .filter((r): r is Record<string, unknown> => r != null)
            .map(r => api.put(`${editRowEndpointBase}/${encodeURIComponent(String(r.oid))}`, {
              title:     String(r.title ?? ''),
              plainText: String(r.plainText ?? r.text ?? ''),
              sort:      Number(r.sort ?? 0),
              form:      editFormName,
            }))
        : [];
      await runLoading(
        Promise.all([...reviewPuts, ...sortPuts]),
        { blocking: true, label: 'Saving…' });
      notify.success('Saved successfully.');
      setDirtySortOids(new Set());
      onClose();
    } catch {
      notify.error('Could not save the Case Narrative review.');
    } finally {
      setSavingReview(false);
    }
  };

  // ── Author-gated inline note editor (ITEM 3) ── mirrors the single-job
  // ActionPopup: double-click an editable note row → edit Title / Sort / Note,
  // PUT to {editRowEndpointBase}/{noteOid}.  The server is the authoritative
  // author-only gate (403 → red banner); the row's CanEdit flag mirrors it.
  const [authErr,   setAuthErr]   = useState<string | null>(null);
  const [editing,   setEditing]   = useState<Record<string, unknown> | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editSort,  setEditSort]  = useState('0');
  const [editText,  setEditText]  = useState('');
  const [editBusy,  setEditBusy]  = useState(false);
  const [editFile,       setEditFile]       = useState<File | null>(null);
  const [editAttachName, setEditAttachName] = useState<string | null>(null);

  // ── Inline Sort editing (ALPACA parity, 2026-07-16 fix, corrected 2026-07-16) —
  // the Case Narrative grid's Sort column was static plain text; legacy ALPACA lets
  // you type a new number directly in the grid cell. ALPACA parity CONFIRMED from
  // source (ReportingViewController.cs): CaseNarrative_Execute sets
  // `dc.SaveOnAccept = false` and wires `dc.Accepting += CaseDC_Accepting` — a grid
  // edit (ASPxGridListEditor.UpdateEdit(), line ~1005) only updates the in-memory
  // ObjectSpace-tracked Notes object; CaseDC_Accepting is the ONLY place that calls
  // ObjectSpace.CommitChanges() (line ~6840), and it only runs when the dialog's OK
  // is accepted. Closing/cancelling discards every uncommitted edit. So a Sort edit
  // (like a checkbox tick) is staged in memory and persisted ONLY on OK — never on
  // blur. sortLocal holds the in-progress typed text per row oid (so typing doesn't
  // fight the displayed value); dirtySortOids tracks which rows have an uncommitted
  // Sort change, PUT together with the ticked/cleared review state in onOkReview's
  // single bulk save — nothing is written to the DB until OK is clicked.
  const [sortLocal,     setSortLocal]     = useState<Record<string, string>>({});
  const [dirtySortOids, setDirtySortOids] = useState<Set<string>>(new Set());
  const commitSort = (r: Record<string, unknown>, raw: string) => {
    const oid = String(r.oid ?? '');
    if (!editRowEndpointBase || !oid) return;
    setSortLocal(prev => { const n = { ...prev }; delete n[oid]; return n; });
    const parsed = parseInt(raw, 10);
    const newSort = Number.isFinite(parsed) ? parsed : 0;
    if (newSort === Number(r.sort ?? 0)) return;   // no actual change — nothing to stage
    setDirtySortOids(prev => new Set(prev).add(oid));
    // Update the row LOCALLY and re-sort (matching the backend's own
    // ORDER BY ISNULL(Sort,0)) so the grid reflects the new order immediately —
    // purely in-memory, same as ALPACA's ObjectSpace-tracked (uncommitted) edit.
    setRows(prev => prev
      .map(row => String(row.oid) === oid ? { ...row, sort: newSort } : row)
      .sort((a, b) => Number(a.sort ?? 0) - Number(b.sort ?? 0)));
  };

  const openEdit = (r: Record<string, unknown>) => {
    if (!editRowEndpointBase) return;
    if (r.canEdit === false) {
      setAuthErr('You are not authorized to modify a note created by another author.');
      return;
    }
    setEditing(r);
    setEditTitle(String(r.title ?? ''));
    setEditSort(String(r.sort ?? 0));
    setEditText(String(r.plainText ?? r.text ?? ''));
    setEditFile(null);
    setEditAttachName(r.attachmentName ? String(r.attachmentName) : null);
  };
  const downloadAttachment = async (noteOid: string, name: string) => {
    if (!editRowEndpointBase) return;
    try {
      const res = await api.get(
        `${editRowEndpointBase}/${encodeURIComponent(noteOid)}/attachment`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch { /* surfaced via the edit dialog's error path on next action */ }
  };
  const saveEdit = async () => {
    if (!editing || !editRowEndpointBase) return;
    setEditBusy(true); setAuthErr(null);
    try {
      await api.put(`${editRowEndpointBase}/${encodeURIComponent(String(editing.oid))}`, {
        title: editTitle, plainText: editText, sort: parseInt(editSort, 10) || 0,
        form: editFormName,
      });
      if (editFile) {
        const fd = new FormData();
        fd.append('file', editFile);
        await api.post(
          `${editRowEndpointBase}/${encodeURIComponent(String(editing.oid))}/attachment` +
            (editFormName ? `?form=${encodeURIComponent(editFormName)}` : ''),
          fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setEditing(null);
      await reload();
    } catch (e: unknown) {
      const er = e as { response?: { status?: number; data?: { error?: { message?: string } } } };
      const msg = er.response?.data?.error?.message ?? 'Request failed.';
      setAuthErr(msg);
      if (er.response?.status === 403) setEditing(null);  // show banner against the list
    } finally {
      setEditBusy(false);
    }
  };

  // ── Add a new project note (ALPACA "New") ── posts to the chosen job; when
  // a single job is selected it is the implicit target, otherwise the user
  // picks the JobID in the form.
  const [showAdd,  setShowAdd]  = useState(false);
  const [addTitle, setAddTitle] = useState('');
  const [addText,  setAddText]  = useState('');
  const [addJobOid, setAddJobOid] = useState('');
  const [addBusy,  setAddBusy]  = useState(false);
  useEffect(() => { setAddJobOid(jobs[0]?.jobOid ?? ''); }, [jobsKey]);  // eslint-disable-line react-hooks/exhaustive-deps
  const saveAdd = async () => {
    // Target the chosen job, or fall back to the first selected job (single-job
    // case, where no picker is shown and the effect-set state may not be ready).
    const targetJob = addJobOid || jobs[0]?.jobOid || '';
    if (!addEndpointTemplate || !addText.trim() || !targetJob) return;
    setAddBusy(true); setAuthErr(null);
    try {
      await api.post(addEndpointTemplate.replace('{jobOid}', encodeURIComponent(targetJob)),
        { title: addTitle, text: addText });
      setAddTitle(''); setAddText(''); setShowAdd(false);
      await reload();
    } catch (e: unknown) {
      const er = e as { response?: { data?: { error?: { message?: string } } } };
      setAuthErr(er.response?.data?.error?.message ?? 'Failed to add note.');
    } finally {
      setAddBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      columns.some(c => {
        const v = c.fmt ? c.fmt(r[c.key], r) : String(r[c.key] ?? '');
        return v.toLowerCase().includes(t);
      }) || String(r._fromJobId ?? '').toLowerCase().includes(t)
    );
  }, [rows, search, columns]);

  // Always prepend a "JobID" column showing the source job, except when
  // the data already has a `jobId` column (All Params does).
  const showFromJobCol = !columns.some(c => c.key === 'jobId');

  return (
    <div onClick={onClose}
         style={{
           position: 'fixed', inset: 0, zIndex: 4000,
           background: 'rgba(0,0,0,.35)',
           display: 'flex', alignItems: 'center', justifyContent: 'center',
         }}>
      <div onClick={e => e.stopPropagation()}
           style={{
             width: 1200, maxWidth: '95vw', maxHeight: '90vh',
             background: '#fff', borderRadius: 4,
             boxShadow: '0 6px 24px rgba(0,0,0,.25)',
             display: 'flex', flexDirection: 'column', overflow: 'hidden',
           }}>
        {/* Title bar — themed */}
        <div style={{
          padding: '8px 14px', background: theme.accent, color: '#fff',
          fontSize: 13, fontWeight: 600, display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>
            {title}
            &nbsp;—&nbsp;
            <span style={{ opacity: .85, fontWeight: 500 }}>
              {jobs.length} JobID{jobs.length === 1 ? '' : 's'}:&nbsp;
              {jobs.map(j => j.jobId).join(', ')}
            </span>
          </span>
          <button onClick={onClose}
                  style={{ background: 'transparent', border: 'none',
                           color: '#fff', fontSize: 18, cursor: 'pointer',
                           lineHeight: 1 }}
                  title="Close">×</button>
        </div>

        {/* Sub-toolbar — New (Project Notes) + search + row count */}
        <div style={{
          padding: '6px 12px', background: 'var(--ai-header-bg)',
          borderBottom: '1px solid var(--ai-line)',
          display: 'flex', gap: 8, alignItems: 'center',
        }}>
          {canAdd && (
            <button className="ca-btn ca-btn-solid"
                    onClick={() => setShowAdd(s => !s)}
                    title="Add a new project note"
                    style={{ padding: '3px 10px', fontSize: 11 }}>
              <i className="ti ti-plus" /> New
            </button>
          )}
          <div className="list-search" style={{ width: 240 }}>
            <span className="tb-i">🔍</span>
            <input type="text" value={search}
                   onChange={e => setSearch(e.target.value)}
                   placeholder="Search…" />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span style={{ fontSize: 11, color: 'var(--ai-ink)' }}>
            {filtered.length} of {rows.length} row{rows.length === 1 ? '' : 's'}
          </span>
          {editRowEndpointBase && (
            <span style={{ fontSize: 11, color: 'var(--ai-muted)', marginLeft: 'auto' }}>
              Click the <i className="ti ti-edit" /> to edit a note you authored
            </span>
          )}
        </div>

        {/* New-note form (ALPACA "New") — Title + Note, with a JobID picker when
            more than one job is selected (single job = implicit target). */}
        {canAdd && showAdd && (
          <div style={{ padding: '10px 12px', background: '#fff', borderBottom: '1px solid var(--ai-line)',
                        display: 'flex', flexDirection: 'column', gap: 8 }}>
            {jobs.length > 1 && (
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ai-ink)',
                              display: 'flex', alignItems: 'center', gap: 6 }}>
                JobID
                <select value={addJobOid} onChange={e => setAddJobOid(e.target.value)}
                        style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--ai-line)', borderRadius: 3 }}>
                  {jobs.map(j => <option key={j.jobOid} value={j.jobOid}>{j.jobId}</option>)}
                </select>
              </label>
            )}
            <input type="text" placeholder="Title" value={addTitle}
                   onChange={e => setAddTitle(e.target.value)}
                   style={{ padding: '5px 8px', fontSize: 12, border: '1px solid var(--ai-line)', borderRadius: 3 }} />
            <textarea placeholder="Note" value={addText} rows={3}
                      onChange={e => setAddText(e.target.value)}
                      style={{ padding: '5px 8px', fontSize: 12, border: '1px solid var(--ai-line)',
                               borderRadius: 3, resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="ca-btn ca-btn-solid" disabled={addBusy || !addText.trim()}
                      onClick={saveAdd}>
                <BusyIcon busy={addBusy} icon="ti-device-floppy" /> Save Note
              </button>
              <button className="ca-btn"
                      onClick={() => { setShowAdd(false); setAddTitle(''); setAddText(''); }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Author-only banner (ALPACA parity) — shown when a non-author tries
            to edit, or the server returns 403. */}
        {authErr && (
          <div style={{ padding: '7px 12px', background: 'var(--ai-pill-alert-bg)',
                        borderBottom: '1px solid var(--ai-line)', color: 'var(--ai-red)',
                        fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="ti ti-alert-triangle" /> {authErr}
            <button onClick={() => setAuthErr(null)}
                    style={{ marginLeft: 'auto', background: 'transparent', border: 'none',
                             color: 'var(--ai-red)', cursor: 'pointer', fontSize: 15, lineHeight: 1 }}
                    title="Dismiss">×</button>
          </div>
        )}

        {/* Grid */}
        <div style={{ flex: 1, overflow: 'auto', background: '#fff' }}>
          {loading && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 12 }}>
              <LoaderSymbol size={14} /> Loading from {jobs.length} JobID{jobs.length === 1 ? '' : 's'}…
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 12 }}>
              <i className="ti ti-database-off" style={{ fontSize: 24, opacity: .35, display: 'block', marginBottom: 8 }} />
              No rows across the selected JobIDs.
            </div>
          )}
          {!loading && filtered.length > 0 && (
            // Each cell carries a right border so the grid reads as a
            // proper table (user feedback 2026-06-08).  Last column drops
            // its right border via :last-child wouldn't work inline, so we
            // suppress it on the rightmost cell explicitly with idx check.
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                {/* Standard grey grid header (BO UI Standard §9) — not blue. */}
                <tr style={{ background: 'var(--ai-header-bg)' }}>
                  {reviewTick && (
                    <th style={{ padding: '6px 8px', textAlign: 'center',
                                 borderBottom: '1px solid var(--ai-line)', borderRight: '1px solid var(--ai-line-soft)',
                                 fontWeight: 600, color: 'var(--ai-ink)', width: 40 }}>
                      {/* Select-all — ticks/clears every VISIBLE (filtered) row in one
                          click, ALPACA parity (2026-07-16 fix: this header used to be a
                          static "✓" label with no input at all — not clickable, no way
                          to bulk-select). */}
                      <input type="checkbox"
                             title="Select all rows"
                             checked={filtered.length > 0 && filtered.every(r => ticked.has(String(r.oid)))}
                             ref={el => { if (el) {
                               const allTicked = filtered.length > 0 && filtered.every(r => ticked.has(String(r.oid)));
                               el.indeterminate = !allTicked && filtered.some(r => ticked.has(String(r.oid)));
                             } }}
                             onChange={() => {
                               const allTicked = filtered.length > 0 && filtered.every(r => ticked.has(String(r.oid)));
                               setTicked(prev => {
                                 const n = new Set(prev);
                                 for (const r of filtered) {
                                   const id = String(r.oid);
                                   if (allTicked) n.delete(id); else n.add(id);
                                 }
                                 return n;
                               });
                             }}
                             style={{ cursor: 'pointer', accentColor: 'var(--brand-accent, var(--ai-orange))', width: 15, height: 15 }} />
                    </th>
                  )}
                  {showFromJobCol && (
                    <th style={{ padding: '6px 8px', textAlign: 'left',
                                 borderBottom: '1px solid var(--ai-line)', borderRight: '1px solid var(--ai-line-soft)',
                                 fontWeight: 600, color: 'var(--ai-ink)', width: 110 }}>
                      JobID
                    </th>
                  )}
                  {/* Edit column — inline pencil per row (Settings/CRUD-grid
                      affordance), replacing double-click-to-edit. */}
                  {editRowEndpointBase && (
                    <th style={{ padding: '6px 8px', textAlign: 'center',
                                 borderBottom: '1px solid var(--ai-line)', borderRight: '1px solid var(--ai-line-soft)',
                                 fontWeight: 600, color: 'var(--ai-ink)', width: 44 }}>Edit</th>
                  )}
                  {columns.map((c, idx) => (
                    <th key={c.key} style={{
                      padding: '6px 8px', textAlign: 'left',
                      borderBottom: '1px solid var(--ai-line)',
                      borderRight: idx === columns.length - 1 ? 'none' : '1px solid var(--ai-line-soft)',
                      fontWeight: 600, color: 'var(--ai-ink)', width: c.width,
                    }}>
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const mine = r.canEdit !== false;              // server CanEdit flag
                  return (
                  // Stable identity key (oid), not the array index — the row order
                  // changes when a Sort edit re-sorts `rows` (see commitSort); an
                  // index key would make React reuse each DOM row for whatever data
                  // now lands at that position instead of following the actual row,
                  // which is its own source of "the grid looks inconsistent" bugs.
                  <tr key={String(r.oid ?? i)}
                      style={{ borderBottom: '1px solid var(--ai-line-soft)' }}>
                    {reviewTick && (
                      <td style={{ padding: '5px 8px', textAlign: 'center', borderRight: '1px solid var(--ai-line-soft)' }}>
                        <input type="checkbox"
                               checked={ticked.has(String(r.oid))}
                               onChange={() => setTicked(prev => {
                                 const n = new Set(prev); const id = String(r.oid);
                                 if (n.has(id)) n.delete(id); else n.add(id);
                                 return n;
                               })}
                               style={{ cursor: 'pointer', accentColor: 'var(--brand-accent, var(--ai-orange))', width: 15, height: 15 }} />
                      </td>
                    )}
                    {showFromJobCol && (
                      <td style={{ padding: '5px 8px', color: 'var(--ai-blue)', fontWeight: 500,
                                   borderRight: '1px solid var(--ai-line-soft)' }}>
                        {String(r._fromJobId ?? '')}
                      </td>
                    )}
                    {/* Inline Edit pencil — click to edit (author-only); a
                        non-author sees a dash with the same explanation. */}
                    {editRowEndpointBase && (
                      <td style={{ padding: '5px 8px', textAlign: 'center', borderRight: '1px solid var(--ai-line-soft)' }}>
                        {mine ? (
                          <button title="Edit note" onClick={() => openEdit(r)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                                     color: 'var(--ai-icon-edit)', fontSize: 16, padding: '2px 4px' }}>
                            <i className="ti ti-edit" />
                          </button>
                        ) : (
                          <span title="Only the author can edit this note." style={{ color: 'var(--ai-muted)' }}>—</span>
                        )}
                      </td>
                    )}
                    {columns.map((c, idx) => {
                      let val = c.fmt ? c.fmt(r[c.key], r) : String(r[c.key] ?? '');
                      // Case Narrative: DateReviewed / ReviewedBy reflect the LIVE
                      // tick state — auto-filled when checked, blank when un-checked.
                      if (reviewTick && (c.key === 'caseNarrativeDateReviewed' || c.key === 'caseNarrativeReviewedBy')) {
                        if (!ticked.has(String(r.oid))) val = '';
                        else if (c.key === 'caseNarrativeDateReviewed')
                          val = r.caseNarrativeDateReviewed
                            ? (fmtLocalDate(String(r.caseNarrativeDateReviewed)) || '')
                            : (fmtLocalDate(new Date().toISOString()) || '');
                        else
                          val = String(r.caseNarrativeReviewedBy ?? reviewerName);
                      }
                      // Sort — inline-editable, ALPACA parity (2026-07-16 fix): a
                      // number input right in the grid cell, not a separate dialog.
                      if (reviewTick && c.key === 'sort' && editRowEndpointBase) {
                        const oid = String(r.oid ?? '');
                        return (
                          <td key={c.key} style={{ padding: '3px 6px',
                                                    borderRight: idx === columns.length - 1
                                                      ? 'none' : '1px solid var(--ai-line-soft)' }}>
                            <input type="number"
                                   value={sortLocal[oid] ?? String(r.sort ?? 0)}
                                   disabled={savingReview}
                                   onChange={e => setSortLocal(prev => ({ ...prev, [oid]: e.target.value }))}
                                   onBlur={e => commitSort(r, e.target.value)}
                                   onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                                   style={{ width: 50, padding: '2px 4px', fontSize: 12,
                                            border: '1px solid var(--ai-line)', borderRadius: 2 }} />
                          </td>
                        );
                      }
                      return (
                        <td key={c.key} style={{ padding: '5px 8px', color: 'var(--ai-ink)',
                                                  borderRight: idx === columns.length - 1
                                                    ? 'none' : '1px solid var(--ai-line-soft)' }}>
                          {val}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer — error chips per failed job (if any) */}
        {errors.length > 0 && (
          <div style={{
            padding: '6px 12px', background: 'var(--ai-pill-warn-bg)', borderTop: '1px solid var(--ai-line)',
            fontSize: 11, color: 'var(--ai-pill-warn-fg)',
          }}>
            <strong>Some JobIDs failed:</strong>&nbsp;{errors.join(' | ')}
          </div>
        )}

        {/* Case Narrative review footer — OK saves the tick state, Cancel discards. */}
        {reviewTick && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid var(--ai-line)',
                        display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="ca-btn" disabled={savingReview} onClick={onClose}>Cancel</button>
            <button className="ca-btn ca-btn-solid" disabled={savingReview} onClick={() => void onOkReview()}>
              <BusyIcon busy={savingReview} icon="ti-check" /> OK
            </button>
          </div>
        )}

        {/* Inline note editor (ITEM 3) — ALPACA "Sample Login - Note" parity.
            Title / Sort / Note are editable; Author + Date stay read-only.
            Save PUTs to {editRowEndpointBase}/{noteOid} (author-gated server-side). */}
        {editing && (
          <div onClick={() => !editBusy && setEditing(null)}
               style={{ position: 'fixed', inset: 0, zIndex: 4100,
                        background: 'rgba(0,0,0,.35)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center' }}>
            <div onClick={e => e.stopPropagation()}
                 style={{ width: 520, maxWidth: '92vw', background: '#fff',
                          borderRadius: 4, boxShadow: '0 6px 24px rgba(0,0,0,.3)',
                          display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '8px 14px', background: theme.accent, color: '#fff',
                            fontSize: 13, fontWeight: 600 }}>Edit Note</div>
              <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ai-ink)' }}>Title
                  <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)}
                         style={{ width: '100%', marginTop: 3, padding: '5px 8px', fontSize: 12,
                                  border: '1px solid var(--ai-line)', borderRadius: 3 }} />
                </label>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ai-ink)' }}>Sort
                  <input type="number" value={editSort} onChange={e => setEditSort(e.target.value)}
                         style={{ width: 120, marginTop: 3, padding: '5px 8px', fontSize: 12,
                                  border: '1px solid var(--ai-line)', borderRadius: 3, display: 'block' }} />
                </label>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ai-ink)' }}>Note
                  <textarea value={editText} onChange={e => setEditText(e.target.value)} rows={5}
                            style={{ width: '100%', marginTop: 3, padding: '5px 8px', fontSize: 12,
                                     border: '1px solid var(--ai-line)', borderRadius: 3, resize: 'vertical' }} />
                </label>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ai-ink)' }}>Attachment
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 3 }}>
                    <input type="file"
                           onChange={e => setEditFile(e.target.files?.[0] ?? null)}
                           style={{ flex: 1, fontSize: 12 }} />
                    {editAttachName && !editFile && (
                      <button type="button" className="ca-btn"
                              onClick={() => downloadAttachment(String(editing.oid), editAttachName)}
                              title="Download current attachment"
                              style={{ whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <i className="ti ti-download" /> {editAttachName}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ padding: '8px 14px', borderTop: '1px solid var(--ai-line)',
                            display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="ca-btn" disabled={editBusy} onClick={() => setEditing(null)}>Cancel</button>
                <button className="ca-btn ca-btn-solid" disabled={editBusy} onClick={saveEdit}>
                  <BusyIcon busy={editBusy} icon="ti-device-floppy" /> Save Note
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MultiJobPackagePopup — exact UI parity with the per-job detail page's
// Report Package popup (Reported Date · Approved By · Accreditation
// toggle · sub-report grid · Refresh/Export · OK/Cancel/Preview).
// Only difference: Preview FANS OUT one /render-devx call per selected
// JobID, opening each PDF in a new tab.  Per-row sub-report preview
// renders that sub-report for the FIRST selected JobID as a quick peek.
// ─────────────────────────────────────────────────────────────────────
interface PackageChildRow {
  oid: string;
  reportName: string | null;
  sort: number;
  showReportId: boolean;
}

/** Sentinel Oid of the synthetic "EDD - …" package popup row (must match
 *  ReportingReader.EddPackageItemOid on the server).  ALPACA appends this
 *  in-memory row when the selected job has an EDDTemplate — it is NOT a PDF
 *  sub-report; its Preview triggers the EDD Excel download (Process_EDD). */
const EDD_ITEM_OID = 'edd00000-0000-0000-0000-00000000edd0';
const isEddItemOid = (oid: string | undefined) =>
  (oid ?? '').toLowerCase() === EDD_ITEM_OID;

// DEMO (2026-06-20): the "Level IV Package" combo entry has no REPX sub-reports;
// its "sub-reports" ARE the report's own sections.  The popup lists these with
// checkboxes (oid = the backend section key) so the user can preview an INDIVIDUAL
// section (per-row) or the WHOLE package filtered to the checked sections.
// Level IV report list — the front-matter "Project Overview" reports (A&B-style
// layouts) plus the per-batch CLP forms and embedded raw data.  Each row is a
// previewable sub-report; an `oid` may carry several backend section keys
// (comma-separated), flattened in genLevel4.
const LEVEL4_SECTIONS: PackageChildRow[] = [
  { oid: 'cover',        reportName: 'Cover Page (General)',                     sort: 1,  showReportId: false },
  { oid: 'narrative',    reportName: 'Case Narrative',                          sort: 2,  showReportId: false },
  { oid: 'qualifiers',   reportName: 'Term & Qualifier Definitions',            sort: 3,  showReportId: false },
  { oid: 'certification',reportName: 'Certification',                           sort: 4,  showReportId: false },
  { oid: 'hitreport',    reportName: 'Hit Report',                              sort: 5,  showReportId: false },
  { oid: 'results',      reportName: 'Analytical Results — Samples',            sort: 6,  showReportId: false },
  { oid: 'resultsqc',    reportName: 'Analytical Results — QC',                 sort: 7,  showReportId: false },
  { oid: 'reagent',      reportName: 'Reagent Report',                          sort: 8,  showReportId: false },
  { oid: 'dcssummary',   reportName: 'DCS Summary',                             sort: 9,  showReportId: false },
  { oid: 'resultdcs',    reportName: 'Result DCS',                              sort: 10, showReportId: false },
  { oid: 'scc',          reportName: 'Sample Condition Checklist',              sort: 11, showReportId: false },
  { oid: 'dpcover',      reportName: 'Data Package Cover Page',                 sort: 12, showReportId: false },
  { oid: 'labreview',    reportName: 'Laboratory Review Checklist',            sort: 13, showReportId: false },
  { oid: 'form1,form2c,form4,form3lcs,form3ms,form7ical,form7ccv',
                         reportName: 'CLP Forms 1–7 (Inorganic & Organic, all batches)', sort: 14, showReportId: false },
  { oid: 'rawdata',      reportName: 'Raw Data Files (instrument PDFs)',        sort: 15, showReportId: false },
];

function MultiJobPackagePopup(props: {
  jobs: Array<{ jobOid: string; jobId: string }>;
  packageOid: string;
  packageName: string;
  selectedParameterOids: string[];
  onClose: () => void;
  onError: (msg: string) => void;
  /** Fired after a successful whole-package render — unlocks the parent's
   *  Save button (the report has been generated, so it can now be committed). */
  onGenerated?: () => void;
  amended: boolean;
  amendedNote: string;
  onAmendedChange: (v: boolean) => void;
  onAmendedNoteChange: (v: string) => void;
  /** When set, the footer OK button PERSISTS via this callback (and reads
   *  "Save") instead of just closing — used by Edit Report's "update existing
   *  report id" path. When omitted, OK simply closes (preview / Custom Reporting). */
  onOk?: () => void | Promise<void>;
  okBusy?: boolean;
  /** Reports the dialog's Reported Date / Approved By whenever the user changes them
   *  (only user-set values) — the save stamps them like G5 (owner 2026-10-17). */
  onReportInfoChange?: (info: PackageReportInfo) => void;
}) {
  const { jobs, packageOid, packageName, selectedParameterOids, onClose, onError, onGenerated,
          amended, amendedNote, onAmendedChange, onAmendedNoteChange, onOk, okBusy,
          onReportInfoChange } = props;
  // DEMO (2026-06-20): the "Level IV Package" combo entry generates the full
  // Level IV report via the demo endpoint instead of the REPX sub-report render
  // pipeline (it is one server-assembled PDF — forms + QC + real raw data +
  // bookmarks — not a set of sub-reports).
  const isLevel4 = packageName === 'Level IV Package';

  // Theme — title bar accent follows the active Login theme.
  type AppTheme = { accent: string; featureBg: string };
  const [theme, setTheme] = useState<AppTheme>(() => {
    const s = getComputedStyle(document.documentElement);
    return {
      accent:    s.getPropertyValue('--brand-accent').trim()     || '#F97316', /* token-exempt: JS fallback of --brand-accent */
      featureBg: s.getPropertyValue('--brand-feature-bg').trim() || '#FFF7ED', /* token-exempt: JS fallback of --brand-feature-bg */
    };
  });
  useEffect(() => {
    const obs = new MutationObserver(() => {
      const s = getComputedStyle(document.documentElement);
      setTheme({
        accent:    s.getPropertyValue('--brand-accent').trim()     || '#F97316',
        featureBg: s.getPropertyValue('--brand-feature-bg').trim() || '#FFF7ED',
      });
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    return () => obs.disconnect();
  }, []);

  // Sub-report children for this package.
  const [children, setChildren] = useState<PackageChildRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading,  setLoading]  = useState(true);
  // Form fields — match ALPACA (read by the renderer via headers).
  const [reportedDate, setReportedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const reportedDateTouched = useRef(false);
  const [approvedBy,   setApprovedBy]   = useState('');
  const [accreditOn,   setAccreditOn]   = useState(true);
  const [accreditStd,  setAccreditStd]  = useState('');
  const [dlgEmployees, setDlgEmployees] = useState<{ oid: string; name: string }[]>([]);
  const [dlgAccredits, setDlgAccredits] = useState<{ oid: string; name: string; isDefault: boolean }[]>([]);
  const toggleAmended = () => {
    if (!amended && !amendedNote) {
      const d = new Date();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      onAmendedNoteChange(`Amended report 2 on ${mm}/${dd}/${d.getFullYear()}`);
    }
    onAmendedChange(!amended);
  };
  // Per-sub-report page counts (key = reportName).  Each completed
  // /render-devx response carries an X-SubReport-Pages JSON header.
  const [pageCounts, setPageCounts] = useState<Record<string, number>>({});
  // Render state — `rendering` shows the ReportProgressOverlay over the
  // popup (Tier-1 deterministic progress bar — same component MOR Adhoc
  // + the per-job package popup use, so the look-and-feel is identical).
  // Render failures surface through the shared notifier popup (via onError →
  // notify), which sits above the modal — no in-popup error banner needed.
  const [rendering, setRendering] = useState(false);
  // Which single sub-report row is currently previewing (drives the per-row
  // 📑 → spinner swap, user request 2026-07-04).  null = whole-package preview.
  const [previewingOid, setPreviewingOid] = useState<string | null>(null);
  // Stop button on the progress overlay genuinely aborts the in-flight
  // render request (prototype fix #3, 2026-07-03) — never just hides it.
  const renderAbortRef = useRef<AbortController | null>(null);

  // Load the package's sub-report children once.  For the DEMO "Level IV
  // Package" the sub-reports are the report's own sections, so we populate
  // them locally (no report-package-children call) and DON'T auto-generate.
  useEffect(() => {
    if (isLevel4) {
      setChildren(LEVEL4_SECTIONS);
      setSelected(new Set(LEVEL4_SECTIONS.map(c => c.oid)));   // all ticked by default
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    // jobOid lets the server append ALPACA's dynamic "EDD - <EDDName>" row when
    // the (first) selected job has an EDD template (ReportingViewController.cs
    // 226-234).  Without it the popup shows one row too few (no EDD).
    api.get('/api/v1/reporting/custom/report-package-children',
      { params: { packageOid, jobOid: jobs[0]?.jobOid } })
      .then(res => {
        if (!alive) return;
        const data = (res.data as { data?: PackageChildRow[] }).data ?? [];
        setChildren(data);
        // Tick every child by default (matches ALPACA).
        setSelected(new Set(data.map(c => c.oid)));
      })
      .catch(e => {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message ?? 'Failed to load sub-reports.';
        onError(msg);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [packageOid, jobs, onError, isLevel4]);

  // Load Approved By (employees) + Accreditation options from DB once, and
  // auto-select the logged-in user in Approved By (ALPACA LIMS parity).
  useEffect(() => {
    let alive = true;
    api.get('/api/v1/reporting/custom/dialog-lookups')
      .then(res => {
        if (!alive) return;
        const payload = res.data as { data?: { employees: { oid: string; name: string }[]; accreditations: { oid: string; name: string; isDefault: boolean }[] }; currentEmployeeOid?: string };
        const ld = payload.data;
        if (ld) {
          setDlgEmployees(ld.employees);
          setDlgAccredits(ld.accreditations);
          const def = ld.accreditations.find(a => a.isDefault);
          if (def) setAccreditStd(def.name ?? '');
          // currentEmployeeOid (user_id claim == Employee.Oid) → set the NAME,
          // because the options use value={e.name}.
          const meOid = payload.currentEmployeeOid ?? '';
          if (meOid) {
            const match = ld.employees.find(e => e.oid.toLowerCase() === meOid.toLowerCase());
            if (match) setApprovedBy(match.name ?? '');
          }
        }
      })
      .catch(() => { /* non-fatal — dropdowns just stay on "-- Select --" */ });
    return () => { alive = false; };
  }, []);

  const toggleChild = (oid: string) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(oid)) n.delete(oid); else n.add(oid);
      return n;
    });
  };

  // EDD PREVIEW download (ALPACA Process_EDD, save=false) — generates the EDD
  // for the FIRST selected job on the fly (no DB persist) and triggers the
  // browser save dialog.  A 204 (job has no EDD / format NA) is a silent no-op.
  const downloadEddPreview = useCallback(async () => {
    const j0 = jobs[0];
    if (!j0) return;
    const res = await api.get(
      `/api/v1/reporting/custom/${encodeURIComponent(j0.jobOid)}/edd-preview`,
      { responseType: 'blob', validateStatus: s => s === 200 || s === 204 });
    const blob = res.data as Blob | undefined;
    if (res.status === 204 || !blob || blob.size === 0) return;
    const cd = (res.headers['content-disposition'] as string | undefined) ?? '';
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
    const fileName = m && m[1] ? decodeURIComponent(m[1]) : 'EDD.xlsx';
    const url = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove();
    } finally {
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }, [jobs]);

  // Whole-package render — ONE /render-devx-multi call, one merged PDF
  // containing every selected JobID's data.  Server renders each job
  // sequentially with the package's REPX layout, then PdfSharp
  // concatenates the page streams into a single PDF.  Matches the
  // per-job flow exactly: progress bar over popup, then PDF opens in
  // a new tab, then #Pages column populates.
  const onPreviewMulti = useCallback(async () => {
    if (selected.size === 0) {
      onError('Tick at least one sub-report first.');
      return;
    }
    if (jobs.length === 0) return;
    setRendering(true);
    renderAbortRef.current?.abort();
    const ac = new AbortController();
    renderAbortRef.current = ac;
    try {
      // EXCLUDE the synthetic "EDD - " row — it's NOT a PDF sub-report; it
      // triggers the EDD Excel download instead (ALPACA ReportingViewController
      // line 1927 filters "EDD - " out of the PDF render list).
      const eddSelected = Array.from(selected).some(isEddItemOid);
      const selectedSubReportOids = Array.from(selected).filter(oid => !isEddItemOid(oid));
      const res = await api.post(
        '/api/v1/reporting/custom/render-devx-multi',
        {
          jobOids: jobs.map(j => j.jobOid),
          selectedParameterOids,
          // Render ONLY the sub-reports ticked in the popup (the checked package
          // child Oids) — generate based on the checkbox selection.
          selectedSubReportOids,
          amendedNote: amended && amendedNote ? amendedNote : null,
        },
        // deliver=token → open a real, re-fetchable preview URL rather than a
        // blob: URL that dies before the PDF viewer's Download can use it
        // (see utils/reportPreview).
        { params: { packageOid, ...PREVIEW_AS_TOKEN }, signal: ac.signal });

      // Aggregate per-sub-report page counts (summed across jobs by the
      // server) → populate the #Pages column + footer total.
      const pagesHdr = res.headers['x-subreport-pages'] as string | undefined;
      if (pagesHdr) {
        try {
          const arr = JSON.parse(pagesHdr) as Array<{ name: string; pages: number; skipped: boolean }>;
          const map: Record<string, number> = {};
          for (const e of arr) map[e.name] = e.pages;
          setPageCounts(map);
        } catch { /* malformed — ignore */ }
      }

      // Open the SINGLE merged PDF in a new tab (matches per-job flow).
      if (!openReportPreview(res.data))
        onError('Browser blocked the PDF popup — allow popups for this site.');
      // Report generated → tell the parent to unlock its Save button.
      onGenerated?.();

      // ALPACA parity: previewing a package that contains the "EDD - " item
      // ALSO downloads the EDD Excel (Process_EDD → Download_Execute, no DB
      // persist).  Fire it after the PDF opens — best-effort, never blocks.
      if (eddSelected) {
        try { await downloadEddPreview(); }
        catch { onError('The report previewed, but the EDD could not be generated for download.'); }
      }
    } catch (e: unknown) {
      if (ac.signal.aborted) return;   // user clicked Stop — no error banner
      // The response is plain JSON now (no responseType:'blob'), so axios has
      // already parsed the error envelope for us.
      const err = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data;
      // Surface via the shared notifier popup (renders above the modal).
      onError(err?.error?.message ?? (e as { message?: string })?.message ?? 'render failed');
    } finally {
      setRendering(false);
    }
  }, [selected, jobs, packageOid, selectedParameterOids, onError, onGenerated, downloadEddPreview]);

  // DEMO Level IV — generate the full package PDF (server assembles forms + QC +
  // real raw data + bookmarks) and open it in a new tab.  Reuses the SAME
  // progress bar (ReportProgressOverlay over `rendering`) as the normal render.
  const genLevel4 = useCallback(async (sectionKeys: string[]) => {
    const j0 = jobs[0];
    if (!j0) return;
    // A row's oid may bundle several backend keys (e.g. the CLP-forms row) —
    // flatten on the comma so the server receives the real section keys.
    const sections = sectionKeys.flatMap(k => k.split(',')).map(s => s.trim()).filter(Boolean);
    setRendering(true);
    try {
      renderAbortRef.current?.abort();
      const ac4 = new AbortController();
      renderAbortRef.current = ac4;
      const res = await api.post(
        `/api/v1/demo/level4/${encodeURIComponent(j0.jobOid)}/generate`,
        { sections },
        { responseType: 'blob', signal: ac4.signal });

      // Server returns per-section page counts (by backend key) → fill the
      // #Pages column for each report row + the footer total.  Merge so a
      // single-row preview only updates that row, leaving the rest intact.
      const pagesHdr = res.headers['x-subreport-pages'] as string | undefined;
      if (pagesHdr) {
        try {
          const obj = JSON.parse(pagesHdr) as Record<string, number>;
          setPageCounts(prev => {
            const next = { ...prev };
            for (const row of LEVEL4_SECTIONS) {
              const keys = row.oid.split(',');
              if (keys.some(k => k in obj))
                next[row.reportName ?? ''] = keys.reduce((a, k) => a + (obj[k] ?? 0), 0);
            }
            return next;
          });
        } catch { /* malformed — ignore */ }
      }

      const ctype = (res.headers['content-type'] as string | undefined) ?? 'application/pdf';
      const url = URL.createObjectURL(new Blob([res.data as Blob], { type: ctype }));
      const w = window.open(url, '_blank');
      if (!w) onError('Browser blocked the PDF popup — allow popups for this site.');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      onGenerated?.();
    } catch (e: unknown) {
      if (renderAbortRef.current?.signal.aborted) return;   // Stop clicked
      const errBlob = (e as { response?: { data?: Blob } }).response?.data;
      let msg = 'Level IV generation failed';
      if (errBlob && typeof errBlob.text === 'function') {
        try { const txt = await errBlob.text(); msg = JSON.parse(txt)?.error?.message ?? msg; }
        catch { /* not JSON */ }
      } else { msg = (e as { message?: string })?.message ?? msg; }
      onError(msg);
    } finally {
      setRendering(false);
    }
  }, [jobs, onError, onGenerated]);

  // NO auto-generate on open (user 2026-06-20): clicking the PDF button shows the
  // package popup ONLY — the user reviews / un-ticks sub-reports, then the report is
  // generated when they click Preview.  Same flow as the Level IV package (which
  // already waited for Preview).  (Previously this auto-fired /render-devx-multi on
  // open; removed so EVERY package behaves identically — popup first, then Preview.)

  // Per-row peek: render JUST that sub-report for the first JobID.  For
  // Level IV the row IS a section, so generate that one section only.
  const onPreviewOne = useCallback(async (subReportOid: string) => {
    if (isLevel4) { void genLevel4([subReportOid]); return; }
    const j0 = jobs[0];
    if (!j0) return;
    // The synthetic "EDD - …" row isn't a PDF sub-report — its Preview triggers
    // the EDD Excel download (ALPACA Process_EDD), not a render-subreport call.
    if (isEddItemOid(subReportOid)) {
      setPreviewingOid(subReportOid);
      try { await downloadEddPreview(); }
      catch { onError('The EDD could not be generated for download.'); }
      finally { setPreviewingOid(null); }
      return;
    }
    // Loading — a lightweight per-row spinner on the clicked 📑 only.  A single
    // sub-report renders fast, so it deliberately does NOT trigger the heavy
    // full-screen progress overlay (which paces to a 4 s minimum and made the
    // per-row preview feel slow — user report 2026-07-04).  The whole-package
    // Preview still uses the overlay.
    setPreviewingOid(subReportOid);
    try {
      const res = await api.post(
        '/api/v1/reporting/custom/' + encodeURIComponent(j0.jobOid) + '/render-subreport',
        { selectedParameterOids, amendedNote: amended && amendedNote ? amendedNote : null },
        { params: { subReportOid }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      // A per-row (single sub-report) preview also counts as "report generated"
      // — unlock Save Report, same as the whole-package Preview (ALPACA parity).
      onGenerated?.();
    } catch (e: unknown) {
      const errBlob = (e as { response?: { data?: Blob } }).response?.data;
      let msg = 'preview failed';
      if (errBlob && typeof errBlob.text === 'function') {
        try { const txt = await errBlob.text(); msg = JSON.parse(txt)?.error?.message ?? msg; }
        catch { /* not JSON */ }
      }
      onError(msg);
    } finally {
      setRendering(false);
      setPreviewingOid(null);
    }
  }, [jobs, selectedParameterOids, onError, isLevel4, genLevel4, onGenerated, downloadEddPreview]);

  const totalPages = Object.values(pageCounts).reduce((a, b) => a + (b || 0), 0);

  return (
    <div onClick={onClose}
         style={{
           position: 'fixed', inset: 0, zIndex: 4000,
           background: 'rgba(0,0,0,.35)',
           display: 'flex', alignItems: 'center', justifyContent: 'center',
         }}>
      <div onClick={e => e.stopPropagation()}
           style={{
             width: 760, maxWidth: '95vw', maxHeight: '90vh',
             background: '#fff', borderRadius: 4,
             boxShadow: '0 6px 24px rgba(0,0,0,.25)',
             display: 'flex', flexDirection: 'column', overflow: 'hidden',
             position: 'relative',
           }}>
        {/* Tier-1 deterministic progress bar overlay — SAME component the
            per-job detail popup uses (see CustomReportingDetailPage line
            1573).  Shows a real 0→85% progress bar + elapsed clock, not
            just a spinner.  Fires only after the user clicks Preview. */}
        {rendering && (
          <ReportProgressOverlay
            label={jobs.length > 1
              ? `Generating merged report — ${jobs.length} JobIDs…`
              : 'Generating report PDF…'}
            onStop={() => renderAbortRef.current?.abort()}
          />
        )}

        {/* Title bar */}
        <div style={{
          padding: '8px 14px', background: theme.accent, color: '#fff',
          fontSize: 13, fontWeight: 600, display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>
            {packageName} - Report Package
            <span style={{ marginLeft: 10, opacity: .85, fontWeight: 500 }}>
              ({jobs.length} JobID{jobs.length === 1 ? '' : 's'}: {jobs.map(j => j.jobId).join(', ')})
            </span>
          </span>
          <button onClick={onClose}
                  style={{
                    background: 'transparent', border: 'none', color: '#fff',
                    fontSize: 18, cursor: 'pointer', lineHeight: 1,
                  }}
                  title="Close">×</button>
        </div>

        {/* Render failures now surface through the shared notifier popup
            (useNotify, z-index above the modal) — the old in-popup banner was
            removed 2026-07-04 ("all warning/error must popup message"). */}

        {/* Header form */}
        <div style={{ padding: '14px 16px 12px 16px', borderBottom: '1px solid var(--ai-line)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 24px 1fr', rowGap: 8, columnGap: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--ai-ink)', alignSelf: 'center' }}>Reported Date</label>
            <span />
            <input type="date" value={reportedDate}
                   onChange={e => {
                     reportedDateTouched.current = true;
                     setReportedDate(e.target.value);
                     const emp = dlgEmployees.find(x => (x.name ?? '') === approvedBy);
                     onReportInfoChange?.({ reportedDate: e.target.value || undefined,
                                            approvedByOid: emp?.oid });
                   }}
                   style={{ padding: '4px 8px', fontSize: 12,
                            border: '1px solid var(--ai-line)', borderRadius: 3, width: 180 }} />
            <label style={{ fontSize: 12, color: 'var(--ai-ink)', alignSelf: 'center' }}>Approved By</label>
            <span />
            <select value={approvedBy}
                    onChange={e => {
                      setApprovedBy(e.target.value);
                      const emp = dlgEmployees.find(x => (x.name ?? '') === e.target.value);
                      onReportInfoChange?.({ reportedDate: reportedDateTouched.current ? reportedDate : undefined,
                                             approvedByOid: emp?.oid });
                    }}
                    style={{ padding: '4px 8px', fontSize: 12,
                             border: '1px solid var(--ai-line)', borderRadius: 3, width: 200 }}>
              <option value="">-- Select --</option>
              {dlgEmployees.map(e => (
                <option key={e.oid} value={e.name ?? ''}>{e.name}</option>
              ))}
            </select>
            <label style={{ fontSize: 12, color: 'var(--ai-ink)', alignSelf: 'center' }}>Accreditation</label>
            <input type="checkbox" checked={accreditOn}
                   onChange={e => setAccreditOn(e.target.checked)}
                   style={{ justifySelf: 'center', alignSelf: 'center' }} />
            <select value={accreditStd} disabled={!accreditOn}
                    onChange={e => setAccreditStd(e.target.value)}
                    style={{ padding: '4px 8px', fontSize: 12,
                             border: '1px solid var(--ai-line)', borderRadius: 3, width: 200,
                             opacity: accreditOn ? 1 : .5 }}>
              {dlgAccredits.map(a => (
                <option key={a.oid} value={a.name ?? ''}>{a.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Toolbar above grid */}
        <div style={{
          padding: '6px 12px', background: 'var(--ai-header-bg)',
          borderBottom: '1px solid var(--ai-line)',
          display: 'flex', gap: 6, alignItems: 'center',
        }}>
          <button className="ca-btn" style={{ padding: '3px 10px', fontSize: 11 }}
                  onClick={() => setPageCounts({})}>
            <span className="tb-i tb-i-green">🔄</span> Refresh
          </button>
          <button className="ca-btn" style={{ padding: '3px 10px', fontSize: 11 }}
                  disabled>
            <span className="tb-i tb-i-teal">📤</span> Export
          </button>
        </div>

        {/* Sub-report grid */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 12 }}>
              <LoaderSymbol size={14} /> Loading sub-reports…
            </div>
          )}
          {!loading && children.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 12, lineHeight: 1.5 }}>
              {isLevel4
                ? 'Level IV full data package — click Preview to generate the complete report (cover, case narrative, COC, Form 1/2C/3/4/7 per batch, real instrument raw data, ~5,000 pages with bookmarks).'
                : 'No sub-reports for this package.'}
            </div>
          )}
          {!loading && children.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                {/* Standard grey grid header (BO UI Standard §9) — not blue. */}
                <tr style={{ background: 'var(--ai-header-bg)' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'center',
                               borderBottom: '1px solid var(--ai-line)', borderRight: '1px solid var(--ai-line-soft)',
                               width: 36 }}>
                    <input type="checkbox"
                           checked={children.every(c => selected.has(c.oid))}
                           onChange={() => {
                             if (children.every(c => selected.has(c.oid))) setSelected(new Set());
                             else setSelected(new Set(children.map(c => c.oid)));
                           }} />
                  </th>
                  <th style={{ padding: '6px 8px', textAlign: 'left',
                               borderBottom: '1px solid var(--ai-line)', borderRight: '1px solid var(--ai-line-soft)',
                               fontWeight: 600, color: 'var(--ai-ink)' }}>ReportName</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center',
                               borderBottom: '1px solid var(--ai-line)', borderRight: '1px solid var(--ai-line-soft)',
                               fontWeight: 600, color: 'var(--ai-ink)', width: 70 }}>Preview</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center',
                               borderBottom: '1px solid var(--ai-line)', borderRight: '1px solid var(--ai-line-soft)',
                               fontWeight: 600, color: 'var(--ai-ink)', width: 70 }}>#Pages</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center',
                               borderBottom: '1px solid var(--ai-line)', borderRight: '1px solid var(--ai-line-soft)',
                               fontWeight: 600, color: 'var(--ai-ink)', width: 60 }}>sort</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center',
                               borderBottom: '1px solid var(--ai-line)',
                               fontWeight: 600, color: 'var(--ai-ink)', width: 100 }}>ShowReportID</th>
                </tr>
              </thead>
              <tbody>
                {children.map(c => {
                  const p = pageCounts[c.reportName ?? ''];
                  const pageLabel = p == null ? '' : (p === 0 ? 'skip' : String(p));
                  return (
                    <tr key={c.oid} style={{ borderBottom: '1px solid var(--ai-line-soft)' }}>
                      <td style={{ padding: '5px 8px', textAlign: 'center',
                                   borderRight: '1px solid var(--ai-line-soft)' }}>
                        <input type="checkbox" checked={selected.has(c.oid)}
                               onChange={() => toggleChild(c.oid)} />
                      </td>
                      <td style={{ padding: '5px 8px', color: 'var(--ai-ink)',
                                   borderRight: '1px solid var(--ai-line-soft)' }}>
                        {c.reportName}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center',
                                   borderRight: '1px solid var(--ai-line-soft)' }}>
                        <button onClick={() => onPreviewOne(c.oid)}
                                disabled={rendering}
                                title={`Preview ${c.reportName} for ${jobs[0]?.jobId ?? ''}`}
                                style={{ background: 'transparent', border: 'none',
                                         cursor: rendering ? 'default' : 'pointer', padding: 0 }}>
                          {previewingOid === c.oid
                            ? <LoaderSymbol size={14} />
                            : <span style={{ fontSize: 15 }} aria-hidden="true">📑</span>}
                        </button>
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center', color: 'var(--ai-ink)',
                                   borderRight: '1px solid var(--ai-line-soft)' }}>
                        {pageLabel}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center', color: 'var(--ai-ink)',
                                   borderRight: '1px solid var(--ai-line-soft)' }}>
                        {c.sort}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                        {/* Read-only boolean → shared .cellcheck glyph (BO UI Standard). */}
                        <span className={`cellcheck${c.showReportId ? ' on' : ''}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Amended row — GoLive: below the sub-report grid, above OK/Cancel/Preview */}
        <div style={{
          padding: '6px 14px', borderTop: '1px solid var(--ai-line)',
          background: 'var(--ai-header-bg)', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <input type="checkbox" checked={amended} onChange={toggleAmended} style={{ margin: 0 }} />
          <label style={{ fontSize: 12, color: 'var(--ai-ink)', fontWeight: 600,
                          cursor: 'pointer', userSelect: 'none' }}
                 onClick={toggleAmended}>
            Amended
          </label>
          {/* Always rendered (flex:1 slot always reserved) so toggling never resizes
              the popup; VISIBLE only when Amended is ticked (visibility, not opacity —
              opacity:0.5 kept the box on screen when unticked). */}
          <input type="text" value={amendedNote}
                 onChange={e => onAmendedNoteChange(e.target.value)}
                 disabled={!amended}
                 aria-hidden={!amended}
                 tabIndex={amended ? 0 : -1}
                 placeholder="Amended note (auto-filled)"
                 style={{ padding: '3px 8px', fontSize: 12, flex: 1,
                          border: '1px solid var(--ai-line)', borderRadius: 3,
                          visibility: amended ? 'visible' : 'hidden' }} />
        </div>

        {/* Footer */}
        <div style={{
          padding: '8px 14px', borderTop: '1px solid var(--ai-line)',
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--ai-header-bg)',
        }}>
          <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
            Total: {totalPages > 0 ? `${totalPages} pages` : '— pages'}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="ca-btn"
                    onClick={() => { if (onOk) { void onOk(); } else { onClose(); } }}
                    disabled={rendering || okBusy}
                    style={{ padding: '3px 16px', fontSize: 12 }}>
              {onOk ? (okBusy ? 'Saving…' : 'Save') : 'OK'}
            </button>
            <button className="ca-btn" onClick={onClose} disabled={okBusy}
                    style={{ padding: '3px 16px', fontSize: 12 }}>
              Cancel
            </button>
            <button className="ca-btn ca-btn-solid"
                    onClick={isLevel4 ? () => genLevel4(Array.from(selected)) : onPreviewMulti}
                    disabled={rendering || selected.size === 0}
                    style={{ padding: '3px 16px', fontSize: 12 }}>
              {rendering
                ? <><BusyIcon busy icon="ti-file-type-pdf" />&nbsp;Rendering…</>
                : 'Preview'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Edit Report — full-page view (ALPACA Frame.SetView parity). ─────────────
// Replaces the Report Tracking list view entirely (no modal) when the user
// clicks "Edit Report" on a ticked row — exactly as ALPACA's EditReport_Execute
// called Frame.SetView(SampleParameter_ListView_Copy_CustomReporting_Edit).
// Shows the report's samples in a full-width table (like ALPACA's ListView),
// plus Report Package + Revision Reason.  On Save Revision posts to
// /report/{oid}/revise (mints <base>R<n>, supersedes the old report,
// audits "Report Tracking" ValueChanged old→new ReportID). ──────────────────
interface EditCtxSample {
  parameterOid: string; sampleId: string | null; test: string | null;
  method: string | null; parameter: string | null; matrix: string | null;
}
interface EditCtx {
  reportingOid: string; reportId: string | null; reportName: string | null;
  jobOid: string; jobId: string | null; client: string | null;
  statusId: string | null; isSent: boolean;
  samples: EditCtxSample[];
}
interface PkgOption { oid: string; packageName: string | null; reportName: string | null; }

// ── Read-only samples detail — Report Validation row click (user request
// 2026-07-25): "show the jobid detail in the listview with reportname … like
// Report Tracking Edit Report click load … reportname + listview details only,
// no save / preview".  Reuses the SAME /report/{oid}/edit-context feed as
// EditReportPage, but renders it READ-ONLY: report name + the job's samples
// (SampleID / Matrix / Test / Parameter / Method), no package editing, no
// checkboxes, no Save / Preview — only a Back-to-list action. ─────────────────
function ReportSamplesViewPage({ reportingOid, backLabel, onClose }: {
  reportingOid: string;
  backLabel:    string;   // breadcrumb / back link — the source form (e.g. "Report Delivery")
  onClose:      () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [ctx, setCtx]         = useState<EditCtx | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setLoadErr(null);
      try {
        const res = await api.get(
          `/api/v1/reporting/report/${encodeURIComponent(reportingOid)}/edit-context`);
        if (!alive) return;
        setCtx((res.data as { data?: EditCtx })?.data ?? null);
      } catch (e) {
        if (alive) setLoadErr(
          (e as { response?: { data?: { error?: { message?: string } } } })
            .response?.data?.error?.message ?? 'Failed to load the report details.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reportingOid]);

  // Same styling as the list grids — the shared <AppDataGrid> (zebra rows, column
  // borders, hover, footer). This report is typically ONE sample with many tests
  // (one grid row per test / parameter).
  // EQUAL-WIDTH columns (user request 2026-07-25): give every column the SAME
  // width hint so the shared grid (table width:100%, auto layout) stretches them
  // evenly — otherwise a column with no width (Test) balloons to fill the row.
  const sampleCols: AppColumn<EditCtxSample>[] = [
    { field: 'sampleId',  header: 'SampleID',  width: 200 },
    { field: 'matrix',    header: 'Matrix',    width: 200 },
    { field: 'test',      header: 'Test',      width: 200 },
    { field: 'parameter', header: 'Parameter', width: 200 },
    { field: 'method',    header: 'Method',    width: 200 },
  ];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Reporting</span><i className="ti ti-chevron-right" />
            <span style={{ color: 'var(--ai-blue)', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={onClose}>
              {backLabel}
            </span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{ctx?.reportId || 'Report'}</span>
          </div>
          <div className="dp-title-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="ti ti-file-description" style={{ fontSize: 22, color: 'var(--ai-orange)' }} />
              <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ai-ink)' }}>
                {ctx?.reportId ?? ''}{ctx?.reportName ? ` — ${ctx.reportName}` : ''}
              </span>
              {loading && <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>Loading…</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="bl-body" style={{ padding: '0 20px 32px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* Back to list — this is a read-only view, so no Save / Preview. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0' }}>
          <button className="ca-btn" onClick={onClose}>
            <i className="ti ti-arrow-left" />&nbsp;Back to list
          </button>
        </div>

        {loadErr && (
          <div style={{
            fontSize: 12, color: 'var(--ai-pill-alert-fg)', background: 'var(--ai-pill-alert-bg)',
            border: '1px solid var(--ai-line)', borderRadius: 4, padding: '10px 14px', marginBottom: 16,
          }}>{loadErr}</div>
        )}

        {ctx && !loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Report name / IDs — read-only summary */}
            <div style={{
              background: 'var(--ai-surface)', border: '1px solid var(--ai-line)',
              borderRadius: 6, padding: '14px 16px', maxWidth: 520,
              display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 18px', fontSize: 12,
            }}>
              <span style={{ fontWeight: 600, color: 'var(--ai-muted)' }}>Report</span>
              <span style={{ color: 'var(--ai-ink)' }}>{ctx.reportName ?? '—'}</span>
              <span style={{ fontWeight: 600, color: 'var(--ai-muted)' }}>Report ID</span>
              <span style={{ color: 'var(--ai-ink)' }}>{ctx.reportId ?? '—'}</span>
              <span style={{ fontWeight: 600, color: 'var(--ai-muted)' }}>Job ID</span>
              <span style={{ color: 'var(--ai-ink)' }}>{ctx.jobId ?? '—'}</span>
              <span style={{ fontWeight: 600, color: 'var(--ai-muted)' }}>Client</span>
              <span style={{ color: 'var(--ai-ink)' }}>{ctx.client ?? '—'}</span>
            </div>

            {/* Samples on this report — shared <AppDataGrid> (same style as the list) */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ai-ink)' }}>
                  Samples on this report
                </span>
                {/* one row per TEST/parameter — usually ONE sample with many tests,
                    so show distinct samples · tests, not the raw row count. */}
                {(() => {
                  const nSamples = new Set(ctx.samples.map(s => s.sampleId ?? '')).size;
                  const nTests   = ctx.samples.length;
                  return (
                    <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
                      {nSamples} sample{nSamples === 1 ? '' : 's'} · {nTests} test{nTests === 1 ? '' : 's'}
                    </span>
                  );
                })()}
              </div>

              <AppDataGrid<EditCtxSample>
                rows={ctx.samples}
                columns={sampleCols}
                getRowId={(s) => s.parameterOid}
                emptyText="No samples on this report."
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EditReportPage({ reportingOid, busy, onCancel, onSubmit, onUpdated }: {
  reportingOid: string;
  busy:         boolean;
  onCancel:     () => void;
  onSubmit:     (form: { revisionReason: string; packageName: string | null; keepParameterOids: string[] }) => void;
  /** Fired after a successful in-place "update existing report id" — the parent
   *  closes the Edit view and reloads the list (report went back to Pending
   *  Validation). */
  onUpdated:    () => void;
}) {
  const [loading, setLoading]             = useState(true);
  const [loadErr, setLoadErr]             = useState<string | null>(null);
  const [ctx, setCtx]                     = useState<EditCtx | null>(null);
  const [packages, setPackages]           = useState<PkgOption[]>([]);
  const [pkgName, setPkgName]             = useState<string>('');
  const [pkgOid, setPkgOid]               = useState<string>('');
  const [kept, setKept]                   = useState<Set<string>>(new Set());
  const [showPackagePopup, setShowPackagePopup] = useState(false);
  // Which action opened the popup: 'preview' (OK just closes) vs 'update' (OK
  // persists the in-place update-existing — ALPACA "update existing report id").
  const [popupMode, setPopupMode]         = useState<'preview' | 'update'>('preview');
  const [savingUpdate, setSavingUpdate]   = useState(false);
  const [amended, setAmended]             = useState(false);
  const [amendedNote, setAmendedNote]     = useState('');
  const { confirm: confirmDlg, prompt: promptDlg } = useConfirm();
  const notify = useNotify();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setLoadErr(null);
      try {
        const [ctxRes, pkgRes] = await Promise.all([
          api.get(`/api/v1/reporting/report/${encodeURIComponent(reportingOid)}/edit-context`),
          api.get('/api/v1/reporting/custom/report-packages'),
        ]);
        if (!alive) return;
        const c = (ctxRes.data as { data?: EditCtx })?.data ?? null;
        const pk = ((pkgRes.data as { data?: PkgOption[] })?.data) ?? [];
        setCtx(c);
        setPackages(pk);
        const reportName = c?.reportName ?? '';
        setPkgName(reportName);
        // Find the matching package OID — falls back to the first package so
        // Preview is always usable even when the stored name drifts from the list.
        const matchPkg = pk.find(p => (p.packageName ?? p.reportName) === reportName) ?? pk[0];
        setPkgOid(matchPkg?.oid ?? '');
        setKept(new Set((c?.samples ?? []).map(s => s.parameterOid)));
      } catch (e) {
        if (alive) setLoadErr(
          (e as { response?: { data?: { error?: { message?: string } } } })
            .response?.data?.error?.message ?? 'Failed to load the report for editing.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reportingOid]);

  const toggle = (oid: string) => setKept(prev => {
    const n = new Set(prev);
    if (n.has(oid)) n.delete(oid); else n.add(oid);
    return n;
  });
  const allOn = !!ctx && ctx.samples.length > 0 && kept.size === ctx.samples.length;
  const toggleAll = () => setKept(allOn ? new Set() : new Set((ctx?.samples ?? []).map(s => s.parameterOid)));

  // Preview: ALPACA parity — show Report Package popup first (sub-reports, Reported Date,
  // Approved By, Accreditation, Amended), then generate the PDF from the popup's Preview button.
  const handlePreview = () => {
    if (!ctx || loading || kept.size === 0 || !pkgOid) return;
    setPopupMode('preview');
    setShowPackagePopup(true);
  };

  // OK on the update-existing popup → persist the in-place update (ALPACA parity:
  // regenerate + overwrite the SAME ReportID, reset the workflow to Pending
  // Validation, audit).  The picked package name feeds ReportName.
  const handleUpdateExisting = async () => {
    if (!ctx || savingUpdate) return;
    setSavingUpdate(true);
    try {
      await runLoading(
        api.post(`/api/v1/reporting/report/${encodeURIComponent(reportingOid)}/update-existing`,
          { packageName: pkgName && pkgName !== ctx.reportName ? pkgName : null }),
        { blocking: true, label: 'Updating report…' });
      notify.success('Report saved successfully.');   // ALPACA generic message (no ID)
      setShowPackagePopup(false);
      onUpdated();
    } catch (e) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Failed to update the report.');
    } finally {
      setSavingUpdate(false);
    }
  };

  // Save flow — ALPACA parity (ReportingViewController two-step confirm, user
  // request 2026-07-25):
  // 1. "Do you want to generate a new report ID?"
  //      Yes → prompt for Revision Reason → revise (mint <base>R<n>, re-enter
  //            Pending Validation).  [existing behaviour]
  // 2. If No → "Do you want to update existing report ID?"
  //      Yes → open the Report Package popup to regenerate the report IN PLACE
  //            (SAME ReportID — no new revision).
  //      No  → NO ACTION (ALPACA's second Cancel simply aborts).
  const canSave = !busy && !loading && kept.size > 0;
  const handleSave = async () => {
    if (!canSave || !ctx) return;

    const genNew = await confirmDlg({
      title: 'Generate New Report ID',
      message: 'Do you want to generate a new report ID?',
    });
    if (genNew) {
      // ── NEW-ID path (revise) ──
      const reason = await promptDlg({
        title: 'Revision Reason',
        message: `Changes made on ${ctx.jobId ?? reportingOid}, please provide the comment.`,
        placeholder: 'Reason',
        required: true,
      });
      if (reason === null) return;  // user cancelled the prompt
      onSubmit({
        revisionReason: reason.trim(),
        packageName: pkgName && pkgName !== ctx.reportName ? pkgName : null,
        keepParameterOids: Array.from(kept),
      });
      return;
    }

    // ── No → offer to update the EXISTING report ID (ALPACA parity) ──
    const updateExisting = await confirmDlg({
      title: 'Update Existing Report ID',
      message: 'Do you want to update existing report ID?',
    });
    if (!updateExisting) return;              // No → no action (ALPACA parity)
    if (!pkgOid) { notify.error('Select a report package first.'); return; }
    setPopupMode('update');                   // Yes → popup whose OK PERSISTS the update
    setShowPackagePopup(true);
  };

  const inputStyle: CSSProperties = {
    width: '100%', padding: '6px 8px', fontSize: 12,
    border: '1px solid var(--ai-line)', borderRadius: 4, boxSizing: 'border-box',
    background: 'var(--ai-surface)', color: 'var(--ai-ink)',
  };

  // Shared action bar rendered at both top and bottom (Standing Rule 12).
  const actionBar = (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 0',
    }}>
      <button className="ca-btn ca-btn-solid" onClick={() => { void handleSave(); }} disabled={!canSave}>
        <BusyIcon busy={busy} icon="ti-device-floppy" />
        &nbsp;{busy ? 'Saving…' : 'Save Revision'}
      </button>
      <button className="ca-btn" onClick={handlePreview}
              disabled={!ctx || kept.size === 0 || loading || !pkgOid}
              title="Open Report Package popup to configure and preview the report">
        <i className="ti ti-eye" />
        &nbsp;Preview
      </button>
      <button className="ca-btn" onClick={onCancel} disabled={busy}>
        <i className="ti ti-x" />&nbsp;Cancel
      </button>
      {ctx && !loading && (
        <span style={{ fontSize: 11, color: 'var(--ai-muted)', marginLeft: 8 }}>
          Saving will create revision <strong>{(ctx.reportId ?? '') + 'R…'}</strong> and
          re-enter Pending Validation.
        </span>
      )}
    </div>
  );

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          {/* Breadcrumb */}
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Reporting</span><i className="ti ti-chevron-right" />
            <span
              style={{ color: 'var(--ai-blue)', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={onCancel}>
              Report Tracking
            </span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">
              Edit Report{ctx?.reportId ? ` — ${ctx.reportId}` : ''}
            </span>
          </div>
          {/* Page title row */}
          <div className="dp-title-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i className="ti ti-edit" style={{ fontSize: 22, color: 'var(--ai-orange)' }} />
              <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ai-ink)' }}>
                Edit Report{ctx?.reportId ? ` — ${ctx.reportId}` : ''}
              </span>
              {loading && (
                <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>Loading…</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bl-body" style={{ padding: '0 20px 32px', flex: 1, minHeight: 0, overflowY: 'auto' }}>

        {/* Action bar — TOP */}
        {actionBar}

        {loadErr && (
          <div style={{
            fontSize: 12, color: 'var(--ai-pill-alert-fg)', background: 'var(--ai-pill-alert-bg)',
            border: '1px solid var(--ai-line)', borderRadius: 4, padding: '10px 14px', marginBottom: 16,
          }}>{loadErr}</div>
        )}

        {ctx && !loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Report Package — single row (Revision Reason is collected via the Save dialog flow) */}
            <div style={{
              background: 'var(--ai-surface)', border: '1px solid var(--ai-line)',
              borderRadius: 6, padding: '14px 16px', maxWidth: 500,
            }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ai-ink)', marginBottom: 4, display: 'block' }}>
                Report Package
              </label>
              <select style={inputStyle} value={pkgName} onChange={e => {
                setPkgName(e.target.value);
                const found = packages.find(p => (p.packageName ?? p.reportName) === e.target.value);
                setPkgOid(found?.oid ?? '');
              }}>
                {ctx.reportName && !packages.some(p => (p.packageName ?? p.reportName) === ctx.reportName) && (
                  <option value={ctx.reportName}>{ctx.reportName}</option>
                )}
                {packages.map(p => {
                  const nm = p.packageName ?? p.reportName ?? '';
                  return <option key={p.oid} value={nm}>{nm}</option>;
                })}
              </select>
            </div>

            {/* Samples table — full-width ListView (ALPACA parity) */}
            <div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8,
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ai-ink)' }}>
                  Samples on this report
                </span>
                <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>
                  {kept.size} of {ctx.samples.length} selected
                </span>
                <button
                  className="ca-btn"
                  style={{ fontSize: 11, padding: '2px 10px' }}
                  onClick={toggleAll}>
                  {allOn ? 'Clear all' : 'Select all'}
                </button>
              </div>

              <div style={{
                border: '1px solid var(--ai-line)', borderRadius: 6, overflow: 'hidden',
              }}>
                {/* Table header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '36px 160px 110px 1fr 1fr 160px',
                  background: 'var(--ai-grid-header, #fafbfc)',
                  borderBottom: '2px solid var(--ai-line)',
                  fontSize: 11, fontWeight: 700, color: 'var(--ai-ink)',
                }}>
                  {/* Select-all checkbox header — .adg-cb-col style */}
                  <div style={{ padding: '7px 0', textAlign: 'center', borderRight: '1px solid var(--ai-line)' }}>
                    <input
                      type="checkbox"
                      style={{ display: 'block', margin: '0 auto' }}
                      checked={allOn}
                      onChange={toggleAll}
                    />
                  </div>
                  {['SampleID', 'Matrix', 'Test', 'Parameter', 'Method'].map(h => (
                    <div key={h} style={{
                      padding: '7px 10px', borderRight: '1px solid var(--ai-line)',
                    }}>{h}</div>
                  ))}
                </div>

                {/* Table body */}
                {ctx.samples.length === 0 && (
                  <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--ai-muted)' }}>
                    No samples on this report.
                  </div>
                )}
                {ctx.samples.map((s, i) => (
                  <label
                    key={s.parameterOid}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '36px 160px 110px 1fr 1fr 160px',
                      cursor: 'pointer',
                      background: kept.has(s.parameterOid)
                        ? 'var(--ai-row-selected, #edf6ff)'
                        : i % 2 === 1 ? 'var(--ai-row-alt, #f9fbfe)' : 'transparent',
                      borderBottom: '1px solid var(--ai-line-soft)',
                      fontSize: 12, color: 'var(--ai-ink)',
                    }}
                  >
                    {/* Row checkbox — .adg-cb-col style */}
                    <div style={{ padding: '7px 0', textAlign: 'center', borderRight: '1px solid var(--ai-line)' }}>
                      <input
                        type="checkbox"
                        style={{ display: 'block', margin: '0 auto' }}
                        checked={kept.has(s.parameterOid)}
                        onChange={() => toggle(s.parameterOid)}
                      />
                    </div>
                    <div style={{ padding: '7px 10px', borderRight: '1px solid var(--ai-line)', fontWeight: 600, fontFamily: 'monospace' }}>
                      {s.sampleId ?? '—'}
                    </div>
                    <div style={{ padding: '7px 10px', borderRight: '1px solid var(--ai-line)' }}>
                      {s.matrix ?? '—'}
                    </div>
                    <div style={{ padding: '7px 10px', borderRight: '1px solid var(--ai-line)' }}>
                      {s.test ?? '—'}
                    </div>
                    <div style={{ padding: '7px 10px', borderRight: '1px solid var(--ai-line)' }}>
                      {s.parameter ?? '—'}
                    </div>
                    <div style={{ padding: '7px 10px' }}>
                      {s.method ?? '—'}
                    </div>
                  </label>
                ))}
              </div>
            </div>

          </div>
        )}

        {/* Action bar — BOTTOM (Standing Rule 12) */}
        <div style={{ marginTop: 20 }}>
          {actionBar}
        </div>

      </div>

      {/* Report Package popup — portalled to document.body so position:fixed
          escapes any CSS stacking context created by the app shell / bl-page. */}
      {showPackagePopup && ctx && pkgOid && createPortal(
        <MultiJobPackagePopup
          jobs={[{ jobOid: ctx.jobOid, jobId: ctx.jobId ?? '' }]}
          packageOid={pkgOid}
          packageName={pkgName}
          selectedParameterOids={Array.from(kept)}
          onClose={() => setShowPackagePopup(false)}
          onError={msg => notify.error(msg)}
          amended={amended}
          amendedNote={amendedNote}
          onAmendedChange={setAmended}
          onAmendedNoteChange={setAmendedNote}
          /* update-existing: OK PERSISTS the in-place update (same ReportID);
             preview mode leaves OK as a plain close. */
          onOk={popupMode === 'update' ? handleUpdateExisting : undefined}
          okBusy={savingUpdate}
        />,
        document.body
      )}

    </div>
  );
}
